import { Controller } from "@hotwired/stimulus"
import {
  COLUMN_ORDER as CC_COLS,
  COLUMN_TITLE as CC_COL_META,
  GROUP_ICON   as CC_GROUP_ICON,
  DEFAULT_OPEN_BY_COL as CC_OPEN_BY_COL,
  optionsFor   as ccOptionsFor,
  hasOverrideFor as ccHasOverrideFor,
  findOption   as ccFindOption,
  genericFor   as ccGenericFor,
  quickPicksFor as ccQuickPicksFor,
  commHintFor  as ccHintFor
} from "comm_chart_data"
import { syncWhoLivesIntoCircle } from "who_lives_link"
import { savePlan, debounce, isNotAttendingSchool, registerFlush } from "plan_storage"
import { renderIcons } from "lucide_icons"
import { findScrollParent } from "controllers/scroll_preserve"
import {
  goalLibrary, readGoalOverrides, writeGoalOverride, deleteGoalOverride,
  goalOverrideKey, libAllSorted, saveGoalToLibrary, libFamily, libFamilyVendorsResolved,
} from "goal_library"
import { vendorGet } from "vendor_library"
import { wizardGoalEditorHtml } from "goal_editor_view"

// Wizard controller — Rails-native port of editor_source/wizard.html.
// Maintains a step index across SECTIONS (matches production wizard.html
// line 281). Plan data lives in localStorage under "pcp_plan_data"
// (same key the editor + preview use), so edits in the wizard
// immediately show up in the editor and the print preview.

const STORAGE_KEY = "pcp_plan_data"

// Section list mirrors editor_source/wizard.html line 281 SECTIONS array.
// Smart Calendar / Not working / Open questions (TBD) are placeholder
// sections rendered by their step renderers (calendar by the calendar
// section in the editor, notWorking is read from plan.notWorking, and
// Open Questions is a derived list view over every TBD across the
// plan).
// Order + ids MUST match editor's SECTION_KEYS in
// app/controllers/editor_controller.rb so the wizard pane is
// permanently bound to the editor section the user is on. When the
// user lands on an editor section that doesn't yet have a wizard
// step renderer (unmetNeeds, review, facilitator), _renderStep
// falls back to a "Step content not yet ported" placeholder so the
// pane is blank but the binding holds.
const SECTIONS = [
  { id: "client",          label: "Who is this plan for?",    short: "Client",         icon: "user" },
  { id: "about",           label: "About [FirstName]",        short: "About",          icon: "info" },
  { id: "circleOfSupport", label: "Circle of support",        short: "Circle",         icon: "custom-ring-5-center" },
  { id: "communication",   label: "How [FirstName] Communicates", short: "Communication",  icon: "message-square" },
  { id: "wonderfulThings", label: "Wonderful things",         short: "Wonderful",      icon: "thumbs-up" },
  { id: "notSoFavorites",  label: "Not-so-favorite things",   short: "Not favorites",  icon: "thumbs-down" },
  { id: "dayInLife",       label: "A day in my life",         short: "Day in life",    icon: "sun" },
  { id: "important",       label: "What's important",         short: "Important",      icon: "heart" },
  { id: "bigDreams",       label: "Hopes & dreams",           short: "Dreams",         icon: "rocket" },
  { id: "supportMatch",    label: "Support match",            short: "Support",        icon: "user-check" },
  { id: "working",         label: "What's working",           short: "Working",        icon: "check-circle" },
  { id: "goals",           label: "SMART Goals",              short: "Goals",          icon: "target" },
  { id: "barriers",        label: "Barriers",                 short: "Barriers",       icon: "shield" },
  { id: "currentServices", label: "Current services",         short: "Services",       icon: "briefcase" },
  { id: "unmetNeeds",      label: "Unmet needs",              short: "Unmet",          icon: "alert-triangle" },
  { id: "resourceCalendar", label: "Service Calendar legend",  short: "Services",       icon: "calendar-check" },
  { id: "review",          label: "Review",                   short: "Review",         icon: "clipboard-check" },
  { id: "facilitator",     label: "Facilitator",              short: "Facilitator",    icon: "user-cog" },
  { id: "openQuestions",   label: "Open Questions",           short: "Questions",      icon: "help-circle" }
]

// Sections that intentionally have no guided wizard questions — clicking
// them in the editor sidebar moves the binding here, and _renderStep
// shows a neutral "edit on the left" placeholder instead of the
// "not yet ported" stub.
const NO_WIZARD_SECTIONS = new Set(["openQuestions", "review", "facilitator"])

// Family-relationship chips on the "Who lives with me?" list. When the
// user taps one of these we launch a focused micro-flow
// (first name → last name) instead of dropping a blank row in the
// editor — much faster + the form fills itself in.
const FAMILY_PERSON_CHIPS = new Set([
  "Mom", "Dad", "Stepmom", "Stepdad", "Spouse",
  "Brother", "Sister", "Stepbrother", "Stepsister",
  "Grandma", "Grandpa", "Aunt", "Uncle", "Cousin", "Caregiver"
])

// Relationships that imply a likely-minor person — only these get the
// age step. Adults (Mom/Dad/Grandma/Spouse/…) skip straight to name.
const FAMILY_MINOR_CHIPS = new Set([
  "Brother", "Sister", "Stepbrother", "Stepsister", "Cousin"
])

// Single-year age picker chips: under-1, every year 1..17, and 18+.
// Tap-to-advance — no separate exact-age input needed.
const AGE_GROUPS = [
  "<1", "1", "2", "3", "4", "5", "6", "7", "8", "9",
  "10", "11", "12", "13", "14", "15", "16", "17", "18+"
]

// Issue #6 — focused micro-flow for adding a school. Triggered when
// the user taps a school-level chip (Elementary / Middle / High /
// College). Walks them through Grade → School in a screen-takeover
// card, mirroring the family flow's pattern. The level is implied by
// the chip clicked, shown as a badge in the header.
const GRADES_BY_LEVEL = {
  "Elementary school": ["Pre-K", "K", "1st", "2nd", "3rd", "4th", "5th"],
  "Middle school":     ["6th", "7th", "8th"],
  "High school":       ["9th", "10th", "11th", "12th"],
  "College":           ["Freshman", "Sophomore", "Junior", "Senior", "Grad"]
}

const _wizSchoolFlow = {
  active: false,
  level: "",         // "Elementary school" / "Middle school" / etc.
  step: "grade",     // "grade" | "school"
  grade: "",         // picked grade label
  school: "",        // picked or typed school name
  nearby: null,      // array of nearby schools (loaded async) or null while loading
  loadError: ""
}

// State machine for the focused "Add family member" micro-flow. Lives
// at module scope (matching the existing _wizGoalUi pattern) so the
// step renderer can read it without threading state through the call.
const _wizPersonFlow = {
  active: false,
  chip: "",
  listKey: "",
  step: "age",          // "age" | "first" | "last"
  age: "",              // bucket label (<1, 1–2, …, 18+) — quick-pick
  exactAge: "",         // exact integer (years) — optional precision
  firstName: "",
  lastName: ""
}


// TBD marker matcher — production line 3640. Matches whole-word "TBD"
// (case-insensitive) and "??". Used by Open Questions, the Next TBD
// button, and the per-section count badge.
const TBD_PATTERNS_TEST = /\btbd\b|\?\?/i

// Quick-pick option banks — verbatim from editor_source/wizard.html so the
// chips on each section match production 1:1.
const WONDERFUL_OPTIONS = {
  activitiesGroups: {
    "🎨 Creative":           ["Painting","Drawing","Coloring","Crafts","Photography","Play-Doh","Stickers","Cutting & Gluing","Building with Blocks","LEGO"],
    "🎮 Play & Games":       ["Video Games","Board Games","Puzzles","Card Games","Gaming","Building Towers","Hide & Seek","Dress Up"],
    "🎵 Music & Movement":   ["Listening to Music","Music","Dancing","Singing","Jumping","Spinning","Swinging","Bouncing on a Trampoline"],
    "📺 Screen Time":        ["Watching Cartoons","YouTube Videos","Movies","Favorite TV Shows","Tablets"],
    "🐾 Animals & Nature":   ["Playing with Pets","Feeding Animals","Going to the Park","Being Outside","Water Play","Sand Play"],
    "🍕 Food & Treats":      ["Eating My Favorite Food","Having a Snack","Cooking / Baking","Cooking"],
    "👨‍👩‍👧 People & Connection":["Being with Family","Playing with Friends","Hugs","Laughing","Getting Compliments"],
    "😊 Calm & Comfort":     ["Quiet Time","Reading Books","Reading","Listening to Stories","Cuddling","Weighted Blanket","Fidget Toys"],
    "🏃 Active":             ["Running","Swimming","Bike Riding","Sports","Playground","Yoga","Gardening","Hiking"]
  },
  places:  ["The park","The beach","Library","Mall","Movies","Church","School","Grandma's house","Community center","Pool","Restaurant"],
  events:  ["Birthday parties","Holiday gatherings","School events","Family reunions","Field trips","Concerts","Sports games","Art shows","Graduations"],
  foods:   ["Pizza","Pasta","Tacos","Ice cream","Fruit","Chicken nuggets","Mac & cheese","Burgers","Chocolate","Pancakes","Sandwiches","Smoothies"],
  strengthsGroups: {
    "💖 Personal qualities": ["Kind","Funny","Patience","Honest","Empathetic","Curious","Generous","Loyal","Brave","Resilient","Determined"],
    "🎨 Talents & skills":   ["Artistic","Musical","Athletic","Great memory","Strong reader","Good at math","Detail-oriented","Quick learner","Hands-on","Tech-savvy"],
    "🤝 People skills":      ["Good listener","Helpful","Caring","Friendly","Welcoming","Cooperative","Encouraging","Loves making others laugh"],
    "💪 Work ethic":         ["Hard-working","Reliable","Focused","Independent","Organized","Persistent","Takes pride in their work"]
  },
  admiredQualitiesGroups: {
    "✨ Presence":   ["Bright smile","Infectious laugh","Calm presence","Joyful spirit","Bright personality","Magnetic energy"],
    "🧠 Mind":      ["Sharp memory for details","Deep thinker","Sees what others miss","Encyclopedic knowledge of a favorite topic","Strong intuition","Imaginative"],
    "❤️ Heart":     ["Big heart","Loves fiercely","Strong sense of fairness","Pure honesty","Authentic self","Loyal to the people they love"],
    "🌟 Style":     ["Quirky and fun","Unique perspective","Strong opinions","Independent spirit","Effortlessly themselves","Marches to their own drum"]
  }
}
const NSF_OPTIONS = {
  triggers:     ["Loud noises","Schedule changes","Being rushed","Crowds","Waiting","Criticism","Surprises","Being ignored","Transitions","New situations"],
  sensory:      ["Bright lights","Loud sounds","Strong smells","Rough textures","Tags on clothing","Crowded spaces","Certain food textures","Flashing lights","Perfume","Scratchy fabrics"],
  situations:   ["Crowded places","New social settings","Long lines","Doctor visits","Big groups","Public speaking","Eating out","Shopping","Parties"],
  foods:        ["Spicy foods","Mushy textures","Strong flavors","Bitter vegetables","Sour foods","Crunchy foods","Mixed textures","Cold foods","Seafood"],
  environments: ["Noisy restaurants","Crowded malls","Dark rooms","Hot weather","Cold weather","Fluorescent lighting","Small spaces","Unfamiliar places"]
}
const IMPORTANT_OPTIONS = {
  to:  ["Time with family","Being with friends","Music","Art/drawing","Going outdoors","My pets","Having choices","My routine","Alone time","Being independent","Favorite foods","Watching TV/movies","Video games","Sports/exercise","Reading","Swimming","Going to church","Riding bike","Shopping","Cooking/baking"],
  for: ["Taking medication","Regular sleep","Healthy meals","Exercise","Hygiene/bathing","Dental care","Doctor visits","Routine/structure","Social interaction","Safety awareness","Hydration","Emotional regulation","Therapy sessions","Wearing glasses/hearing aids","Sunscreen/skin care","Proper nutrition","Weight management","Supervision","Behavioral support","Seizure management"]
}
const DREAMS_OPTIONS = {
  hopes: ["Make a new friend","Join a club or team","Learn to cook a meal","Get a part-time job","Volunteer in my community","Take a class I enjoy","Learn to ride a bike","Go to summer camp","Perform in a show/recital","Try a new sport","Learn to swim","Save money for something","Go on a trip","Learn an instrument","Earn a certificate/award","Start a small project","Attend a live event","Read a chapter book","Help someone else","Spend a night away from home","Sit at one place for longer time","Express 'no' without getting frustrated","Use words instead of melting down","Take turns without getting upset","Wait calmly when asked","Calm down within 5 minutes when upset","Ask for a break when overwhelmed","Name my emotions (happy, sad, mad, scared)","Use a calming strategy (deep breath, fidget, quiet space)","Tolerate transitions without distress","Stay focused on a task for 10+ minutes","Follow a 2-step direction","Accept hearing 'no' calmly","Tolerate a haircut without distress","Reduce frequency of meltdowns","Brush teeth independently","Use the bathroom independently","Wash hands without reminders","Get dressed independently (top and bottom)","Pick out my own clothes","Try a new food each week","Pack my own backpack","Make my bed","Tie shoelaces","Drink from an open cup without spilling","Use full sentences when speaking","Ask for help using words","Answer 'wh-' questions (who/what/where/when)","Tell someone when I'm hurt or sick","Greet familiar people","Use 'please' and 'thank you' consistently","Have a 3-turn conversation","Use my AAC device functionally","Play cooperatively with a peer","Share with a sibling or friend","Take turns in a game","Join a group activity","Sit at a table with peers","Catch and throw a ball","Use scissors safely","Write my name","Hold a pencil correctly","Recognize all letters of the alphabet","Count to 50","Read 10 sight words","Identify colors and shapes","Sit through a doctor visit calmly","Sit through a meal at a restaurant","Order food at a restaurant","Use a public restroom safely","Cross the street safely with help","Sleep through the night","Stay in my own bed all night","Use the toilet at night without accidents"],
  dreams: ["Live on my own","Have my own place","Get married/have a partner","Have a career I love","Start my own business","Travel the world","Go to college/trade school","Learn to drive","Own a pet","Help others/be a mentor","Become an artist","Be a musician/performer","Work with animals","Be a chef/baker","Be a teacher/coach","Work with computers","Create content online","Be an athlete","Write a book/tell my story","Design games/apps","Work in healthcare","Have a garden/farm","Be a fashion designer","Make my family proud"]
}
const SUPPORT_OPTIONS = {
  qualities:    ["Patience","Good listener","Encouraging","Respectful","Fun","Calm","Positive","Dependable","Gentle","Believes in me","Motivated","Kind","Shares my interests","Celebrates my wins"],
  skills:       ["Knows my disability well","CPR/First aid certified","Sign language","Behavioral support","Sensory awareness","Medication management","De-escalation skills","AAC/communication devices","Personal care","Job coaching experience","Community navigation","Teaching daily living skills"],
  personality:  ["Warm and friendly","Consistent","Flexible","Creative","Energetic","Laid-back","Adventurous","Structured","Humorous","Nurturing","Pushes me (in a good way)","Goes at my pace"],
  dealBreakers: ["Impatient","Uses raised voice","Inconsistent","Distracted by phone","Condescending","Dismissive","Rushes through tasks","Ignores boundaries","Inflexible","Unreliable","Doesn't believe in my abilities","Does things for me instead of teaching"]
}
// Barriers to *completing the plan's goals* — family / access /
// system constraints, not participant traits. Participant-level
// challenges already surface in the strengths-and-challenges and
// communication-chart sections.
const BARRIER_OPTIONS = [
  "Both parents work full-time",
  "Single-parent household",
  "Caregiver burnout / no respite",
  "Sibling has competing care needs",
  "Family health or medical issues",
  "No reliable transportation",
  "Long commute to providers",
  "No nearby qualified providers",
  "Provider waitlist",
  "No bilingual provider",
  "Insurance denied or limited coverage",
  "IRC service hours capped",
  "Out-of-pocket cost burden",
  "School schedule conflicts with services",
  "Inconsistent school district support",
  "Limited after-school options",
  "Housing instability",
  "Language barrier in IEP/IPP meetings",
  "Difficulty navigating regional-center process",
  "Trouble finding trained respite workers",
  "Lack of in-home support hours"
]
const SERVICE_OPTIONS = ["Speech therapy","Occupational therapy","Physical therapy","ABA/Behavioral therapy","Behavioral support","Respite care","Day program","Supported living services","Personal assistant","Transportation services","Mental health counseling","Medication management","School IEP services","School aide support","After-school program","Social skills group","Recreation program","Tutoring","Job coaching","Nursing"]

// Smart Calendar categories — verbatim from editor_source/wizard.html#2866.
// `kind: "window"` items have start/end + days; `kind: "hours"` items have
// hours + frequency (weekly/monthly/quarterly/annually).
const WEEKS_PER_MONTH = 4.345
const CALENDAR_CATEGORIES = [
  { id: "school",  label: "Out for School", icon: "🏫", kind: "window", defaultStart: "07:30", defaultEnd: "15:00", defaultDays: 5 },
  { id: "sleep",   label: "Sleep",          icon: "😴", kind: "window", defaultStart: "21:00", defaultEnd: "06:30", defaultDays: 7 },
  { id: "respite", label: "Respite",        icon: "🛟", kind: "hours",  defaultHours: 32,  defaultFreq: "monthly" },
  { id: "ihss",    label: "IHSS",           icon: "🏠", kind: "hours",  defaultHours: 100, defaultFreq: "monthly" },
  { id: "pa",      label: "PA Hours",       icon: "🤝", kind: "hours",  defaultHours: 20,  defaultFreq: "weekly"  },
  { id: "other",   label: "Other",          icon: "⏱",  kind: "hours",  defaultHours: 0,   defaultFreq: "weekly"  }
]

function formatHrs(n) {
  const v = Number(n) || 0
  if (Number.isInteger(v)) return String(v)
  return v.toFixed(1)
}

// Weekly hours math — mirrors editor_source/wizard.html. Window items
// span timeIn → timeOut over N days; hours items normalise frequency to
// weeks via WEEKS_PER_MONTH (4.345).
function weeklyHoursForCalItem(it) {
  if (!it) return 0
  if (it.kind === "window") {
    // Accept the legacy startTime/endTime field names too so plans
    // written by editor_source/wizard.html still compute correctly.
    const start = parseMin(it.timeIn24 || it.startTime || it.start)
    const end   = parseMin(it.timeOut24 || it.endTime  || it.end)
    const days  = Number(it.daysPerWeek) || 0
    if (start == null || end == null || !days) return 0
    let span = end - start
    if (span < 0) span += 24 * 60
    let total = (span / 60) * days
    // Sleep with overnight protective supervision: those hours are
    // providing PS, not resting — deduct so the Sleep total reflects
    // effective sleep. Mirrors editor_source/wizard.html#2980.
    if (it.categoryId === "sleep" && it.protectiveSupervision === true) {
      const ps = Number(it.psHours) || 0
      total = Math.max(0, total - ps)
    }
    return total
  }
  const h = Number(it.hours) || 0
  switch (it.frequency || "weekly") {
    case "weekly":    return h
    case "monthly":   return h / WEEKS_PER_MONTH
    case "quarterly": return h / (WEEKS_PER_MONTH * 3)
    case "annually":  return h / (WEEKS_PER_MONTH * 12)
    default:          return h
  }
}
function parseMin(s) {
  if (!s) return null
  const m = String(s).match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return null
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10)
}

// Day-in-life bucket labels + suggested activities — verbatim from
// editor_source/wizard.html#421 DAY_LABELS and #437 DAY_CATEGORIES.
// We flatten DAY_CATEGORIES[bucket]→activities into a single suggestion
// list per bucket so the user can chip-add the most relevant items.
const DAY_LABELS = {
  morning:         { title: "Morning Time",     icon: "🌅" },
  day:             { title: "School Time",      icon: "🏫" },
  afternoon:       { title: "After School",     icon: "🌇" },
  night:           { title: "Before Bed",       icon: "🌙" },
  afterBed:        { title: "After Bed",        icon: "💤" },
  weekendMorning:  { title: "Morning Time",     icon: "🌅" },
  weekendDay:      { title: "Home & Community", icon: "🏡" },
  weekendNight:    { title: "Before Bed",       icon: "🌙" },
  weekendAfterBed: { title: "After Bed",        icon: "💤" }
}
// Structured category list per bucket — verbatim port of
// editor_source/wizard.html#437 DAY_CATEGORIES. Drives the
// "tap a category → pick activities" modal flow that the legacy
// wizard uses instead of a plain text input. Each category carries
// a default time, an activity list, and optional flags:
//   pickOnly: skip the Indep/Needs help toggle (e.g. transit)
//   yesNo:    swap Indep/Needs help for Yes/No on those activities
//             (only the listed activities — others stay normal)
const DAY_CATEGORIES = {
  morning: [
    { id: "wake",      name: "Wake Up",            icon: "⏰", defaultTime: "7:00 AM",  activities: ["Gets out of bed"] },
    { id: "selfcare",  name: "Morning Self-Care",  icon: "🪥", defaultTime: "7:15 AM",  activities: ["Brushes teeth","Showers/bathes","Gets dressed","Toileting","Takes medication"] },
    { id: "breakfast", name: "Breakfast",          icon: "🍽", defaultTime: "7:30 AM",  activities: ["Prepares food","Eats meal","Drinks/beverages","Takes medication","Cleans up"] },
    { id: "travel_to", name: "Going to School",    icon: "🚌", defaultTime: "7:45 AM",  pickOnly: true, activities: ["Rides school bus","Parent/caregiver drives","Walks","Public transit","Paratransit/special transport"] }
  ],
  day: [
    { id: "in_school",    name: "In School",          icon: "📚", defaultTime: "9:00 AM",  activities: ["General education","Special education","Pull-out services","Resource room","Therapies (SLP/OT/PT)","Counseling","Natural Environment Teaching (NET) (ABA)"] },
    { id: "school_supp",  name: "Supports at School", icon: "🛟", defaultTime: "10:00 AM", activities: ["1:1 aide / paraprofessional","Visual supports","Sensory breaks","AAC device","Behavior plan","Social stories"] },
    { id: "lunch_school", name: "Lunch at School",    icon: "🥪", defaultTime: "12:00 PM", activities: ["Eats lunch","Recess / play","Social with peers","Quiet break"] },
    { id: "travel_home",  name: "Coming Home",        icon: "🚌", defaultTime: "3:00 PM",  pickOnly: true, activities: ["Rides school bus","Parent/caregiver picks up","Walks","Public transit","Paratransit/special transport"] }
  ],
  afternoon: [
    { id: "after_school", name: "After School",       icon: "🎨", defaultTime: "4:00 PM",  activities: ["Snack","Homework","Free play","Screen time","Outdoor play","Sports/exercise","Art/crafts","Music","Reading"] },
    { id: "social",       name: "Social / Friends",   icon: "👥", defaultTime: "4:30 PM",  activities: ["Time with friends","Community activity","Volunteer work","Club/extracurricular"] },
    { id: "household",    name: "Household",          icon: "🏠", defaultTime: "5:00 PM",  activities: ["Chores","Helps in kitchen","Sets/clears table","Pet care","Tidies room","Laundry"] },
    { id: "dinner",       name: "Dinner",             icon: "🍽", defaultTime: "6:00 PM",  activities: ["Prepares food","Eats meal","Drinks/beverages","Takes medication","Cleans up"] },
    { id: "evening_soc",  name: "Family / Visitors",  icon: "👨‍👩‍👧", defaultTime: "6:30 PM", activities: ["Family time","Phone/video calls","Visitors"] }
  ],
  night: [
    { id: "bedtime", name: "Bedtime Routine", icon: "🌙", defaultTime: "8:00 PM", activities: ["Bath/shower","Brushes teeth","Changes into pajamas","Bedtime story/reading","Bedtime snack"] },
    { id: "sleep",   name: "Falling Asleep",  icon: "😴", defaultTime: "9:00 PM", activities: ["Falls asleep on own","Needs someone nearby","Listens to music/sounds","Uses weighted blanket"] }
  ],
  afterBed: [
    { id: "overnight", name: "Sleep Patterns", icon: "🌙", defaultTime: "10:00 PM",
      activities: ["Sleeps through the night","Wakes up occasionally","Needs comfort to fall back asleep","Sleepwalks","Uses bathroom at night"] }
  ],
  weekendMorning: [
    { id: "wake_w",    name: "Wake Up",            icon: "⏰", defaultTime: "9:00 AM",  activities: ["Gets out of bed","Sleeps in"] },
    { id: "selfcare",  name: "Morning Self-Care",  icon: "🪥", defaultTime: "9:15 AM",  activities: ["Brushes teeth","Showers/bathes","Gets dressed","Toileting","Takes medication"] },
    { id: "meals_b",   name: "Breakfast",          icon: "🍽", defaultTime: "9:30 AM",  activities: ["Prepares food","Eats meal","Drinks/beverages","Takes medication","Cleans up"] },
    { id: "morning_w", name: "Morning Activities", icon: "🎨", defaultTime: "10:00 AM", activities: ["Watches TV/cartoons","Free play","Outdoor play","Video games","Tablet/computer time","Art/crafts","Reading"] }
  ],
  weekendDay: [
    { id: "activities_w", name: "Activities & Outings", icon: "🎨", defaultTime: "11:00 AM", activities: ["Park/playground","Swimming","Library","Shopping","Sports/exercise","Art/crafts","Video games","Riding bike"] },
    { id: "social_w",     name: "Social & Community",   icon: "👥", defaultTime: "1:00 PM",  activities: ["Time with friends","Family outing","Visiting relatives","Church/worship","Community event","Volunteer work"] },
    { id: "meals_l",      name: "Lunch",                icon: "🍽", defaultTime: "12:00 PM", activities: ["Prepares food","Eats meal","Drinks/beverages","Takes medication","Cleans up"] },
    { id: "eve_w",        name: "Evening Activities",   icon: "🎬", defaultTime: "5:00 PM",  activities: ["Family movie night","Games (board/card/video)","Creative projects","Outdoor play","Screen time","Dinner out"] },
    { id: "household",    name: "Household",            icon: "🏠", defaultTime: "5:00 PM",  activities: ["Chores","Helps in kitchen","Sets/clears table","Pet care","Tidies room","Laundry"] },
    { id: "meals_d",      name: "Dinner",               icon: "🍽", defaultTime: "6:00 PM",  activities: ["Prepares food","Eats meal","Drinks/beverages","Takes medication","Cleans up"] },
    { id: "social_e",     name: "Evening Social",       icon: "👥", defaultTime: "6:30 PM",  activities: ["Family time","Phone/video calls","Visitors"] }
  ],
  weekendNight: [
    { id: "bedtime", name: "Bedtime Routine", icon: "🌙", defaultTime: "8:00 PM", activities: ["Bath/shower","Brushes teeth","Changes into pajamas","Bedtime story/reading","Bedtime snack"] },
    { id: "sleep",   name: "Falling Asleep",  icon: "😴", defaultTime: "9:00 PM", activities: ["Falls asleep on own","Needs someone nearby","Listens to music/sounds","Uses weighted blanket"] }
  ],
  weekendAfterBed: [
    { id: "overnight", name: "Sleep Patterns", icon: "🌙", defaultTime: "10:00 PM",
      activities: ["Sleeps through the night","Wakes up occasionally","Needs comfort to fall back asleep","Sleepwalks","Uses bathroom at night"] }
  ]
}

// Parse a stored day-in-life entry. Format mirrors legacy:
//   [time] <activity>(0|1|Y|N) | <activity>(...)   (combined)
//   [time] <activity>                              (pickOnly)
// Returns { time, parts: [{ text, mark }] } where mark is "" | "0" |
// "1" | "Y" | "N".
const parseDayEntry = (raw) => {
  const txt = String(raw || "")
  const tm  = txt.match(/^\[([^\]]+)\]\s*/)
  const time = tm ? tm[1] : ""
  const body = tm ? txt.slice(tm[0].length) : txt
  if (!body) return { time, parts: [] }
  const parts = body.split("|").map(seg => {
    const s = seg.trim()
    const m = s.match(/^<([^>]+)>(?:\(([01YN])\))?\s*$/)
    if (m) return { text: m[1], mark: m[2] || "" }
    return { text: s, mark: "" }
  }).filter(p => p.text)
  return { time, parts }
}
const MARK_LABEL = { "0": "Indep.", "1": "Help", "Y": "Yes", "N": "No" }
const MARK_CLASS = { "0": "is-indep", "1": "is-help", "Y": "is-yes", "N": "is-no" }
const renderDayChip = (field, idx, raw) => {
  const { time, parts } = parseDayEntry(raw)
  const inner = parts.length
    ? parts.map(p => {
        const cls = p.mark ? ` ${MARK_CLASS[p.mark]}` : ""
        const badge = p.mark ? `<span class="q-day-chip-badge${cls}">${MARK_LABEL[p.mark] || p.mark}</span>` : ""
        return `<span class="q-day-chip-part${cls}">${esc(p.text)}${badge}</span>`
      }).join("")
    : `<span class="q-day-chip-part">${esc(raw)}</span>`
  const timePill = time ? `<span class="q-day-chip-time">${esc(time)}</span>` : ""
  return `
    <span class="q-day-chip">
      ${timePill}
      ${inner}
      <button type="button"
              class="q-day-chip-x"
              data-action="wizard#removeDayItem"
              data-day-field="${esc(field)}"
              data-day-idx="${idx}"
              title="Remove">×</button>
    </span>`
}

// Time helpers — convert between 24h (HTML <input type="time">) and
// the 12h string the legacy editor saves into plan entries.
const parseTo24h = (s) => {
  const m = String(s || "").match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i)
  if (!m) return ""
  let h = parseInt(m[1], 10)
  const min = m[2]
  const ap = (m[3] || "").toUpperCase()
  if (ap === "PM" && h < 12) h += 12
  if (ap === "AM" && h === 12) h = 0
  return `${String(h).padStart(2, "0")}:${min}`
}
const format12h = (t24) => {
  const m = String(t24 || "").match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return ""
  let h = parseInt(m[1], 10)
  const min = m[2]
  const ap = h >= 12 ? "PM" : "AM"
  h = h % 12 || 12
  return `${h}:${min} ${ap}`
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"]

// Parses every School Start / School End entry on plan.dayInLife.day.
// Supports both the legacy day-less form (`[time] <School Start>`,
// treated as covering Mon–Fri) and the multi-day form
// (`[time] <School Start> on Mon, Wed, Fri`). Returns an array of
// { kind: 'start'|'end', time12, time24, days: ['Mon',...] }.
const parseSchoolSchedule = (items) => {
  const out = []
  ;(items || []).forEach(it => {
    const t = typeof it === "string" ? it : (it.text || "")
    const m = t.match(/^\[([^\]]+)\]\s*<School (Start|End|starts|ends)>(?:\s+on\s+(.+?))?\s*$/i)
    if (!m) return
    const time12 = m[1].trim()
    const k      = m[2].toLowerCase()
    const kind   = (k === "start" || k === "starts") ? "start" : "end"
    const tail   = (m[3] || "").trim()
    const days   = tail
      ? tail.split(/[\s,]+/).map(s => s.trim()).filter(d => WEEKDAYS.includes(d))
      : [...WEEKDAYS]
    out.push({ kind, time12, time24: parseTo24h(time12) || "", days: days.length ? days : [...WEEKDAYS] })
  })
  return out
}

// Writes a fresh set of School Start / End entries to dayInLife.day,
// merging entries that share kind + time so the editor pane shows
// one tidy "School Start 8:00 AM on Mon, Wed, Fri" line per group.
// Omits the "on …" suffix when an entry covers every weekday so the
// same-mode UI round-trips with legacy single-time entries.
const writeSchoolSchedule = (arr, entries) => {
  const cleaned = arr.filter(it => {
    const t = typeof it === "string" ? it : (it.text || "")
    return !/^\[[^\]]+\]\s*<School (?:Start|starts|End|ends)>/i.test(t)
        && !/^<Does not attend school>/i.test(t)
  })
  arr.length = 0
  cleaned.forEach(c => arr.push(c))
  const grouped = {}
  entries.forEach(e => {
    if (!e.time12 || !e.days?.length) return
    const k = `${e.kind}|${e.time12}`
    if (!grouped[k]) grouped[k] = { kind: e.kind, time12: e.time12, days: [] }
    e.days.forEach(d => { if (!grouped[k].days.includes(d)) grouped[k].days.push(d) })
  })
  Object.values(grouped).forEach(e => {
    const label = e.kind === "start" ? "School Start" : "School End"
    e.days.sort((a, b) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b))
    const isAll = e.days.length === WEEKDAYS.length && WEEKDAYS.every(d => e.days.includes(d))
    const suffix = isAll ? "" : ` on ${e.days.join(", ")}`
    arr.push({ id: Date.now() + Math.random(), text: `[${e.time12}] <${label}>${suffix}` })
  })
}

const DAY_SUGGESTIONS = {
  morning:         ["Gets out of bed","Brushes teeth","Showers/bathes","Gets dressed","Toileting","Takes medication","Prepares food","Eats meal","Drinks/beverages","Cleans up","Rides school bus","Parent/caregiver drives","Walks","Public transit","Paratransit/special transport"],
  day:             ["General education","Special education","Pull-out services","Resource room","Therapies (SLP/OT/PT)","Counseling","Natural Environment Teaching (NET) (ABA)","1:1 aide / paraprofessional","Visual supports","Sensory breaks","AAC device","Behavior plan","Social stories","Eats lunch","Recess / play","Social with peers","Quiet break"],
  afternoon:       ["Snack","Homework","Free play","Screen time","Outdoor play","Sports/exercise","Art/crafts","Music","Reading","Time with friends","Community activity","Volunteer work","Club/extracurricular","Chores","Helps in kitchen","Sets/clears table","Pet care","Tidies room","Laundry","Prepares food","Eats meal","Family time","Phone/video calls","Visitors"],
  night:           ["Bath/shower","Brushes teeth","Changes into pajamas","Bedtime story/reading","Bedtime snack","Falls asleep on own","Needs someone nearby","Listens to music/sounds","Uses weighted blanket"],
  afterBed:        ["Sleeps through the night","Wakes up occasionally","Needs comfort to fall back asleep","Sleepwalks","Uses bathroom at night"],
  weekendMorning:  ["Gets out of bed","Sleeps in","Brushes teeth","Showers/bathes","Gets dressed","Toileting","Takes medication","Prepares food","Eats meal","Drinks/beverages","Cleans up","Watches TV/cartoons","Free play","Outdoor play","Video games","Tablet/computer time","Art/crafts","Reading"],
  weekendDay:      ["Park/playground","Swimming","Library","Shopping","Sports/exercise","Art/crafts","Video games","Riding bike","Time with friends","Family outing","Visiting relatives","Church/worship","Community event","Volunteer work","Family movie night","Games (board/card/video)","Creative projects","Dinner out","Chores","Helps in kitchen","Sets/clears table","Pet care","Tidies room","Laundry","Prepares food","Eats meal","Family time","Phone/video calls","Visitors"],
  weekendNight:    ["Bath/shower","Brushes teeth","Changes into pajamas","Bedtime story/reading","Bedtime snack","Falls asleep on own","Needs someone nearby","Listens to music/sounds","Uses weighted blanket"],
  weekendAfterBed: ["Sleeps through the night","Wakes up occasionally","Needs comfort to fall back asleep","Sleepwalks","Uses bathroom at night"]
}

// Chip option banks used by the About section — verbatim from
// editor_source/wizard.html#528 OPTIONS. The {value, desc, icon} shape
// per multi-select option carries a lucide icon name so each card can
// surface a small monochrome glyph next to its value.
const ABOUT_OPTIONS = {
  whoLivesWithMe: ["Mom","Dad","Spouse","Brother","Sister","Grandma","Grandpa","Pet dog","Pet cat","Roommate","Caregiver","Other"],
  communicationStyle: [
    { value: "Verbal",                       desc: "Uses spoken words and sentences",         icon: "mic" },
    { value: "Non-Verbal",                   desc: "Communicates without spoken words",       icon: "mic-off" },
    { value: "Limited Expressive Language",  desc: "Has difficulty expressing thoughts in words", icon: "message-square-off" },
    { value: "Limited Receptive Language",   desc: "Has difficulty understanding spoken language", icon: "ear-off" },
    { value: "Sign Language / ASL",          desc: "Uses sign language to communicate",       icon: "hand" },
    { value: "AAC Device",                   desc: "Uses an augmentative communication device", icon: "cpu" },
    { value: "Picture Exchange / PECS",      desc: "Uses pictures or symbols to communicate", icon: "image" },
    { value: "Gestures & Body Language",     desc: "Points, nods, waves, or uses facial expressions", icon: "hand-metal" },
    { value: "Written Communication",        desc: "Communicates through writing or typing",  icon: "pen-line" },
    { value: "Echolalia",                    desc: "Repeats words or phrases heard from others", icon: "repeat" },
    { value: "Behavioral Communication",     desc: "Communicates needs through behavior",     icon: "activity-square" }
  ],
  schoolDetails: ["Elementary school","Middle school","High school","College","Homeschooled","Other school","Does not go to school"],
  workDetails:   ["Not yet","Volunteer work","Part-time job","Full-time job","Job training program","Internship","Day program","Supported employment","Other"],
  // Scalar-field suggestions (tap to toggle a token in the comma / · list).
  primaryLanguages: ["English","Spanish","Mandarin","Cantonese","Vietnamese","Tagalog","Korean","Armenian","Arabic","Farsi","Russian","ASL"],
  // Lanterman Act / DDS person-centered cultural preferences (W&I Code §4512,
  // §4646, §4646.5, §4519.5, §4620.4 LACC) — framed as preferences/details to
  // HONOR in services, not clinical demographics. Language, living situation,
  // and communication have their own fields, so they're not duplicated here.
  // No commas (the value splits on comma/·).
  culturalIdentity: ["Ethnic & cultural heritage","Religion / faith","Spiritual practices","Dietary customs","Holidays & observances","Traditions & celebrations","Family & community values","Dress & grooming","Gender / modesty","Music & arts","Customs to respect in care"],
  livingSituation:  ["Family home","Foster home","Group home","Other"]
}

// Lucide icon mapping for chip suggestion labels. Keys are matched
// case-insensitively against the chip label so "Mom" + "mom" both
// hit the same icon. Unknown labels render without an icon (the chip
// still works — it just won't have a glyph).
const CHIP_ICON_MAP = {
  // Who lives with me / Circle of Support
  "mom": "user-round", "dad": "user-round", "spouse": "heart",
  "brother": "user-round", "sister": "user-round",
  "grandma": "user-round", "grandpa": "user-round",
  "pet dog": "dog", "pet cat": "cat",
  "roommate": "users", "caregiver": "hand-helping", "other": "user-plus",
  "family": "users", "friend": "smile", "professional": "briefcase",
  "community": "globe", "service dog": "dog",
  // School levels
  "elementary school": "school", "middle school": "school", "high school": "school",
  "college": "graduation-cap", "homeschooled": "home", "other school": "school",
  "does not go to school": "ban",
  // Work
  "not yet": "clock", "volunteer work": "hand-helping",
  "part-time job": "briefcase", "full-time job": "briefcase",
  "job training program": "graduation-cap", "internship": "briefcase",
  "day program": "calendar-days", "supported employment": "briefcase",
  // Common day-in-life words
  "gets out of bed": "alarm-clock", "brushes teeth": "smile",
  "showers/bathes": "shower-head", "gets dressed": "shirt",
  "toileting": "toilet", "takes medication": "pill",
  "prepares food": "utensils", "eats meal": "utensils",
  "drinks/beverages": "coffee", "cleans up": "spray-can",
  "rides school bus": "bus", "walks": "footprints",
  "public transit": "train-front", "screen time": "smartphone",
  "outdoor play": "trees", "art/crafts": "palette",
  "reading": "book-open", "homework": "pencil",
  // Common activities / preferences
  "library": "library", "swimming": "waves", "park/playground": "trees",
  "shopping": "shopping-bag", "music": "music",
  "sports/exercise": "dumbbell", "video games": "gamepad-2",
  "riding bike": "bike", "family time": "users",
  "family outing": "users", "visiting relatives": "users",
  "church/worship": "cross", "community event": "globe",
  // Common goals/services
  "speech therapy": "mic", "occupational therapy": "hand",
  "physical therapy": "activity", "aba/behavioral therapy": "brain",
  "respite care": "shield", "day program ": "calendar-days",
  "transportation services": "bus", "nursing": "heart-pulse",
  "mental health counseling": "brain", "tutoring": "graduation-cap"
}
function chipIconFor(label) {
  if (!label) return ""
  return CHIP_ICON_MAP[String(label).toLowerCase().trim()] || ""
}

// Transient UI state for the Circle-of-Support inline accordion. Lives
// at module scope because the renderer methods on STEP_RENDERERS run
// with `this` bound to that object (not the controller). State survives
// re-renders and is reset on a fresh wizard connect via _resetWizCosUi.
const _wizCosUi = { expand: null, more: null, drafts: {} }

// Service-calendar legend state — which service the user has picked
// in the wizard pane as the "active" brush, and the Weekly/Monthly
// toggle for the hours pill on each chip. Module scope because the
// renderer methods on STEP_RENDERERS run with `this` bound to that
// object (not the controller). Live across re-renders; reset on a
// fresh wizard connect via _resetWizRcUi.
const _wizRcUi = { active: null, monthly: false, editing: null }

// Goal-suggestion → schedule micro-step. After the user clicks "+"
// on a suggested goal, the wizard hides the deck and shows a single
// schedule-row form (day type, # of times, hours, minutes) so they
// can set the frequency for the just-added goal before browsing more
// suggestions. Module-scope so renderers can read it; the controller
// owns the writes (addGoalFromSuggestion sets it; saveGoalSchedule /
// cancelGoalSchedule clear it).
// Step ids for the post-add walkthrough.
//   tag         — Generic / RC Funded / Unmet Need
//   vendor      — pick a vendor from the directory; auto-fills name,
//                 address, website, phone, email
//   service     — pick one of the vendor's services; auto-fills cost
//                 and location
//   frequency   — schedule row (day type, times, hours, mins)
const GOAL_WIZ_STEPS = ["tag", "vendor", "service", "frequency"]
const _wizGoalUi = {
  pendingSchedGoalId: null,
  step: "tag",
  draft: {
    tag: "general",
    // Vendor fields — back-filled when the user picks from the directory.
    vendorId: null,
    vendorName: "",
    vendorWebsite: "",
    vendorPhone: "",
    vendorEmail: "",
    vendorAddress: "",
    // Service + cost — pre-filled from the picked vendor, user-editable.
    serviceType: "",
    costDisplay: "",
    // Frequency / schedule row.
    dayType: "Weekly", times: "", hours: "", mins: ""
  }
}

// Service legend palette — colors + abbrevs MUST match the editor's
// resource_calendar_controller so the wizard's swatch and the grid's
// fill agree on what's what. Keep these tables in sync if either side
// adds a new service.
const RC_PALETTE = [
  "#3b82f6","#22c55e","#a855f7","#f59e0b","#ec4899","#14b8a6",
  "#6366f1","#f97316","#06b6d4","#84cc16","#f43f5e","#0ea5e9"
]
const RC_BUILTIN = ["In Parental Care", "Charter Home School"]
const RC_ABBREV = {
  "IHSS - In-Home Supportive Services": "IH",
  "IHSS – In-Home Supportive Services": "IH",
  "Personal assistance Services":       "PA",
  "Personal Assistance Services":       "PA",
  "Respite Services - In-Home":         "RS",
  "Respite Services – In-Home":         "RS",
  "Respite Services - Out-of-Home":     "RO",
  "Day Program":                        "DP",
  "Transportation":                     "Tr",
  "Behavioral Therapy":                 "BT",
  "Behavior Services":                  "BS",
  "Behavior Management Services":       "BM",
  "Speech Therapy":                     "ST",
  "Occupational Therapy":               "OT",
  "Occupational Therapy (OT)":          "OT",
  "Physical Therapy":                   "PT",
  "Physical Therapy (PT)":              "PT",
  "Social Recreation":                  "SR",
  "Adaptive Swimming":                  "AS",
  "Tutoring Services":                  "Tutr",
  "Service Coordination":               "SC",
  "Parental Care":                      "PC",
  "In Parental Care":                   "PC",
  "School":                             "SCH",
  "Charter Home School":                "SCH",
  "Music Class":                        "MC",
  "Music Lessons":                      "MC",
  "Art Class":                          "AC",
  "Dance Class":                        "DC",
  "Dance class":                        "DC",
  "Dance Lessons":                      "DC",
  "Art Lessons":                        "AC",
  "Swimming Lessons":                   "SW",
  "Day Camp":                           "DYC",
  "Overnight Camp":                     "ONC",
  "Respite":                            "RS",
  "Personal Assistance":                "PA",
  "Other":                              "Othr"
}
// Abbreviation lookup: per-plan custom override (edited from the legend)
// → canonical map → first-4-char fallback. MUST match
// resource_calendar_controller#_abbrev so the wizard swatch and the grid
// fill agree. An empty override means "use the default".
function rcAbbrev(name, plan) {
  if (!name) return ""
  const custom = (plan && plan.resourceCalendar && plan.resourceCalendar.abbrev) || {}
  if (custom[name]) return String(custom[name])
  if (RC_ABBREV[name]) return RC_ABBREV[name]
  // Any school name (e.g. "Pine Elementary") → SCH.
  if (plan && name === rcSchoolName(plan)) return "SCH"
  return name.length > 4 ? name.slice(0, 4) : name
}
// Weekly REQUESTED hours from a goal resource's schedule rows
// ({dayType, times, hours, mins}). Mirrors goal_resources' ANNUAL_PERIODS.
const RC_PERIODS_YR = {
  "Daily": 365, "Mon-Fri": 260, "Sat-Sun": 104, "Non-School": 0,
  "Weekly": 52, "Bi-Weekly": 26, "Monthly": 12, "Quarterly": 4,
  "Semi-Annually": 2, "Annually": 1, "As Needed": 0,
  "Mon-Wed-Fri": 156, "Tue-Thu": 104, "Mon-Wed": 104, "Tue-Thu-Sat": 156,
  "Mon": 52, "Tue": 52, "Wed": 52, "Thu": 52, "Fri": 52, "Sat": 52, "Sun": 52
}
function rcWeeklyReqHrs(schedule) {
  let yr = 0
  ;(Array.isArray(schedule) ? schedule : []).forEach(s => {
    const times = parseFloat(String(s.times || "1").replace(/[^0-9.]/g, "")) || 1
    const h = (parseFloat(s.hours) || 0) + (parseFloat(s.mins) || 0) / 60
    yr += h * times * (RC_PERIODS_YR[s.dayType] || 0)
  })
  return yr / 52
}
// School name from the About section (about.schoolDetails) — used for the
// built-in "school" slot. Falls back to the generic "Charter Home School".
function rcSchoolName(plan) {
  const items = (plan && plan.about && plan.about.schoolDetails) || []
  for (const it of items) {
    const raw = (typeof it === "string" ? it : (it && (it.text || it.description)) || "").trim()
    if (raw.length >= 2) return raw.replace(/\s*[,(].*$/, "").trim() || raw
  }
  return "Charter Home School"
}

// Display / paint label for a resource's service — mirrors the Unmet
// Needs list (unmet_needs_list_controller): when the service is "Other",
// the real name lives in r.comment (e.g. "Karate Class"), so show that
// instead of the literal "Other". Keeps the legend chips, the grid paint
// key, and the Unmet Needs table all naming the same thing.
function rcServiceLabel(r) {
  const svc = ((r && (r.text || r.type)) || "").trim()
  if (svc === "Other") return (((r && r.comment) || "").trim()) || "Other"
  return svc
}

function rcServicesFor(plan) {
  const builtin = [
    { text: "Parental Care",    source: "parental" },
    { text: rcSchoolName(plan), source: "parental" }
  ]
  // A linked current service stores its TYPE ("Other"); resolve "Other" to
  // the typed-in name from the matching goal/barrier resource comment so the
  // legend reads e.g. "Trampoline Park Membership" instead of "Other".
  const resolveOtherName = (t) => {
    if (t !== "Other") return t
    // Exclude UNMET "Other" resources — those belong to Unmet Needs, not
    // Current Services, so a current service must resolve to the rc/generic
    // "Other" (e.g. "Trampoline Park Membership", not the unmet "Adaptive
    // Swimming").
    const find = (list) => (list || [])
      .flatMap(x => Array.isArray(x.resources) ? x.resources : [])
      .filter(r => r && r.tag !== "unmet" && ((r.type || r.text || "").trim() === "Other") && (r.comment || "").trim())
      .map(r => r.comment.trim())[0]
    return find(plan.goals) || find(plan.barriers) || "Other"
  }
  const fromCurrent = (plan.currentServices || [])
    .map(s => ({ text: resolveOtherName((s.linkedType || s.text || "").trim()), source: "current" }))
    .filter(s => s.text)
  // UNMET NEEDS mirror the Unmet Needs list EXACTLY: every Goal AND
  // Barrier resource tagged "unmet", labeled by its Service selection
  // ("Other" → the typed-in r.comment). NOT the Unmet Needs SECTION
  // (plan.unmetNeeds, broad DDS categories) and NOT the goal
  // heading/domain. Each entry carries its source reference
  // ("Goal 3" / "Barrier 2") + heading and the weekly REQUESTED hours
  // from the resource schedule (the "/req" half of the Scheduled /
  // Requested pill).
  const fromUnmet = []
  const collectUnmet = (list, kind, headingOf) => {
    ;(list || []).forEach((item, i) => {
      if (!item || !Array.isArray(item.resources)) return
      item.resources.forEach(r => {
        if (!r || r.tag !== "unmet") return
        const text = rcServiceLabel(r)
        if (!text) return
        fromUnmet.push({
          text, source: "unmet",
          refKind: kind, refNum: i + 1,
          refHeading: (headingOf(item) || "").trim(),
          reqHrs: rcWeeklyReqHrs(r.schedule)
        })
      })
    })
  }
  collectUnmet(plan.goals,    "Goal",    g => g.heading)
  collectUnmet(plan.barriers, "Barrier", b => b.description || b.strategy || "")
  const seen = new Set()
  const out = []
  ;[...builtin, ...fromCurrent, ...fromUnmet].forEach(s => {
    if (seen.has(s.text)) return
    seen.add(s.text); out.push(s)
  })
  return out
}
function rcColorFor(service, allServices) {
  if (service === "Parental Care") return "#94a3b8"
  const names = allServices.filter(s => s.text !== "Parental Care").map(s => s.text)
  const idx = names.indexOf(service)
  return RC_PALETTE[(idx >= 0 ? idx : 0) % RC_PALETTE.length]
}

// Which sub-block in the editor pane currently has focus. Filled by the
// `editor-native:subblock-focus` listener on the wizard controller.
// `path` is the dotted plan path (e.g. "about.whoLivesWithMe"); for
// Circle-of-Support rows `category` carries the relationship so the
// right accordion auto-opens. Renderers consult this to scope their
// output to the focused block; null means "show everything" (initial
// state when the user hasn't clicked into any specific block yet).
const _wizSubFocus = { section: null, path: null, category: null, rowId: null }

// Compact "focused on the clicked block" chip shown at the top-right of
// the wizard card, with a "Show all" button to clear the sub-focus.
// Reused by every section that scopes to a clicked sub-block.
const SUBFOCUS_PILL = `
          <div class="q-subfocus-pill" title="Showing just the block you clicked in the editor">
            <i data-lucide="filter"></i>
            <button type="button" class="q-subfocus-clear"
                    data-action="wizard#clearSubFocus">Show all</button>
          </div>`

// Wraps a { path: html } blocks map in the standard focused/unfocused
// view: when the editor signals a clicked sub-block in `section`, show
// ONLY that block + the "Show all" pill; otherwise show every block.
function subFocusView(section, blocks) {
  const focusPath = _wizSubFocus.section === section ? _wizSubFocus.path : null
  const inner = (focusPath && blocks[focusPath])
    ? `${SUBFOCUS_PILL}${blocks[focusPath]}`
    : Object.values(blocks).join("")
  return `<div class="q-space-y-9">${inner}</div>`
}

// Step content renderers. Each receives the current plan and returns
// HTML. Form inputs use data-plan-field for two-way binding through
// the wizard controller's update handler.
const STEP_RENDERERS = {
  // Mirrors editor_source/wizard.html#970 SECTION_RENDERERS.client —
  // two 2-col grids (First/Last on top, DOB/ZIP on bottom). Each field
  // carries a description, an input, and an "Unknown — mark as TBD"
  // checkbox below.
  client(plan) {
    const c = plan.client || {}
    return `
      <div class="q-space-y-7">
        <div class="q-grid q-grid-2">
          ${qField({
            label: "First name",
            help:  "What does the team call this person?",
            input: `<input class="q-input" data-plan-field="client.firstName" value="${esc(c.firstName)}" placeholder="e.g. Sam">`
          })}
          ${qField({
            label: "Last name",
            help:  "Family or chosen surname.",
            input: `<input class="q-input" data-plan-field="client.lastName" value="${esc(c.lastName)}">`
          })}
        </div>
        <div class="q-grid q-grid-2">
          ${qField({
            label: "Date of birth",
            help:  "Used for the plan header. We use this to adjust later prompts (school vs. work).",
            input: `<input type="date" class="q-input" data-plan-field="client.dob" value="${esc(/^\d{4}-\d{2}-\d{2}$/.test(c.dob || "") ? c.dob : "")}">`,
            tbdFor: "client.dob", tbdChecked: flagState(plan, "client.dob", c.dob)
          })}
        </div>
        ${qField({
          label: "Street address",
          help:  "Type a few characters — we'll suggest matches and auto-fill city / state / ZIP. Used by the goal-resources Mileage lookup as the starting point. Not shown on the PDF — kept private to your workspace.",
          input: `<input class="q-input" data-controller="address-autocomplete" data-plan-field="client.address" placeholder="123 Main St" value="${esc(c.address)}">`
        })}
        <div class="q-grid q-grid-2">
          ${qField({
            label: "ZIP code",
            help:  "5-digit ZIP — auto-fills when you pick a Street address suggestion, or resolves a city when you type one.",
            input: `<input class="q-input" data-plan-field="client.zip" inputmode="numeric" maxlength="5" placeholder="90638" value="${esc(c.zip)}">
                    <div class="q-zip-status" data-wizard-target="zipStatus">${(c.city && c.state) ? esc(c.city + ", " + c.state) : ""}</div>`,
            tbdFor: "client.zip", tbdChecked: flagState(plan, "client.zip", c.zip)
          })}
        </div>
      </div>`
  },

  about(plan) {
    const a = plan.about || {}
    const youth = isYouthAge(plan)
    // Each sub-block is keyed by its dotted plan path so we can render
    // a single one when the editor pane signals focus (see
    // editor-native:subblock-focus). All four are rendered when no
    // sub-block has focus yet.
    const blocks = {
      // NOTE: "Who lives with [FirstName]?" was relocated to the Circle of
      // Support section (issue #7) — it is edited there now, so it is NOT
      // rendered in the About step to avoid a duplicate.
      "about.communicationStyle": multiSelectBlock(a.communicationStyle || [], "about.communicationStyle", "How [FirstName] Communicates", "Select all that apply.", ABOUT_OPTIONS.communicationStyle),
      "about.schoolDetails": dynamicListInput(a.schoolDetails, "about.schoolDetails", "School", "e.g. 4th grade at Lincoln Elementary",
        { help: "Tap a quick option, then type the name or details.", suggestions: ABOUT_OPTIONS.schoolDetails }),
      "about.workDetails": dynamicListInput(a.workDetails, "about.workDetails", "Work or day program", "e.g. Volunteers at the library on Saturdays",
        { help: "Tap a quick option to add — type details after.", suggestions: ABOUT_OPTIONS.workDetails }),
      "about.primaryLanguages": qField({
        label: "Primary language(s) at home",
        help: "Tap any that apply, or type your own. Flags interpreter / translation needs.",
        chips: tokenChipsRow("about.primaryLanguages", ABOUT_OPTIONS.primaryLanguages, a.primaryLanguages, ", "),
        tbdFor: "about.primaryLanguages", tbdChecked: flagState(plan, "about.primaryLanguages", a.primaryLanguages)
      }),
      "about.culturalIdentity": qField({
        label: "Cultural preferences and details (if any)",
        chips: tokenChipsRow("about.culturalIdentity", ABOUT_OPTIONS.culturalIdentity, a.culturalIdentity, " · "),
        tbdFor: "about.culturalIdentity", tbdChecked: flagState(plan, "about.culturalIdentity", a.culturalIdentity)
      }),
      "about.livingSituation": livingSituationBlock(a)
    }
    const focusPath = _wizSubFocus.section === "about" ? _wizSubFocus.path : null
    if (focusPath && blocks[focusPath]) {
      return `
        <div class="q-space-y-9">
          <div class="q-subfocus-pill" title="Showing just the block you clicked in the editor">
            <i data-lucide="filter"></i>
            <button type="button" class="q-subfocus-clear"
                    data-action="wizard#clearSubFocus">Show all</button>
          </div>
          ${blocks[focusPath]}
        </div>`
    }
    // Field order per request: Communication first, then language, culture,
    // living situation, school, work.
    const order = [
      "about.communicationStyle", "about.primaryLanguages", "about.culturalIdentity",
      "about.livingSituation", "about.schoolDetails", "about.workDetails"
    ]
    return `
      <div class="q-space-y-9">
        ${order.map(k => blocks[k]).join("")}
      </div>`
  },

  circleOfSupport(plan) {
    if (!plan.about) plan.about = {}
    if (!Array.isArray(plan.about.circleOfSupport)) plan.about.circleOfSupport = []
    if (!plan.meta) plan.meta = {}
    if (!Array.isArray(plan.meta.standardTeamDismissed)) plan.meta.standardTeamDismissed = []
    // No auto-seed. The Circle of Support starts empty — users add team
    // members via "+ Add Person", Bulk Add, or the role modal. (This used
    // to seed the 10 PROFESSIONAL_STANDARD roles as "TBD (role)"
    // placeholders on first visit; removed per request.)
    const list = plan.about.circleOfSupport
    const empty = list.length === 0
    const pill = (p) => {
      const isTbd = TBD_PATTERNS_TEST.test(String(p.name || ""))
      const parsed = parseCircleEntry(p.name || "", p.relationship || "")
      const specific = parsed.relationship || parsed.role || p.relationship || ""
      return `
        <span class="q-cos-pill q-cos-pill--${esc(cosColorKey(p.relationship))}${isTbd ? " is-tbd" : ""}">
          <i data-lucide="${esc(cosIconFor(p.relationship))}" class="q-cos-pill-icon"></i>
          <button type="button" class="q-cos-name"
                  data-action="wizard#editCircleEntry"
                  data-row-id="${p.id}"
                  title="${isTbd ? "Click to fill in details" : "Click to edit"}">
            ${esc(p.name || "(no name)")}
          </button>
          ${specific ? `<span class="q-cos-role">${esc(specific)}</span>` : ""}
          <button type="button" class="q-cos-del"
                  data-action="wizard#removeCircleEntry"
                  data-row-id="${p.id}" title="Remove">&times;</button>
        </span>`
    }
    // Wizard pane: each category is an accordion card. Click a header
    // to expand it inline — the body shows category-specific suggestion
    // chips, a Name field, (Family-only) Under-18 capture, and an
    // inline Add button. Replaces the legacy modal-on-click flow with
    // a single-pane browse-and-pick UX that uses the empty space.
    // UI state lives on _wizCosUi (module scope) because `this`
    // inside STEP_RENDERERS methods refers to the renderers object,
    // not the wizard controller.
    const expanded = _wizCosUi.expand || ""
    const cats = ["Family", "Friend", "Professional", "Community"]
    // Isolate to a single group when the editor pane focuses one (subblock
    // focus) — mirrors the About section's isolate-in-wizard behavior.
    const focusCat = (_wizSubFocus.section === "circleOfSupport" && cats.includes(_wizSubFocus.category))
      ? _wizSubFocus.category : null
    const shownCats = focusCat ? [focusCat] : cats
    // Focusing the household list in the editor ("Who Lives With …") isolates a
    // household picker here — it isn't one of the category accordions, so give
    // it the same dynamic-list picker the About step used to host.
    if (_wizSubFocus.section === "circleOfSupport" && _wizSubFocus.path === "about.whoLivesWithMe") {
      return `
        <div class="q-listsection">
          <div class="q-subfocus-pill" title="Showing just the block you clicked in the editor">
            <i data-lucide="filter"></i>
            <button type="button" class="q-subfocus-clear" data-action="wizard#clearSubFocus">Show all</button>
          </div>
          ${dynamicListInput((plan.about && plan.about.whoLivesWithMe) || [], "about.whoLivesWithMe", "Who lives with [FirstName]?", "Or type your own…",
            { help: "Tap a chip to add — then type the name (e.g. tap \"Brother\", type \" Alex\"). Click any existing person on the left to jump to it here.", suggestions: ABOUT_OPTIONS.whoLivesWithMe, focusRowId: _wizSubFocus.rowId })}
        </div>`
    }
    const fieldsByCat = {
      "Family":       { role: "relationship", roleLabel: "Relationship",      rolePh: "e.g. Mom, Uncle, Cousin",
                        namePh: "e.g. Maria Rivera",
                        suggestions: FAMILY_RELATIONSHIP_SUGGESTIONS },
      "Friend":       { role: "role",         roleLabel: "Friend type",       rolePh: "e.g. School friend, Soccer friend",
                        namePh: "e.g. Jordan Lee",
                        suggestions: FRIEND_CONTEXT_SUGGESTIONS },
      "Professional": { role: "role",         roleLabel: "Role / Title",      rolePh: "e.g. Speech Therapist (SLP)",
                        namePh: "e.g. Dr. Sarah Chen",
                        suggestions: PROFESSIONAL_STANDARD,
                        moreGroups: PROFESSIONAL_MORE_GROUPS },
      "Community":    { role: "role",         roleLabel: "Role / Connection", rolePh: "e.g. Coach, Mentor, Neighbor",
                        namePh: "e.g. Coach Martinez",
                        suggestions: COMMUNITY_ROLE_SUGGESTIONS }
    }
    const planList = (plan.about && plan.about.circleOfSupport) || []
    const inCircleFor = (cat, fid, s) => {
      const lc = (s || "").toLowerCase()
      return planList.some(p => {
        if (!p || p.relationship !== cat) return false
        const parsed = parseCircleEntry(p.name, cat)
        return ((parsed[fid] || "").toLowerCase() === lc)
      })
    }
    const flatChips = (cat, fid, items) => `
      <div class="q-cos-chiprow">
        ${items.map(s => {
          const sel = inCircleFor(cat, fid, s)
          return `
            <button type="button"
                    class="q-cos-chip${sel ? " is-on" : ""}"
                    data-action="wizard#pickCircleChip"
                    data-cos-cat="${esc(cat)}"
                    data-cos-fid="${esc(fid)}"
                    data-cos-chip="${esc(s)}">
              ${sel ? '<span class="q-cos-chip-check">&#10003;</span>' : ""}
              ${esc(s)}
            </button>`
        }).join("")}
      </div>`
    const groupedChips = (cat, fid, groups) => `
      <div class="q-cos-chipgroups">
        ${Object.entries(groups).map(([g, items]) => `
          <div class="q-cos-chipgroup">
            <div class="q-cos-chipgroup-head">${esc(g)}</div>
            ${flatChips(cat, fid, items)}
          </div>`).join("")}
      </div>`

    const items = shownCats.map(cat => {
      const f = fieldsByCat[cat]
      const isOpen = expanded === cat || focusCat === cat
      const colorKey = cosColorKey(cat)
      return `
        <div class="q-cos-accord-item q-cos-accord-item--${esc(colorKey)}${isOpen ? " is-open" : ""}">
          <button type="button"
                  class="q-cos-accord-head"
                  data-action="wizard#toggleCircleCat"
                  data-cos-cat="${esc(cat)}"
                  aria-expanded="${isOpen ? "true" : "false"}">
            <span class="q-cos-add-plus">+</span>
            <i data-lucide="${esc(cosIconFor(cat))}" class="q-cos-add-icon"></i>
            <span class="q-cos-accord-label">${esc(cat)}</span>
            <i data-lucide="chevron-${isOpen ? "up" : "down"}" class="q-cos-accord-caret"></i>
          </button>
          ${isOpen ? `
            <div class="q-cos-accord-body">
              <div class="q-cos-field">
                <label class="q-cos-flabel">${esc(f.roleLabel)} *</label>
                ${flatChips(cat, f.role, f.suggestions)}
                ${f.moreGroups ? `
                  <button type="button"
                          class="q-cos-more"
                          data-action="wizard#toggleCircleMore"
                          data-cos-cat="${esc(cat)}">
                    ${_wizCosUi.more === cat ? "− Hide extra roles" : "+ Show more roles"}
                  </button>
                  ${_wizCosUi.more === cat ? groupedChips(cat, f.role, f.moreGroups) : ""}
                ` : ""}
                <input type="text"
                       class="q-cos-input"
                       data-wiz-cos-role
                       data-cos-cat="${esc(cat)}"
                       placeholder="${esc(f.rolePh)}"
                       value="${esc((_wizCosUi.drafts[cat] && _wizCosUi.drafts[cat][f.role]) || "")}">
              </div>
              <div class="q-cos-field">
                <label class="q-cos-flabel">Name *</label>
                <input type="text"
                       class="q-cos-input"
                       data-wiz-cos-name
                       data-cos-cat="${esc(cat)}"
                       placeholder="${esc(f.namePh)}"
                       value="${esc((_wizCosUi.drafts[cat] && _wizCosUi.drafts[cat].fullname) || "")}">
              </div>
              ${cat === "Family" ? `
                <div class="q-cos-field q-cos-field--row">
                  <label class="q-cos-checkrow">
                    <input type="checkbox"
                           data-wiz-cos-notinhome
                           data-action="change->wizard#toggleCircleNotInHome"
                           data-cos-cat="${esc(cat)}"
                           ${(_wizCosUi.drafts.Family && _wizCosUi.drafts.Family.notInHome) ? "checked" : ""}>
                    Family who lives outside the home
                  </label>
                </div>
              ` : ""}
              ${cat === "Family" ? `
                <div class="q-cos-field q-cos-field--row">
                  <label class="q-cos-checkrow">
                    <input type="checkbox"
                           data-wiz-cos-under18
                           data-action="change->wizard#toggleCircleUnder18"
                           data-cos-cat="${esc(cat)}"
                           ${(_wizCosUi.drafts.Family && _wizCosUi.drafts.Family.under18) ? "checked" : ""}>
                    Under 18?
                  </label>
                  ${(_wizCosUi.drafts.Family && _wizCosUi.drafts.Family.under18) ? `
                    <input type="number"
                           class="q-cos-input q-cos-input--age"
                           data-wiz-cos-age
                           min="0" max="17"
                           placeholder="Age"
                           value="${esc((_wizCosUi.drafts.Family && _wizCosUi.drafts.Family.age) || "")}">
                  ` : ""}
                </div>
              ` : ""}
              <div class="q-cos-actions">
                <button type="button"
                        class="q-cos-skip"
                        data-action="wizard#submitCircleInline"
                        data-cos-cat="${esc(cat)}"
                        data-cos-skip="1"
                        title="Skip — fill missing fields with TBD; tracked in Open Questions">
                  Skip — mark as TBD
                </button>
                <button type="button"
                        class="q-cos-confirm q-cos-confirm--${esc(colorKey)}"
                        data-action="wizard#submitCircleInline"
                        data-cos-cat="${esc(cat)}">
                  + Add ${esc(cat)} Member
                </button>
              </div>
            </div>
          ` : ""}
        </div>`
    }).join("")

    return `
      <div class="q-listsection">
        ${focusCat ? `
          <div class="q-subfocus-pill" title="Showing just the group you clicked in the editor">
            <i data-lucide="filter"></i>
            <button type="button" class="q-subfocus-clear" data-action="wizard#clearSubFocus">Show all</button>
          </div>` : ""}
        <div class="q-cos-addhead">Add by category</div>
        <div class="q-cos-accord">${items}</div>
      </div>`
  },

  // Communication step is special — the chart lives on the editor pane;
  // the wizard pane hosts the cascading option picker. The actual HTML
  // is built by the controller (_renderCommStep) so it can read live
  // active-cell state. This stub returns a placeholder shell the
  // controller swaps out on render.
  communication(plan) {
    return `<div data-wizard-comm-host>${communicationEmptyHtml()}</div>`
  },

  wonderfulThings(plan) {
    const w = plan.wonderfulThings || {}
    // Each sub-list keyed by its plan path so clicking one in the editor
    // scopes the wizard to just that block (editor-native:subblock-focus),
    // mirroring the `about` step. All render when nothing is focused.
    const blocks = {
      "wonderfulThings.activities": dynamicListInput(w.activities, "wonderfulThings.activities", "🎨 Favorite Activities", "Type your own — Painting, swimming, music…",
        { help: "Things that bring joy. Tap a chip to add — type your own anytime.", suggestionGroups: WONDERFUL_OPTIONS.activitiesGroups }),
      "wonderfulThings.places": dynamicListInput(w.places, "wonderfulThings.places", "📍 Favorite Places", "e.g. The park, grandma's house",
        { help: "Where they love to be.", suggestions: WONDERFUL_OPTIONS.places }),
      "wonderfulThings.events": dynamicListInput(w.events, "wonderfulThings.events", "🎉 Special Events", "e.g. Birthday parties, holiday gatherings",
        { help: "Events they look forward to.", suggestions: WONDERFUL_OPTIONS.events }),
      "wonderfulThings.foods": dynamicListInput(w.foods, "wonderfulThings.foods", "🍕 Favorite Foods", "e.g. Pizza, strawberries",
        { help: "Snacks, meals, treats.", suggestions: WONDERFUL_OPTIONS.foods }),
      "wonderfulThings.possessions": dynamicListInput(w.possessions, "wonderfulThings.possessions", "⭐ Strengths", "e.g. Kind, hard-working, great memory",
        { help: "What they're good at — personal qualities, talents, skills.", suggestionGroups: WONDERFUL_OPTIONS.strengthsGroups }),
      "wonderfulThings.admiredQualities": dynamicListInput(w.admiredQualities, "wonderfulThings.admiredQualities", "💎 Admired Qualities", "e.g. Bright smile, deep thinker",
        { help: "What's unique and special — the things people who know them love most.", suggestionGroups: WONDERFUL_OPTIONS.admiredQualitiesGroups })
    }
    const focusPath = _wizSubFocus.section === "wonderfulThings" ? _wizSubFocus.path : null
    if (focusPath && blocks[focusPath]) {
      return `
        <div class="q-space-y-9">
          <div class="q-subfocus-pill" title="Showing just the block you clicked in the editor">
            <i data-lucide="filter"></i>
            <button type="button" class="q-subfocus-clear"
                    data-action="wizard#clearSubFocus">Show all</button>
          </div>
          ${blocks[focusPath]}
        </div>`
    }
    return `
      <div class="q-space-y-9">
        ${Object.values(blocks).join("")}
      </div>`
  },

  notSoFavorites(plan) {
    const n = plan.notSoFavorites || {}
    // Each sub-list keyed by its plan path so clicking one in the editor
    // scopes the wizard to just that block (editor-native:subblock-focus),
    // mirroring wonderfulThings / the `about` step. All render when nothing
    // is focused.
    const blocks = {
      "notSoFavorites.triggers": dynamicListInput(n.triggers, "notSoFavorites.triggers", "🚫 Things That Upset [FirstName]", "e.g. Loud sudden noises",
        { help: "Knowing what upsets helps the team prevent it.", suggestions: NSF_OPTIONS.triggers }),
      "notSoFavorites.sensory": dynamicListInput(n.sensory, "notSoFavorites.sensory", "🔆 Sensory Sensitivities", "e.g. Bright fluorescent lights",
        { help: "Lights, sounds, textures, smells.", suggestions: NSF_OPTIONS.sensory }),
      "notSoFavorites.situations": dynamicListInput(n.situations, "notSoFavorites.situations", "😶 Situations to Skip", "e.g. Crowded places",
        { help: "Things they'd rather not do.", suggestions: NSF_OPTIONS.situations }),
      "notSoFavorites.foods": dynamicListInput(n.foods, "notSoFavorites.foods", "🍴 Foods I Dislike", "e.g. Spicy foods",
        { suggestions: NSF_OPTIONS.foods }),
      "notSoFavorites.environments": dynamicListInput(n.environments, "notSoFavorites.environments", "🏚 Places That Don't Work", "e.g. Noisy restaurants",
        { suggestions: NSF_OPTIONS.environments })
    }
    const focusPath = _wizSubFocus.section === "notSoFavorites" ? _wizSubFocus.path : null
    if (focusPath && blocks[focusPath]) {
      return `
        <div class="q-space-y-9">
          <div class="q-subfocus-pill" title="Showing just the block you clicked in the editor">
            <i data-lucide="filter"></i>
            <button type="button" class="q-subfocus-clear"
                    data-action="wizard#clearSubFocus">Show all</button>
          </div>
          ${blocks[focusPath]}
        </div>`
    }
    return `
      <div class="q-space-y-9">
        ${Object.values(blocks).join("")}
      </div>`
  },

  dayInLife(plan) {
    if (!plan.dayInLife) plan.dayInLife = {}
    Object.keys(DAY_LABELS).forEach(k => { if (!Array.isArray(plan.dayInLife[k])) plan.dayInLife[k] = [] })
    if (!plan.meta) plan.meta = {}
    if (!plan.meta.weekendSameAsWeekday) plan.meta.weekendSameAsWeekday = {}
    const d = plan.dayInLife
    // Match the builder: merge weekendAfternoon into weekendDay (Home & Community).
    if (Array.isArray(d.weekendAfternoon) && d.weekendAfternoon.length) {
      d.weekendDay = (d.weekendDay || []).concat(d.weekendAfternoon)
      d.weekendAfternoon = []
    }
    // Weekend buckets that can link back to their weekday twin — mirrors
    // editor_source/wizard.html#1870 WEEKEND_LINK_MAP. weekendDay isn't
    // listed because it's a merged "Home & Community" bucket with no
    // single weekday equivalent.
    const linkMap = {
      weekendMorning:  "morning",
      weekendNight:    "night",
      weekendAfterBed: "afterBed"
    }
    const linkBtn = (key) => {
      const src = linkMap[key]
      if (!src) return ""
      const isLinked = !!plan.meta.weekendSameAsWeekday[key]
      return `
        <button type="button"
                class="q-day-link-btn${isLinked ? " is-linked" : ""}"
                data-action="wizard#toggleWeekendLink"
                data-weekend-key="${esc(key)}"
                title="${isLinked ? "Click to unlink — edit independently" : "Mirror the weekday equivalent"}">
          <span class="q-day-link-icon">${isLinked ? "🔗" : "🔓"}</span> Same as weekday
        </button>`
    }
    // When the participant doesn't attend school (DIL flag OR a "Does not go
    // to school" entry in My School Details), collapse the weekday to the
    // weekend shape: hide School Time and relabel After School → Home &
    // Community (matches the editor pane + preview/PDF).
    const notSchool = isNotAttendingSchool(plan)
    const afternoonMeta = notSchool ? { title: "Home & Community", icon: "🏡" } : null
    const bucket = (key, metaOverride) => {
      const meta = metaOverride || DAY_LABELS[key]
      const src = linkMap[key]
      const isLinked = src && plan.meta.weekendSameAsWeekday[key]
      // School Time is special: a "Same every weekday" / "Different per
      // day" toggle, then either one Start+End pair or five Mon–Fri
      // rows. Saves with multi-day suffixes ("on Mon, Wed, Fri") so
      // the editor pane shows one chip per distinct time + day-set.
      if (key === "day") {
        const sameMode = plan.meta.schoolSameAllWeek !== false   // default ON
        const entries  = parseSchoolSchedule(d.day || [])
        // Pick a time per (day, kind). Falls back to a sensible default.
        const timeFor = (day, kind, fallback) => {
          const match = entries.find(e => e.kind === kind && e.days.includes(day))
          return match?.time24 || fallback
        }
        const modeBar = `
          <div class="q-day-school-mode" role="tablist">
            <button type="button"
                    class="q-day-school-mode-btn${sameMode ? " is-on" : ""}"
                    data-action="wizard#setSchoolMode"
                    data-school-mode="same"
                    role="tab" aria-selected="${sameMode}">
              Same every weekday
            </button>
            <button type="button"
                    class="q-day-school-mode-btn${sameMode ? "" : " is-on"}"
                    data-action="wizard#setSchoolMode"
                    data-school-mode="different"
                    role="tab" aria-selected="${!sameMode}">
              Different per day
            </button>
          </div>`
        let body
        if (sameMode) {
          const tIn  = entries.find(e => e.kind === "start")?.time24 || "08:30"
          const tOut = entries.find(e => e.kind === "end")?.time24   || "14:30"
          body = `
            <div class="q-day-school-grid">
              <div class="q-day-school-field">
                <label class="q-day-school-label">⏱ School Start</label>
                <input type="time" class="q-day-school-input"
                       data-action="change->wizard#updateSchoolTime"
                       data-school-time="in"
                       value="${esc(tIn)}">
              </div>
              <div class="q-day-school-field">
                <label class="q-day-school-label">⏱ School End</label>
                <input type="time" class="q-day-school-input"
                       data-action="change->wizard#updateSchoolTime"
                       data-school-time="out"
                       value="${esc(tOut)}">
              </div>
            </div>`
        } else {
          body = `
            <div class="q-day-school-week">
              <div class="q-day-school-weekhead">
                <span></span>
                <span>School Start</span>
                <span>School End</span>
              </div>
              ${WEEKDAYS.map(day => `
                <div class="q-day-school-dayrow">
                  <span class="q-day-school-dayname">${day}</span>
                  <input type="time" class="q-day-school-input"
                         data-action="change->wizard#updateSchoolTime"
                         data-school-day="${day}" data-school-time="in"
                         value="${esc(timeFor(day, "start", "08:30"))}">
                  <input type="time" class="q-day-school-input"
                         data-action="change->wizard#updateSchoolTime"
                         data-school-day="${day}" data-school-time="out"
                         value="${esc(timeFor(day, "end",   "14:30"))}">
                </div>`).join("")}
            </div>`
        }
        return `
          <div class="q-day-bucket q-day-school" data-school-mode="${sameMode ? "same" : "different"}">
            <div class="q-day-bucket-head">
              <h4 class="q-day-bucket-title">${meta.icon} ${esc(meta.title)}</h4>
            </div>
            ${modeBar}
            ${body}
          </div>`
      }
      // Linked weekend bucket — render a placeholder that points back to
      // the weekday bucket instead of duplicating the input UI. Editing
      // the weekday bucket flows through to both views.
      if (isLinked) {
        const srcLabel = DAY_LABELS[src]?.title || src
        return `
          <div class="q-listsection q-day-linked">
            <div class="q-day-linked-head">
              <label class="q-label block">${meta.icon} ${esc(meta.title)}</label>
              ${linkBtn(key)}
            </div>
            <div class="q-day-linked-msg">
              ↗ Uses weekday <strong>${esc(srcLabel)}</strong> — edit there to update both.
            </div>
          </div>`
      }
      // Unlinked bucket — wizard pane is selection-only, so we show
      // the bucket header + category buttons that open the day-builder
      // modal. Existing entries already render in the editor pane on
      // the left; duplicating them here would just add noise.
      const linkHtml = linkBtn(key)
      const cats     = DAY_CATEGORIES[key] || []
      const catsHtml = cats.length ? `
        <div class="q-day-cats">
          ${cats.map(c => `
            <button type="button"
                    class="q-day-cat"
                    data-action="wizard#openDayBuilder"
                    data-day-field="${esc(key)}"
                    data-day-cat="${esc(c.id)}">
              <span class="q-day-cat-ico">${c.icon}</span>${esc(c.name)}
            </button>`).join("")}
        </div>` : ""
      return `
        <div class="q-day-bucket">
          <div class="q-day-bucket-head">
            <h4 class="q-day-bucket-title">${meta.icon} ${esc(meta.title)}</h4>
            ${linkHtml}
          </div>
          ${catsHtml}
        </div>`
    }
    // Sub-block focus: clicking a specific time bucket in the editor
    // (e.g. "Morning") scopes the wizard to just that bucket.
    const focusPath = _wizSubFocus.section === "dayInLife" ? _wizSubFocus.path : null
    const focusKey  = focusPath ? focusPath.split(".")[1] : null
    if (focusKey && DAY_LABELS[focusKey]) {
      if (focusKey === "day" && notSchool) return `<div class="q-space-y-9">${SUBFOCUS_PILL}</div>`
      const ov = focusKey === "afternoon" ? afternoonMeta : null
      return `<div class="q-space-y-9">${SUBFOCUS_PILL}${bucket(focusKey, ov)}</div>`
    }
    return `
      <p class="q-sub">Typical weekday + weekend routine.</p>
      <h3 class="q-h3">🗓 Weekdays</h3>
      <div class="q-space-y-9">
        ${bucket("morning")}
        ${notSchool ? "" : bucket("day")}
        ${bucket("afternoon", afternoonMeta)}
        ${bucket("night")}
        ${bucket("afterBed")}
      </div>
      <h3 class="q-h3 q-h3-weekend">🎉 Weekends</h3>
      <div class="q-space-y-9">
        ${bucket("weekendMorning")}
        ${bucket("weekendDay")}
        ${bucket("weekendNight")}
        ${bucket("weekendAfterBed")}
      </div>`
  },

  important(plan) {
    const i = plan.important || {}
    const blocks = {
      "important.to":  dynamicListInput(i.to,  "important.to",  "💚 Important TO [FirstName]",  "Add what matters most…",
        { help: "Things that bring happiness and fulfillment.", suggestions: IMPORTANT_OPTIONS.to }),
      "important.for": dynamicListInput(i.for, "important.for", "💙 Important FOR [FirstName]", "Add a need…",
        { help: "Things needed for health, safety, and wellbeing.", suggestions: IMPORTANT_OPTIONS.for })
    }
    return subFocusView("important", blocks)
  },

  bigDreams(plan) {
    const b = plan.bigDreams || {}
    const blocks = {
      "bigDreams.hopes":  dynamicListInput(b.hopes,  "bigDreams.hopes",  "🌱 Hopes (next 6–12 months)", "Add a hope…",
        { help: "Short-term — things to accomplish or experience this year.", suggestions: DREAMS_OPTIONS.hopes }),
      "bigDreams.dreams": dynamicListInput(b.dreams, "bigDreams.dreams", "🌟 Big Dreams (long-term)",   "Add a dream…",
        { help: "Long-term aspirations and life goals.", suggestions: DREAMS_OPTIONS.dreams })
    }
    return subFocusView("bigDreams", blocks)
  },

  supportMatch(plan) {
    const s = plan.supportMatch || {}
    const blocks = {
      "supportMatch.qualities":    dynamicListInput(s.qualities,    "supportMatch.qualities",    "🌟 Qualities & Characteristics", "Add a quality…",
        { help: "What kind of person works well with me.", suggestions: SUPPORT_OPTIONS.qualities }),
      "supportMatch.skills":       dynamicListInput(s.skills,       "supportMatch.skills",       "🛠 Skills Needed",                "Add a skill…",
        { help: "Specific skills the support person should have.", suggestions: SUPPORT_OPTIONS.skills }),
      "supportMatch.personality":  dynamicListInput(s.personality,  "supportMatch.personality",  "🎭 Personality Match",            "Add a personality trait…",
        { help: "Personality style that fits well.", suggestions: SUPPORT_OPTIONS.personality }),
      "supportMatch.dealBreakers": dynamicListInput(s.dealBreakers, "supportMatch.dealBreakers", "🚫 Deal Breakers",                "What doesn't work",
        { help: "What doesn't work — flag early so the team can avoid it.", suggestions: SUPPORT_OPTIONS.dealBreakers })
    }
    return subFocusView("supportMatch", blocks)
  },

  working(plan) {
    const w = plan.working || {}
    // Both halves of "What Works & Doesn't Work" — the editor section on
    // the left shows works + doesntWork, so the wizard offers a selection
    // for each (previously only "What's Working" existed here).
    const blocks = {
      "working.works":      dynamicListInput(w.works,      "working.works",      "✅ What's Working",     "Add a service that's helping…",
        { help: "Services or supports that are helping.", suggestions: SERVICE_OPTIONS }),
      "working.doesntWork": dynamicListInput(w.doesntWork, "working.doesntWork", "❌ What Isn't Working",  "Add a service that's not working…",
        { help: "Services or supports that aren't meeting needs.", suggestions: SERVICE_OPTIONS })
    }
    return subFocusView("working", blocks)
  },

  barriers(plan) {
    return `
      <div class="q-space-y-9">
        ${dynamicListInput(plan.barriers, "barriers", "🚧 Current Barriers", "Add a barrier…",
          { help: "Things that make it harder to reach goals right now.", textKey: "description", suggestions: BARRIER_OPTIONS })}
      </div>`
  },

  goals(plan) {
    if (!Array.isArray(plan.goals)) plan.goals = []
    // Wizard pane is SELECTION-ONLY for goals. The editor pane on the
    // left already renders every goal as an editable card — duplicating
    // it here would just create noise (and a stale parallel copy on
    // every keystroke). The wizard shows ONLY the one-click suggestion
    // deck built from data the user entered in earlier sections.
    const goals = plan.goals

    // After a goal is just-added via the suggestion deck, switch the
    // pane into a focused "schedule picker" view for that goal (hide
    // the deck, show day-type + times + hours + minutes inputs that
    // map to the goal's first resource schedule). Cancel / Save sends
    // us back to the deck. State lives at module scope (_wizGoalUi)
    // because renderer methods run with `this` bound to STEP_RENDERERS,
    // not the controller.
    const pendingId = _wizGoalUi.pendingSchedGoalId
    const pendingGoal = pendingId ? goals.find(g => g.id === pendingId) : null
    if (pendingGoal) {
      return goalScheduleStepHtml(pendingGoal, _wizGoalUi.draft, _wizGoalUi.step || "tag")
    }

    const suggestions = applyGoalOverrides(buildGoalSuggestions(plan))

    // A suggestion is "already added" when a goal row in plan.goals has
    // the same heading+description signature. Click acts as a toggle: an
    // unselected card adds, a selected one removes the matching goal so
    // the user doesn't accumulate duplicates by re-clicking the same
    // suggestion.
    const suggestionKey = (g) => `${g.heading || ""}|${g.description || ""}`
    const addedKeys = new Set(goals.map(suggestionKey))
    const isUserGoal = (s) => !!(s._fromLibrary || s._customNew)
    const defaultSuggestions = suggestions.filter(s => !isUserGoal(s))
    const userSuggestions    = suggestions.filter(isUserGoal)
    // Custom goals lead when the user has any; otherwise standard goals show.
    const initialScope = userSuggestions.length ? "user" : "default"
    const scopeSuggestions = initialScope === "user" ? userSuggestions : defaultSuggestions
    const suggestionCard = (s, ix) => {
      const on = addedKeys.has(suggestionKey(s))
      const scope = isUserGoal(s) ? "user" : "default"
      return `
      <button type="button"
              class="q-goalsuggest${on ? " is-on" : ""}"
              data-action="wizard#addGoalFromSuggestion"
              data-goal-index="${ix}"
              data-group="${esc(s.group || "other")}"
              data-scope="${scope}"
              ${scope !== initialScope ? "hidden" : ""}
              title="${on ? "Already added — click to remove" : "Click to add this goal"}">
        <div class="q-goalsuggest-head">
          <span class="q-goalsuggest-icon">${esc(s.icon || "🎯")}</span>
          <span class="q-goalsuggest-domain">${esc(s.domain || "Goal")}</span>
          <span class="q-goalsuggest-lib" role="button" tabindex="0"
                data-action="wizard#addGoalToLibrary" data-goal-index="${ix}"
                title="Add this goal to your library" aria-label="Add to library">★</span>
          <span class="q-goalsuggest-edit" role="button" tabindex="0"
                data-action="wizard#editGoalSuggestion" data-goal-index="${ix}"
                title="Edit this suggestion (saved to your library)"
                aria-label="Edit this suggestion">✏️</span>
          <span class="q-goalsuggest-add" aria-label="${on ? "Added — click to remove" : "Add this goal"}">${on ? "✓" : "+"}</span>
        </div>
        ${s.heading ? `<div class="q-goalsuggest-heading">${esc(s.heading)}</div>` : ""}
        <div class="q-goalsuggest-desc">${esc(s.description)}</div>
        <div class="q-goalsuggest-meta">
          ${s.timeline ? `<span class="q-goalsuggest-meta-chip" title="Timeline">📅 ${esc(s.timeline)}</span>` : ""}
          ${s._serial ? `<span class="q-goalsuggest-serial" title="Library serial">${esc(s._serial)}</span>` : ""}
        </div>
        <div class="q-goalsuggest-foot">
          <span class="q-goalsuggest-source">From ${esc(s.source)}${s._edited ? " · ✏️ edited" : ""}</span>
        </div>
      </button>`
    }

    // Group tabs — "All" first, then only the GOAL_GROUPS keys that
    // actually have at least one card in the current suggestion set.
    // (No empty tabs.) Each tab fires wizard#filterGoalSuggestions
    // which toggles cards in place via [hidden].
    // Tabs are shared across both scopes; counts + empty-hiding reflect the
    // ACTIVE scope. Seed from the initially-active scope's cards and hide
    // group tabs empty in it. _applyGoalDeckFilter() keeps them in sync when
    // the scope switches.
    const groupsPresent = new Set(suggestions.map(s => s.group).filter(Boolean))
    const tabBtn = (key, label) => {
      const count = key === "all"
        ? scopeSuggestions.length
        : scopeSuggestions.filter(s => s.group === key).length
      const hideTab = key !== "all" && count === 0
      return `<button type="button"
                      class="q-goalsuggest-tab${key === "all" ? " is-active" : ""}"
                      data-action="wizard#filterGoalSuggestions"
                      data-group="${esc(key)}"${hideTab ? " hidden" : ""}>
                ${esc(label)} <span class="q-goalsuggest-tab-count">${count}</span>
              </button>`
    }
    const tabsHtml = `
      <div class="q-goalsuggest-tabs" role="tablist">
        ${tabBtn("all", "All")}
        ${Object.entries(GOAL_GROUPS)
          .filter(([key]) => groupsPresent.has(key))
          .map(([key, label]) => tabBtn(key, label))
          .join("")}
      </div>`

    // Standard vs custom switch — sits above the category tabs and filters the
    // deck by source. Custom leads when the user has any of their own goals.
    const scopeToggleHtml = `
      <div class="q-goalsuggest-scope" role="tablist" aria-label="Goal source">
        <button type="button" class="q-goalsuggest-scope-btn${initialScope === "default" ? " is-active" : ""}"
                data-action="wizard#filterGoalScope" data-scope="default"
                title="Built-in suggestions from your data">
          Standard goals <span class="q-goalsuggest-tab-count">${defaultSuggestions.length}</span>
        </button>
        <button type="button" class="q-goalsuggest-scope-btn${initialScope === "user" ? " is-active" : ""}"
                data-action="wizard#filterGoalScope" data-scope="user"
                title="Your custom & saved goals">
          Custom goals <span class="q-goalsuggest-tab-count">${userSuggestions.length}</span>
        </button>
      </div>`

    const suggestionsHtml = `
      ${suggestions.length ? scopeToggleHtml : ""}
      ${suggestions.length ? tabsHtml : ""}
      <div class="q-goalsuggest-deck"
           data-wizard-goal-deck
           data-active-group="all"
           data-active-scope="${initialScope}"
           data-wizard-goal-suggestions='${esc(JSON.stringify(suggestions))}'>
        ${suggestions.map(suggestionCard).join("")}
        ${newGoalCardHtml()}
      </div>
      ${suggestions.length ? "" : `<div class="q-list-empty" style="margin-top:10px">No auto-suggestions from your data yet — use “Add your own goal”, or add activities, day-in-life details, or barriers in earlier steps.</div>`}`

    return `
      <div class="q-space-y-9">
        <div class="q-listsection">
          <div class="q-goal-walkthrough-entry">
            <button type="button" class="q-goal-walkthrough-btn"
                    data-action="wizard#openGoalWalkthrough">
              <i data-lucide="compass"></i>
              <span>Start goal walkthrough</span>
            </button>
            <span class="q-goal-walkthrough-hint">Review services & needs, then define SMART goals</span>
          </div>
          <label class="q-label block">✨ Suggested goals from your data</label>
          <div class="q-help">
            Built from the strengths, day-in-life entries, barriers, and
            services you added in earlier steps. Click to add — refine
            wording, domain, and timeline after.
          </div>
          ${suggestionsHtml}
        </div>
      </div>`
  },

  currentServices(plan) {
    return `
      <div class="q-space-y-9">
        ${dynamicListInput(plan.currentServices, "currentServices", "🛠 Current Services", "Add a current service…",
          { help: "Services currently being received.", suggestions: SERVICE_OPTIONS })}
      </div>`
  },

  // ---- Smart Calendar ----
  // Mirrors editor_source/wizard.html's calendar section. The actual
  // calendar UI lives in the main editor; the wizard view exposes the
  // notes plus a deep-link so the facilitator can hop over to the
  // editor's calendar grid.
  // Service Calendar legend — the editor pane's resourceCalendar
  // grid no longer carries its own palette; instead the user picks a
  // service here and then drags on the grid to assign it. Chips show
  // the per-service hours pill live (h/wk or h/mo), and the
  // "Clear / Eraser" pseudo-chip lets the user drag-to-clear a range.
  // The active selection is mirrored to the editor controller via
  // the `rc:service-select` event the wizard's selectRcService action
  // dispatches.
  resourceCalendar(plan) {
    if (!plan.resourceCalendar) plan.resourceCalendar = { enabled: true, slots: [], notes: "" }
    const slots = plan.resourceCalendar.slots || []
    const services = rcServicesFor(plan)
    const monthly  = _wizRcUi.monthly
    const active   = _wizRcUi.active
    const editing  = _wizRcUi.editing

    // Per-service weekly hour totals from the slot array.
    const hrs = {}
    slots.forEach(s => {
      const h = (s.endHour || 0) - (s.startHour || 0)
      hrs[s.resourceRef] = (hrs[s.resourceRef] || 0) + h
    })

    const groupHtml = (label, items) => {
      if (!items.length) return ""
      const buttons = items.map(s => {
        const wk     = hrs[s.text] || 0        // scheduled (painted on the grid)
        const req    = s.reqHrs || 0           // requested (from the resource schedule)
        const factor = monthly ? (31 / 7) : 1
        const round1 = (n) => Math.round(n * factor * 10) / 10
        const schedD = round1(wk)
        const reqD   = round1(req)
        const unit   = monthly ? "h/mo" : "h/wk"
        const abbr  = rcAbbrev(s.text, plan)
        const color = rcColorFor(s.text, services)
        const isActive = active === s.text
        const isEditing = editing === s.text
        // The school built-in gets an extra "Auto" button that fills the
        // grid from the Day-in-the-Life school Start/End times.
        const isSchool = s.source === "parental" && s.text === rcSchoolName(plan)
        const warn  = s.source === "unmet"
          ? `<i data-lucide="alert-triangle" class="q-rc-svc-warn"></i>`
          : ""
        const nameWrap = `
            <span class="q-rc-svc-namewrap">
              <span class="q-rc-svc-name">${esc(s.text)}</span>
              ${s.refNum ? `<span class="q-rc-svc-goal"><i data-lucide="target"></i>${esc(s.refKind)} ${s.refNum}${s.refHeading ? ` · ${esc(s.refHeading)}` : ""}</span>` : ""}
            </span>`
        const hrsPill = (wk > 0 || req > 0)
          ? `<span class="q-rc-svc-hrs" title="Scheduled / Requested">${schedD}${req > 0 ? `/${reqD}` : ""}${unit}</span>`
          : ""
        // Editing this service's abbreviation → the chip becomes a
        // non-button row holding the inline input (so the <input> isn't an
        // interactive descendant of a <button>).
        if (isEditing) {
          return `
            <div class="q-rc-svc-row is-editing">
              <div class="q-rc-svc q-rc-svc--editing" style="--rc-color:${color};">
                <span class="q-rc-svc-dot" style="background:${color};"></span>
                <input type="text" class="q-rc-svc-abbrinput" maxlength="6"
                       value="${esc(abbr)}"
                       data-rc-svc="${esc(s.text)}"
                       data-action="keydown->wizard#abbrevKeydown blur->wizard#commitRcAbbrev"
                       aria-label="Abbreviation for ${esc(s.text)}">
                ${nameWrap}
                ${hrsPill}
              </div>
            </div>`
        }
        // Normal chip + a SEPARATE pencil button as a sibling (not nested
        // inside the select <button>, which made the click unreliable).
        return `
          <div class="q-rc-svc-row">
            <button type="button"
                    class="q-rc-svc${s.source === "unmet" ? " is-unmet" : ""}${isActive ? " is-active" : ""}"
                    data-action="wizard#selectRcService"
                    data-rc-svc="${esc(s.text)}"
                    style="--rc-color:${color};">
              <span class="q-rc-svc-dot" style="background:${color};"></span>
              ${warn}
              <strong class="q-rc-svc-abbr">${esc(abbr)}</strong>
              ${nameWrap}
              ${hrsPill}
            </button>
            ${isSchool ? `
            <button type="button"
                    class="q-rc-svc-autoadd"
                    data-action="wizard#autoAddSchoolCalendar"
                    title="Auto-fill the calendar with school hours from A Day in the Life"
                    aria-label="Auto-add school hours from A Day in the Life">
              <i data-lucide="wand-sparkles"></i><span>Auto</span>
            </button>` : ""}
            <button type="button"
                    class="q-rc-svc-abbredit"
                    data-action="wizard#editRcAbbrev"
                    data-rc-svc="${esc(s.text)}"
                    style="--rc-color:${color};"
                    title="Edit “${esc(abbr)}” abbreviation"
                    aria-label="Edit abbreviation for ${esc(s.text)}">
              <i data-lucide="pencil"></i>
            </button>
          </div>`
      }).join("")
      return `
        <div class="q-rc-group">
          <div class="q-rc-grouplabel">${esc(label)}</div>
          ${buttons}
        </div>`
    }

    const current  = services.filter(s => s.source === "current")
    const unmet    = services.filter(s => s.source === "unmet")
    const parental = services.filter(s => s.source === "parental")

    return `
      <div class="q-rc-pane">
        <div class="q-rc-pane-head">
          <span class="q-rc-pane-title">Service legend</span>
          <button type="button"
                  class="q-rc-monthly${monthly ? " is-monthly" : ""}"
                  data-action="wizard#toggleRcMonthly"
                  title="Switch the hours pill between weekly and monthly totals">
            ${monthly ? "Monthly" : "Weekly"} <i data-lucide="repeat"></i>
          </button>
        </div>
        <div class="q-rc-pane-hint">
          Click a service below, then click or drag on the grid in the editor
          to assign it to those half-hour cells.
        </div>
        ${groupHtml("Current services", current)}
        ${groupHtml("Unmet needs",      unmet)}
        ${groupHtml("Built-in",         parental)}
        <div class="q-rc-group q-rc-group--clear">
          <button type="button"
                  class="q-rc-svc q-rc-svc--clear${active === "__rc_clear__" ? " is-active" : ""}"
                  data-action="wizard#selectRcService"
                  data-rc-svc="__rc_clear__">
            <span class="q-rc-svc-dot q-rc-svc-dot--clear"></span>
            <strong class="q-rc-svc-abbr">×</strong>
            <span class="q-rc-svc-name">Clear / Eraser</span>
            <span class="q-rc-svc-hrs">drag</span>
          </button>
        </div>
        ${current.length + unmet.length === 0 ? `
          <div class="q-rc-empty">
            <i data-lucide="info"></i>
            Add services in <strong>Current Services</strong> or
            <strong>Unmet Needs</strong> first — only Built-in shows up
            until then.
          </div>` : ""}
      </div>`
  },

  smartCalendar(plan) {
    if (!plan.smartCalendar) plan.smartCalendar = {}
    if (!Array.isArray(plan.smartCalendar.items)) plan.smartCalendar.items = []
    const sc = plan.smartCalendar
    const TOTAL_HRS = 168
    const allocated = sc.items.reduce((s, it) => s + weeklyHoursForCalItem(it), 0)
    const remaining = TOTAL_HRS - allocated
    const pct       = Math.min(100, Math.max(0, (allocated / TOTAL_HRS) * 100))
    const youth     = isYouthAge(plan)
    const remColor  = remaining < 0  ? "#fda4af"
                    : remaining > 84 ? "#fda4af"
                    : remaining > 56 ? "#fcd34d"
                    :                  "#cbd5e1"
    const burdenMsg = remaining < 0
      ? `Over-allocated by ${formatHrs(-remaining)} hrs — double-check overlapping blocks.`
      : remaining > 84
        ? `Heavy caregiver load — ${formatHrs(remaining / 7)} hrs/day on average falls on family${youth ? " (typical for a young child)" : ". For older youth/adults this is unsustainable without more funded support"}.`
        : remaining > 56
          ? `Moderate caregiver load — ${formatHrs(remaining / 7)} hrs/day on average. ${youth ? "Normal for a young child." : "Watch for caregiver burnout."}`
          : `Most hours are covered by school, sleep, or funded services. ${formatHrs(remaining / 7)} hrs/day fall on family.`

    const itemRow = (it, idx) => {
      const cat = CALENDAR_CATEGORIES.find(c => c.id === it.categoryId) || { icon: "⏱", label: "Item" }
      const wk = weeklyHoursForCalItem(it)
      const detail = (it.kind === "window")
        ? `${it.timeIn24 || it.start || ""} – ${it.timeOut24 || it.end || ""} · ${it.daysPerWeek || 0} days/week · ${formatHrs(wk)} hrs/wk`
        : `${formatHrs(it.hours || 0)} hrs ${it.frequency || "weekly"} · ${formatHrs(wk)} hrs/wk`
      // Surface protective-supervision so the deduction is visible at a
      // glance — matches the legacy badge at editor_source/wizard.html#1822.
      const psBadge = (it.categoryId === "sleep" && it.protectiveSupervision === true)
        ? `<span class="q-cal-ps-badge" title="Protective supervision hours are deducted from Sleep">⚠ PS · ${formatHrs(Number(it.psHours) || 0)} hrs/wk</span>`
        : ""
      const notesLine = it.notes
        ? `<div class="q-cal-notes"><strong>Notes:</strong> ${esc(it.notes)}</div>`
        : ""
      return `
        <div class="q-cal-item">
          <div class="q-cal-icon">${cat.icon}</div>
          <div class="q-cal-body">
            <div class="q-cal-head">
              <span class="q-cal-label">${esc(it.label || cat.label)}</span>
              <span class="q-cal-tag">${esc(cat.label)}</span>
              ${psBadge}
            </div>
            <div class="q-cal-detail">${esc(detail)}</div>
            ${notesLine}
          </div>
          <div class="q-cal-actions">
            <button type="button" class="q-cal-edit"
                    data-action="wizard#editCalItem"
                    data-cal-idx="${idx}">Edit</button>
            <button type="button" class="q-cal-remove"
                    data-action="wizard#removeCalItem"
                    data-cal-idx="${idx}">Remove</button>
          </div>
        </div>`
    }

    return `
      <div class="q-listsection">
        <label class="q-label block">🧮 Smart Calendar — Weekly Hours Budget</label>
        <div class="q-help">
          Add scheduled blocks (school, sleep) and funded service hours (Respite / IHSS / PA / Other).
          Whatever isn't covered by these falls on <strong>family / caregivers</strong> directly.
          Monthly/quarterly/annual hours are normalized to weekly using <strong>4.345 weeks/month</strong>.
        </div>

        <div class="q-cal-budget">
          <div class="q-cal-stats">
            <div>
              <div class="q-cal-stat-label">Total / week</div>
              <div class="q-cal-stat-value">${TOTAL_HRS} <span class="q-cal-stat-unit">hrs</span></div>
            </div>
            <div>
              <div class="q-cal-stat-label">Covered (school + sleep + services)</div>
              <div class="q-cal-stat-value q-cal-stat-value--indigo">${formatHrs(allocated)} <span class="q-cal-stat-unit">hrs</span></div>
            </div>
            <div>
              <div class="q-cal-stat-label">Family / caregiver hours</div>
              <div class="q-cal-stat-value" style="color:${remColor};">${formatHrs(remaining)} <span class="q-cal-stat-unit">hrs</span></div>
            </div>
          </div>
          <div class="q-cal-bar"><div class="q-cal-bar-fill" style="width:${pct}%; background:${remaining < 0 ? "#fda4af" : "#818cf8"};"></div></div>
          <div class="q-cal-burden">
            <strong style="color:${remColor};">${esc(burdenMsg)}</strong>
            <div class="q-cal-burden-sub">
              Family / caregiver hours are time when no scheduled program, school, sleep window, or funded service
              covers care — so a parent (or another family member) is providing it directly. For very young children
              this is expected. For older youth and adults with high support needs, large numbers here often signal
              the family needs more funded support to be sustainable.
            </div>
          </div>
        </div>

        <div class="q-cal-items">
          ${sc.items.length
            ? sc.items.map(itemRow).join("")
            : `<div class="q-cal-empty">No blocks yet — pick a category below to add one.</div>`}
        </div>

        <div class="q-cal-addhead">Add a block</div>
        <div class="q-cal-addrow">
          ${CALENDAR_CATEGORIES.map(c => `
            <button type="button" class="q-cal-add"
                    data-action="wizard#addCalItem"
                    data-cal-cat="${esc(c.id)}">
              <span>${c.icon}</span> ${esc(c.label)}
            </button>`).join("")}
        </div>
      </div>`
  },

  // ---- Not Working ----
  // Production has a dedicated notWorking bucket inside plan.working,
  // but it's commonly split out into its own section in the wizard.
  // Keep the same shape so editor + wizard share the data.
  notWorking(plan) {
    const w = plan.working || {}
    return `
      <div class="q-space-y-9">
        ${dynamicListInput(w.doesntWork, "working.doesntWork", "❌ What Isn't Working", "Add a service that's not working…",
          { help: "Services or supports that aren't meeting needs.", suggestions: SERVICE_OPTIONS })}
      </div>`
  },

  // Open Questions moved to the editor's own on-demand section
  // (open_questions_controller + _open_questions.html.erb). The wizard
  // treats it as a no-guided-questions section (see NO_WIZARD_SECTIONS),
  // so the right pane shows the "edit on the left" placeholder.
}

// Walk the plan for any string value containing a TBD marker. Returns a
// flat list of { path, sectionId, question, value, context } entries
// ready for the Open Questions renderer. Mirrors production's
// findOpenQuestions at editor_source/wizard.html#~3700.
function findOpenQuestions(plan, base = [], sectionId = null) {
  const out = []
  const sectionForKey = (k) => {
    // Top-level keys map to their wizard section id where applicable.
    if (k === "client" || k === "facilitator") return "client"
    if (k === "about") return "about"
    if (k === "circleOfSupport") return "circleOfSupport"
    if (k === "communicationChart" || k === "communicationStyle") return "communication"
    if (k === "wonderfulThings") return "wonderfulThings"
    if (k === "notSoFavorites")  return "notSoFavorites"
    if (k === "dayInLife")       return "dayInLife"
    if (k === "resourceCalendar") return "resourceCalendar"
    if (k === "important")       return "important"
    if (k === "bigDreams")       return "bigDreams"
    if (k === "supportMatch")    return "supportMatch"
    if (k === "working")         return "working"
    if (k === "barriers")        return "barriers"
    if (k === "goals")           return "goals"
    if (k === "currentServices") return "currentServices"
    return null
  }
  for (const [k, v] of Object.entries(plan || {})) {
    if (k === "id" || k === "meta") continue
    const path = [...base, k]
    const sid = sectionId || sectionForKey(k)
    if (typeof v === "string") {
      if (TBD_PATTERNS_TEST.test(v)) {
        out.push({
          path,
          sectionId: sid,
          question: tbdInlineQuestion(v) || humanizeFieldName(path),
          value: v,
          context: contextForPath(plan, path)
        })
      }
    } else if (Array.isArray(v)) {
      v.forEach(it => {
        if (typeof it === "string") {
          if (TBD_PATTERNS_TEST.test(it)) out.push({
            path: [...path, "(item)"], sectionId: sid,
            question: tbdInlineQuestion(it) || humanizeFieldName([...path, "(item)"]),
            value: it
          })
        } else if (it && typeof it === "object") {
          out.push(...findOpenQuestions(it, [...path, String(it.id || "")], sid))
        }
      })
    } else if (v && typeof v === "object") {
      out.push(...findOpenQuestions(v, path, sid))
    }
  }
  // At the root, fold in the non-destructive flags (plan.meta.flags[path] ===
  // true). These mark a field as "to be determined" without writing "TBD"
  // into its value, so surface them here with whatever text the field holds.
  if (base.length === 0 && plan && plan.meta && plan.meta.flags) {
    const seen = new Set(out.map(o => o.path.join(".")))
    for (const [flagPath, on] of Object.entries(plan.meta.flags)) {
      if (!on || seen.has(flagPath)) continue
      const parts = flagPath.split(".")
      let val = plan
      for (const p of parts) val = (val == null ? undefined : val[p])
      out.push({
        path: parts,
        sectionId: sectionForKey(parts[0]),
        question: humanizeFieldName(parts),
        value: typeof val === "string" ? val : "",
        context: contextForPath(plan, parts)
      })
    }
  }
  return out
}

function humanizeFieldName(path) {
  // Trim leading top-level container; transform camelCase to words.
  const last = path[path.length - 1]
  return String(last)
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, c => c.toUpperCase())
    .trim()
}

function contextForPath(plan, path) {
  // For nested objects, surface a sibling string that gives the user a
  // hint about which item the TBD belongs to (mirrors production's
  // getSiblingContext).
  if (path.length < 2) return null
  let parent = plan
  for (let i = 0; i < path.length - 1; i++) parent = parent && parent[path[i]]
  if (!parent || typeof parent !== "object" || Array.isArray(parent)) return null
  const last = path[path.length - 1]
  for (const k of Object.keys(parent)) {
    if (k === last || k === "id") continue
    const v = parent[k]
    if (typeof v === "string" && v && !TBD_PATTERNS_TEST.test(v)) return v
  }
  return null
}

// Pull an explicitly-authored question out of a TBD placeholder value.
// Users write the missing-info prompt INSIDE the field, bracketed by a
// leading "TBD"/"TBD:" marker and a trailing "??", e.g.
//   "TBD: How many IHSS hours per month??" → "How many IHSS hours per month?"
// Returns the sentence (normalized to a single "?") when there's a real
// phrase between the markers, else null so the caller falls back to the
// humanized field name. Bare "TBD", "??", "(role)" parentheticals, and
// single tokens all yield null.
function tbdInlineQuestion(value) {
  let s = String(value == null ? "" : value).trim()
  if (!s) return null
  const hadTbd  = /^tbd\b\s*:?/i.test(s)
  s = s.replace(/^tbd\b\s*:?\s*/i, "").trim()   // drop a leading TBD / TBD:
  const hadDblQ = /\?\?\s*$/.test(s)
  if (!hadTbd && !hadDblQ) return null           // ordinary value — nothing authored
  s = s.replace(/[?？\s]+$/, "").trim()       // strip the trailing ?? markers
  if (!s || /^tbd$/i.test(s)) return null         // nothing left between the markers
  if (/^\([^)]*\)$/.test(s)) return null          // "(Pediatrician)" role-only → not a question
  if (!/\s/.test(s)) return null                  // a single token isn't a question
  return s + "?"
}

// ---------- communication picker helpers ----------

// Empty state for the wizard pane when no chart cell is selected.
function communicationEmptyHtml() {
  return `
    <div class="q-cc-pane q-cc-pane--empty">
      <div class="q-cc-pane-eyebrow">Communication picker</div>
      <div class="q-cc-empty">
        <div class="q-cc-empty-icon"><i data-lucide="mouse-pointer-click"></i></div>
        <div class="q-cc-empty-title">Click any cell on the chart to begin</div>
        <div class="q-cc-empty-hint">
          Pick a situation → behaviors filter to fit it →
          meanings filter to that behavior → supports narrow to the meaning.
        </div>
      </div>
    </div>`
}

// "Row N complete" state shown after the support column is filled.
function communicationDoneHtml(rowIdx) {
  return `
    <div class="q-cc-pane q-cc-pane--empty">
      <div class="q-cc-pane-eyebrow">Communication picker</div>
      <div class="q-cc-empty">
        <div class="q-cc-empty-icon q-cc-empty-icon--ok"><i data-lucide="check-circle-2"></i></div>
        <div class="q-cc-empty-title">Row ${rowIdx + 1} complete</div>
        <div class="q-cc-empty-hint">
          Click another cell to keep building, or tap <strong>+ Add Row</strong>
          on the chart for a fresh entry.
        </div>
      </div>
    </div>`
}

// Cascading picker for a single (column, parent-state) pair. `state`
// is the row's current { happening, behavior, meaning, support } map.
// `bypassFilter` short-circuits the strict-cascade so the user can
// reach the generic group when the override doesn't have what they need.
function communicationPickerHtml(rowId, col, state, bypassFilter) {
  const meta = CC_COL_META[col]
  const hasOverride = ccHasOverrideFor(col, state)
  const parentPick = col === "behavior" ? state.happening
                   : col === "meaning"  ? state.behavior
                   : col === "support"  ? state.meaning
                   : null
  const parentCol  = col === "behavior" ? "happening"
                   : col === "meaning"  ? "behavior"
                   : col === "support"  ? "meaning"
                   : null
  const parentOpt  = parentCol && parentPick ? ccFindOption(parentCol, state, parentPick) : null
  const parentIcon = (parentOpt && parentOpt.icon) || "tag"
  const showFilter = hasOverride && parentPick

  const grouped = bypassFilter && hasOverride ? ccGenericFor(col) : ccOptionsFor(col, state)
  const total = Object.values(grouped).reduce((s, v) => s + v.length, 0)

  // Open every group on filtered (compact) sets; otherwise use the
  // column's defaults.
  const openSet = new Set()
  if (hasOverride && !bypassFilter) {
    Object.keys(grouped).forEach(g => openSet.add(g))
  } else {
    ;(CC_OPEN_BY_COL[col] || []).forEach(g => openSet.add(g))
  }

  const quick = ccQuickPicksFor(col, state)
  // Context-aware suggestion for the row's seeded "happening" — the same
  // grey `e.g.` hint the chart shows, surfaced as a one-click option so
  // the good example is part of the selection.
  const hint = ccHintFor(state.happening, col)

  const breadcrumbs = CC_COLS.map(c => {
    const m = CC_COL_META[c]
    const filled = !!state[c]
    const here   = c === col
    return `
      <span class="q-cc-crumb${here ? " is-on" : ""}${filled ? " is-filled" : ""}">
        <i data-lucide="${esc(m.icon)}"></i>
        ${esc(c.charAt(0).toUpperCase() + c.slice(1))}
        ${filled ? `<span class="q-cc-crumb-val">${esc(state[c])}</span>` : ""}
      </span>`
  }).join(`<span class="q-cc-crumb-sep">›</span>`)

  return `
    <div class="q-cc-pane"
         data-row-id="${esc(rowId)}"
         data-col="${esc(col)}"
         data-bypass="${bypassFilter ? "1" : "0"}">

      <div class="q-cc-head">
        <div class="q-cc-eyebrow">Communication picker</div>
        <div class="q-cc-title">
          <i data-lucide="${esc(meta.icon)}"></i>
          ${esc(meta.title)}
        </div>
        <div class="q-cc-breadcrumbs">${breadcrumbs}</div>
      </div>

      ${showFilter ? `
        <div class="q-cc-filterbar">
          <i data-lucide="filter" class="q-cc-filterbar-icon"></i>
          <span class="q-cc-filterbar-text">
            Filtered for
            <span class="q-cc-parent-pill">
              <i data-lucide="${esc(parentIcon)}"></i>${esc(parentPick)}
            </span>
          </span>
          <button type="button"
                  class="q-cc-showall${bypassFilter ? " is-on" : ""}"
                  data-action="wizard#toggleCommBypass">
            <i data-lucide="${bypassFilter ? "filter" : "list"}"></i>
            ${bypassFilter ? "Re-apply filter" : "Show all options"}
          </button>
        </div>` : ""}

      <div class="q-cc-search-wrap">
        <i data-lucide="search" class="q-cc-search-icon"></i>
        <input type="text"
               class="q-cc-search"
               placeholder="Search options…"
               data-wizard-target="commSearch"
               data-action="input->wizard#filterCommOptions keydown->wizard#commSearchKey">
        <button type="button"
                class="q-cc-search-clear"
                data-action="wizard#clearCommSearch"
                hidden>
          <i data-lucide="x"></i>
        </button>
      </div>

      <div class="q-cc-body" data-wizard-target="commBody">
        ${hint ? `
          <div class="q-cc-quick q-cc-suggested">
            <div class="q-cc-quick-head"><i data-lucide="sparkles"></i> Suggested for &ldquo;${esc(state.happening)}&rdquo;</div>
            <div class="q-cc-quick-grid">
              <button type="button"
                      class="q-cc-option q-cc-option--quick q-cc-option--suggested"
                      data-action="wizard#pickCommOption"
                      data-cc-value="${esc(hint)}"
                      data-cc-icon="sparkles">
                <span class="q-cc-option-icon"><i data-lucide="sparkles"></i></span>
                <span class="q-cc-option-label">${esc(hint)}</span>
              </button>
            </div>
          </div>` : ""}
        ${quick.length ? `
          <div class="q-cc-quick">
            <div class="q-cc-quick-head"><i data-lucide="zap"></i> Quick picks</div>
            <div class="q-cc-quick-grid">
              ${quick.map(o => `
                <button type="button"
                        class="q-cc-option q-cc-option--quick"
                        data-action="wizard#pickCommOption"
                        data-cc-value="${esc(o.value)}"
                        data-cc-icon="${esc(o.icon)}"
                        data-cc-color="${esc(o.color || "g-daily")}">
                  <span class="q-cc-option-icon"><i data-lucide="${esc(o.icon)}"></i></span>
                  <span class="q-cc-option-label">${esc(o.value)}</span>
                </button>
              `).join("")}
            </div>
          </div>` : ""}

        <div class="q-cc-groups" data-wizard-target="commGroups">
          ${Object.entries(grouped).map(([group, items]) => {
            const isOpen = openSet.has(group)
            return `
              <div class="q-cc-group${isOpen ? " is-open" : ""}" data-group="${esc(group)}">
                <button type="button"
                        class="q-cc-group-head"
                        data-action="wizard#toggleCommGroup">
                  <i data-lucide="chevron-right" class="q-cc-chev"></i>
                  <i data-lucide="${esc(CC_GROUP_ICON[group] || "tag")}" class="q-cc-group-icon"></i>
                  <span class="q-cc-group-label">${esc(group)}</span>
                  <span class="q-cc-group-count" data-original="${items.length}">${items.length}</span>
                </button>
                <div class="q-cc-group-grid">
                  ${items.map(o => `
                    <button type="button"
                            class="q-cc-option"
                            data-action="wizard#pickCommOption"
                            data-cc-value="${esc(o.value)}"
                            data-cc-icon="${esc(o.icon)}"
                            data-cc-color="${esc(o.color || "g-daily")}">
                      <span class="q-cc-option-icon"
                            style="background:var(--${esc(o.color || "g-daily")});">
                        <i data-lucide="${esc(o.icon)}"></i>
                      </span>
                      <span class="q-cc-option-label">${esc(o.value)}</span>
                    </button>
                  `).join("")}
                </div>
              </div>`
          }).join("")}
        </div>

        <div class="q-cc-divider">Or type your own</div>
        <div class="q-cc-custom">
          <input type="text"
                 class="q-cc-custom-input"
                 placeholder="Type a custom answer…"
                 data-wizard-target="commCustom"
                 data-action="keydown->wizard#commCustomKey">
          <button type="button"
                  class="q-cc-custom-btn"
                  data-action="wizard#pickCommCustom">+ Add</button>
        </div>
      </div>

      <div class="q-cc-stats">
        <span><strong data-wizard-target="commVisible">${total}</strong> visible
          / <strong>${total}</strong> total</span>
        <span class="q-cc-stats-actions">
          <button type="button" class="q-cc-stats-btn" data-action="wizard#expandAllCommGroups">
            <i data-lucide="chevrons-down"></i> Expand
          </button>
          <button type="button" class="q-cc-stats-btn" data-action="wizard#collapseAllCommGroups">
            <i data-lucide="chevrons-up"></i> Collapse
          </button>
        </span>
      </div>
    </div>`
}

// ---------- helpers ----------
function esc(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;")
}

// Escape text, then turn each "[FirstName]" into a live name-token span so
// name_token_controller.js resolves it (real first name, or the literal tag
// when Redact PHI is on / no name) — parity with the editor pane's
// server-side name_tokenize. The name-token controller is mounted on .en-app
// (which wraps the wizard pane) and its MutationObserver re-scans on every
// innerHTML rebuild, so spans injected here resolve automatically. Only use
// in element text content — never inside an HTML attribute (title=, etc.),
// where a <span> can't live.
function escName(s) {
  return esc(s).replace(/\[FirstName\]/g, '<span data-name-token>[FirstName]</span>')
}

// Returns " checked" when a field value reads as TBD/unknown, for the
// "Unknown — mark as TBD" checkbox. Module-level so EVERY step renderer can
// use it — it used to be a const local to the client() renderer, which made
// the about() renderer throw ReferenceError (tbd is not defined) and froze
// the wizard on its last-rendered step.
function tbdCheck(val) {
  return TBD_PATTERNS_TEST.test(String(val || "")) ? " checked" : ""
}

// Flag ("to be determined") state for a field. The flag is a NON-destructive
// marker stored in plan.meta.flags[path] — pressing it never rewrites the
// field text. Older plans that wrote a literal "TBD" into the value still
// read as flagged so their Open Questions continuity is preserved. Returns
// " checked" (truthy) / "" to match how qField consumes tbdChecked.
function flagState(plan, path, value) {
  if (plan && plan.meta && plan.meta.flags && plan.meta.flags[path]) return " checked"
  return tbdCheck(value)
}

// Mirrors editor_source/wizard.html#5488 isYouthAge — youth unless the
// builder has set client.ageGroup to "adult".
function isYouthAge(plan) {
  const ag = (plan?.client?.ageGroup || "").toString().toLowerCase()
  return ag !== "adult"
}

// Mirrors editor_source/wizard.html#4593 PROFESSIONAL_STANDARD — the 10
// roles seeded as TBD entries on first visit to the Circle of Support.
const PROFESSIONAL_STANDARD = [
  "Not yet",
  "Pediatrician",
  "Speech Therapist (SLP)",
  "Occupational Therapist (OT)",
  "Physical Therapist (PT)",
  "BCBA (ABA Supervisor)",
  "RBT (Behavior Tech)",
  "Special Education Teacher",
  "General Education Teacher",
  "Regional Center Service Coordinator",
  "Independent Facilitator"
]

// Full grouped catalog behind the modal's "+ Show more roles" toggle.
// Mirrors editor_source/wizard.html#4570 PROFESSIONAL_ROLE_GROUPS.
const PROFESSIONAL_ROLE_GROUPS = {
  "Doctors": [
    "Pediatrician", "Developmental Pediatrician", "Neurologist", "Psychiatrist",
    "Primary Care Physician", "Geneticist", "Gastroenterologist", "Sleep Specialist"
  ],
  "Therapists": [
    "Speech Therapist (SLP)", "Occupational Therapist (OT)", "Physical Therapist (PT)",
    "BCBA (ABA Supervisor)", "RBT (Behavior Tech)", "Mental Health Therapist",
    "Psychologist", "Music Therapist", "Feeding Therapist", "Vision Therapist"
  ],
  "School / Education": [
    "Special Education Teacher", "General Education Teacher", "IEP Coordinator",
    "School Psychologist", "1:1 Aide / Para", "Speech Therapist (school)",
    "OT (school)", "Resource Specialist", "Adapted PE Teacher"
  ],
  "Service Coordination": [
    "Regional Center Service Coordinator", "IHSS Social Worker",
    "Case Manager", "Insurance Care Coordinator", "Vocational Counselor",
    "Independent Facilitator", "Behavior Consultant", "Respite provider"
  ]
}

const PROFESSIONAL_MORE_GROUPS = (() => {
  const stdSet = new Set(PROFESSIONAL_STANDARD)
  const out = {}
  for (const [g, items] of Object.entries(PROFESSIONAL_ROLE_GROUPS)) {
    const filtered = items.filter(x => !stdSet.has(x))
    if (filtered.length) out[g] = filtered
  }
  return out
})()

const COMMUNITY_ROLE_SUGGESTIONS = [
  "Not yet",
  "Coach", "Mentor", "Neighbor", "Pastor / Faith leader", "Volunteer",
  "Bus driver", "After-school staff", "Camp counselor", "Babysitter"
]

const FRIEND_CONTEXT_SUGGESTIONS = [
  "Not yet",
  "School friend", "Soccer friend", "Church friend", "Neighborhood friend",
  "Family friend", "Daycare friend", "After-school friend",
  "Camp friend", "Online friend", "Best friend"
]

const FAMILY_RELATIONSHIP_SUGGESTIONS = [
  "Mom", "Dad", "Stepmom", "Stepdad", "Brother", "Sister", "Stepbrother", "Stepsister",
  "Grandma", "Grandpa", "Aunt", "Uncle", "Cousin", "Guardian", "Foster parent"
]

// Mirrors editor_source/wizard.html#4579 cosColor. Returns the colour key
// used by the per-relationship CSS variant (q-cos-pill--purple etc).
function cosColorKey(rel) {
  switch (rel) {
    case "Family":       return "purple"
    case "Friend":       return "blue"
    case "Professional": return "green"
    case "Community":    return "amber"
    case "Service Dog":  return "rose"
    default:             return "slate"
  }
}

// Custom (non-Lucide) icon SVGs — mirrors CUSTOM_ICON_INNER in
// app/helpers/lucide_helper.rb so the JS-rendered wizard nav, print
// preview rail, and progress dots all draw the same glyph the
// server-rendered surfaces do.
const CUSTOM_ICON_SVG = {
  "custom-ring-5-center": `<g fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="4.5" r="2.8"/><circle cx="5" cy="17.5" r="2.8"/><circle cx="19" cy="17.5" r="2.8"/><path d="M 14 6.3 Q 22.5 9.5 17.5 15.5"/><path d="M 16.6 18.7 Q 12 21 7.4 18.7"/><path d="M 6.5 15.5 Q 1.5 9.5 10 6.3"/></g>`
}

// Renders a section icon: if it's a custom (non-Lucide) name we emit
// the inline SVG directly; otherwise we drop a `<i data-lucide="...">`
// stub for lucide.createIcons() to hydrate as it does for every other
// icon in the app.
//
// Explicit width/height on the wrapper matches what Lucide injects
// when it hydrates an `<i data-lucide>` placeholder — without them the
// SVG falls into Chromium's intrinsic-sizing path which can collapse
// in PDF print mode, leaving a void in the surrounding flex column.
function sectionIconHtml(name) {
  const custom = CUSTOM_ICON_SVG[name]
  if (custom) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">${custom}</svg>`
  }
  return `<i data-lucide="${name}"></i>`
}

// Lucide icon name for each Circle-of-Support category. Mirrored by the
// editor pane's cos-relationship Stimulus controller so the two views
// agree on the same iconography.
function cosIconFor(rel) {
  switch (rel) {
    case "Family":       return "users"
    case "Friend":       return "smile"
    case "Professional": return "briefcase"
    case "Community":    return "globe"
    case "Service Dog":  return "dog"
    default:             return "user"
  }
}

// Mirrors editor_source/wizard.html#4649 parseCircleEntry — splits a stored
// `name` like "Sam (Brother)" or "Ms. Park (Speech Therapist (SLP))" back
// into { fullname, relationship, role }.
function parseCircleEntry(name, category) {
  if (!name) return {}
  const first = name.indexOf("(")
  const last  = name.lastIndexOf(")")
  if (first === -1 || last === -1 || last <= first) {
    return { fullname: TBD_PATTERNS_TEST.test(name) ? "" : name.trim() }
  }
  const before = name.slice(0, first).trim()
  const inside = name.slice(first + 1, last).trim()
  const fullname = TBD_PATTERNS_TEST.test(before) ? "" : before
  if (category === "Family") {
    const ageMatch = inside.match(/,\s*age\s+(\d+)\s*$/i)
    const relationship = inside.replace(/,\s*age\s+\d+\s*$/i, "").trim()
    return {
      fullname,
      relationship,
      under18: !!ageMatch,
      age: ageMatch ? ageMatch[1] : ""
    }
  }
  return { fullname, role: inside }
}

// ============ Structured entry editor (✎ on any wizard list row) ============
// Turns a stored list item into a set of editable fields and back, so the user
// edits an entry by its parts (household = Name/Relationship/Age, school =
// School/Type/Grade, barrier = Barrier/Strategy) instead of hand-maintaining a
// combined "Name [Age] (Relationship)" blob. Parse mirrors the editor's
// display-chip painter; assemble mirrors the person/school flows' recombine so
// data round-trips identically no matter which surface created it. Circle of
// Support keeps its own richer, category-aware modal (editCircleEntry).

// Household relationship quick-picks: the family roles plus the non-family
// household members the "lwm" parser recognises.
const HOUSEHOLD_RELATIONSHIP_SUGGESTIONS = [
  "Mother", "Father", "Stepmother", "Stepfather", "Spouse", "Partner",
  "Brother", "Sister", "Stepbrother", "Stepsister",
  "Grandmother", "Grandfather", "Aunt", "Uncle", "Cousin",
  "Guardian", "Foster parent", "Caregiver", "Roommate", "Pet"
]

const SCHOOL_LEVELS = [
  "Preschool", "Kindergarten", "Elementary school", "Middle school",
  "High school", "College", "Vocational / Trade", "Homeschool"
]

// about.whoLivesWithMe item → { name, relationship, age }. Prefers the
// structured keys the person-flow writes; falls back to parsing the combined
// text with the same age-vs-relationship heuristic the editor chips use.
function parseHouseholdItem(it) {
  if (!it) return { name: "", relationship: "", age: "" }
  const structuredName = [it.firstName, it.lastName].filter(Boolean).join(" ").trim()
  const raw      = (it.text || "").toString()
  const brackets = [...raw.matchAll(/\[([^\]]+)\]/g)].map(x => x[1].trim())
  const parens   = [...raw.matchAll(/\(([^()]+)\)/g)].map(x => x[1].trim())
  const looksAge = (s) => /\d/.test(s) || /\byear|\bmonth|\bold\b/i.test(s)
  let relFromText = "", ageFromText = ""
  if (brackets.length >= 2) {
    const [a, b] = brackets
    if (looksAge(a) && !looksAge(b))      { ageFromText = a; relFromText = b }
    else if (!looksAge(a) && looksAge(b)) { relFromText = a; ageFromText = b }
    else                                  { ageFromText = a; relFromText = b }
  } else if (brackets.length === 1) {
    if (looksAge(brackets[0])) ageFromText = brackets[0]; else relFromText = brackets[0]
  }
  if (!relFromText && parens.length) relFromText = parens[0]
  const nameFromText = raw.replace(/\[[^\]]*\]/g, "").replace(/\([^()]*\)/g, "").trim()
  return {
    name:         structuredName || (TBD_PATTERNS_TEST.test(nameFromText) ? "" : nameFromText),
    relationship: it.relationship || relFromText || "",
    age:          (it.ageGroup != null && it.ageGroup !== "") ? String(it.ageGroup) : ageFromText
  }
}

// { name, relationship, age } → patch written onto the item. Mirrors
// finishPersonFlow: "Name [Age] (Relationship)" + the structured keys.
function assembleHouseholdItem(v) {
  const name = (v.name || "").trim()
  const rel  = (v.relationship || "").trim()
  const age  = (v.age || "").trim()
  const ageMid    = age ? ` [${age}]` : ""
  const text      = rel ? `${name}${ageMid} (${rel})` : `${name}${ageMid}`.trim()
  const nameParts = name.split(/\s+/)
  const firstName = nameParts.shift() || ""
  const lastName  = nameParts.join(" ")
  const numeric   = /^\d+$/.test(age) ? parseInt(age, 10) : null
  return { text, firstName, lastName, relationship: rel, ageGroup: age, ageYears: numeric }
}

// about.schoolDetails item → { schoolName, level, grade }.
function parseSchoolItem(it) {
  if (!it) return { schoolName: "", level: "", grade: "" }
  const raw    = (it.text || "").toString()
  const parens = [...raw.matchAll(/\(([^()]+)\)/g)].map(x => x[1].trim())
  const gradeFromText = parens.length ? parens[parens.length - 1].replace(/\s*grade$/i, "").trim() : ""
  const nameFromText  = raw.replace(/\([^()]*\)/g, "").trim()
  return {
    schoolName: it.schoolName || (TBD_PATTERNS_TEST.test(nameFromText) ? "" : nameFromText),
    level:      it.level || "",
    grade:      it.grade || gradeFromText
  }
}

// { schoolName, level, grade } → patch. Mirrors finishSchoolFlow's grade text.
function assembleSchoolItem(v) {
  const name  = (v.schoolName || "").trim()
  const level = (v.level || "").trim()
  const grade = (v.grade || "").trim()
  const isCollege = ["Freshman", "Sophomore", "Junior", "Senior", "Grad"].includes(grade)
  const gradeText = (!grade || grade === "N/A") ? "" : (isCollege ? grade : `${grade} grade`)
  const text = gradeText ? `${name} (${gradeText})` : name
  return { text, schoolName: name, level, grade }
}

// Registry keyed by list path. Each: { title, fields[], parse(item),
// assemble(values) }. field = { key, label, placeholder, required?,
// suggestions?, type? ("text"|"textarea") }. Paths with no entry fall back to a
// single roomy field via _schemaForPath so every list is still editable.
const ENTRY_EDIT_SCHEMAS = {
  "about.whoLivesWithMe": {
    title: "Edit household member",
    fields: [
      { key: "name",         label: "Name",            placeholder: "e.g. Elena Rodriguez", required: true },
      { key: "relationship", label: "Relationship",    placeholder: "e.g. Mother, Brother", suggestions: HOUSEHOLD_RELATIONSHIP_SUGGESTIONS },
      { key: "age",          label: "Age (optional)",  placeholder: "e.g. 8",               suggestions: AGE_GROUPS }
    ],
    parse: parseHouseholdItem,
    assemble: assembleHouseholdItem
  },
  "about.schoolDetails": {
    title: "Edit school",
    fields: [
      { key: "schoolName", label: "School name",      placeholder: "e.g. Lincoln Elementary", required: true },
      { key: "level",      label: "Type (optional)",  placeholder: "e.g. Elementary school",  suggestions: SCHOOL_LEVELS },
      { key: "grade",      label: "Grade (optional)", placeholder: "e.g. 4th",                suggestions: SCHOOL_GRADES }
    ],
    parse: parseSchoolItem,
    assemble: assembleSchoolItem
  },
  "barriers": {
    title: "Edit barrier",
    fields: [
      { key: "description", label: "Barrier",              placeholder: "What's getting in the way?", required: true, type: "textarea" },
      { key: "strategy",    label: "Strategy to address it", placeholder: "How will we work on it?",   type: "textarea" }
    ],
    parse:    (it) => ({ description: (it && it.description) || "", strategy: (it && it.strategy) || "" }),
    assemble: (v)  => ({ description: (v.description || "").trim(), strategy: (v.strategy || "").trim() })
  }
}

// ---------- Nearby-schools lookup (OSM Overpass + zippopotam) ----------
// Powers the School-detail step's "Elementary / Middle / High / College"
// chips: instead of adding the literal label as a row, those chips open
// a picker showing actual nearby schools of the matching level — sourced
// from OpenStreetMap's amenity=school dataset around the participant's
// ZIP. Mirrors editor_source/wizard.html#5402.

const SCHOOL_LEVEL_CHIPS = new Set(["Elementary school", "Middle school", "High school", "College"])

// Grade pills shown above the picker's school list. Mirrors
// editor_source/wizard.html#4950. Picking a grade prefixes the row
// the user adds — "Lincoln Elementary, 4th".
const SCHOOL_GRADES = ["N/A", "Pre-K", "K", "1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th", "10th", "11th", "12th"]

const _schoolCache = {}
const _zipLatLonCache = {}

function _haversineMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000
  const toRad = x => x * Math.PI / 180
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a = Math.sin(dLat / 2) ** 2
          + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(a))
}

async function _resolveLatLon(plan) {
  // Plan may carry strings or numbers depending on which controller wrote
  // them — coerce so the downstream toFixed() / float math stays sane.
  const lat0 = parseFloat(plan?.client?.lat)
  const lon0 = parseFloat(plan?.client?.lon)
  if (Number.isFinite(lat0) && Number.isFinite(lon0)) {
    return { lat: lat0, lon: lon0 }
  }
  // ZIP first — zippopotam returns lat/lon alongside city/state.
  const zip = plan?.client?.zip
  if (/^\d{5}$/.test(zip || "")) {
    if (_zipLatLonCache[zip]) {
      return _zipLatLonCache[zip]
    }
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${encodeURIComponent(zip)}`)
      if (res.ok) {
        const data = await res.json()
        const place = (data.places || [])[0]
        const lat = parseFloat(place?.latitude)
        const lon = parseFloat(place?.longitude)
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          const out = { lat, lon }
          _zipLatLonCache[zip] = out
          return out
        }
      }
    } catch (e) { console.warn("[wizard/schools] zippopotam fetch failed", e) }
  }
  // Nominatim fallback — city + state geocode. Mirrors editor_source/
  // wizard.html#5136 so a city-only address still locates the participant.
  const city  = (plan?.client?.city  || "").trim()
  const state = (plan?.client?.state || "").trim()
  if (city) {
    const cacheKey = `${city.toLowerCase()}|${state.toLowerCase()}`
    if (_zipLatLonCache[cacheKey]) {
      return _zipLatLonCache[cacheKey]
    }
    try {
      const q = encodeURIComponent([city, state, "USA"].filter(Boolean).join(", "))
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${q}`, {
        headers: { "Accept": "application/json" }
      })
      if (res.ok) {
        const arr = await res.json()
        const first = Array.isArray(arr) ? arr[0] : null
        const lat = parseFloat(first?.lat)
        const lon = parseFloat(first?.lon)
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          const out = { lat, lon }
          _zipLatLonCache[cacheKey] = out
          return out
        }
      }
    } catch (e) { console.warn("[wizard/schools] nominatim fetch failed", e) }
  }
  console.warn("[wizard/schools] lat/lon not resolved — need ZIP or city on Client step")
  return null
}

async function _fetchNearbySchoolsAtRadius(lat, lon, radiusMeters) {
  const key = `${lat.toFixed(3)},${lon.toFixed(3)},${radiusMeters}`
  if (_schoolCache[key]) {
    return _schoolCache[key]
  }
  const query = `[out:json][timeout:25];
    (
      node["amenity"="school"](around:${radiusMeters},${lat},${lon});
      way["amenity"="school"](around:${radiusMeters},${lat},${lon});
      relation["amenity"="school"](around:${radiusMeters},${lat},${lon});
    );
    out tags center;`
  const res = await fetch("https://overpass-api.de/api/interpreter", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: "data=" + encodeURIComponent(query)
  })
  if (!res.ok) throw new Error(`overpass HTTP ${res.status}`)
  const data = await res.json()
  const seen = new Set()
  const schools = (data.elements || [])
    .map(el => {
      const tags = el.tags || {}
      const name = tags.name
      if (!name) return null
      const eLat = el.lat != null ? el.lat : (el.center && el.center.lat)
      const eLon = el.lon != null ? el.lon : (el.center && el.center.lon)
      const dist = (eLat != null && eLon != null) ? _haversineMeters(lat, lon, eLat, eLon) : Infinity
      const level = String(tags["isced:level"] || tags["school:level"] || "")
      return { name, level, dist }
    })
    .filter(s => {
      if (!s) return false
      const k = s.name.toLowerCase()
      if (seen.has(k)) return false
      seen.add(k)
      return true
    })
    .sort((a, b) => a.dist - b.dist)
  _schoolCache[key] = schools
  return schools
}

// Progressively widen the search radius until we find ANY schools. Rural
// ZIPs can have nothing within 8km but plenty within 24km — same flow the
// user is used to from the legacy wizard's modal.
async function _fetchNearbySchools(lat, lon) {
  for (const r of [8000, 16000, 30000]) {
    const list = await _fetchNearbySchoolsAtRadius(lat, lon, r)
    if (list.length) return list
  }
  return []
}

function _filterSchoolsByLevel(schools, label) {
  if (!schools || !schools.length) return []
  const l = (label || "").toLowerCase()
  const isElem = l.includes("elementary")
  const isMid  = l.includes("middle")
  const isHigh = l.includes("high")
  const isCol  = l.includes("college")
  const matchByName = (n) => {
    const x = n.toLowerCase()
    if (isElem) return /elementary|primary/.test(x)
    if (isMid)  return /middle|junior/.test(x)
    if (isHigh) return /\bhigh\b|senior/.test(x)
    if (isCol)  return /college|university/.test(x)
    return true
  }
  const matchByLevel = (lvl) => {
    const v = (lvl || "").toLowerCase()
    if (isElem) return /(^|\D)1(\D|$)|primary|elementary/.test(v)
    if (isMid)  return /(^|\D)2(\D|$)|middle/.test(v)
    if (isHigh) return /(^|\D)3(\D|$)|secondary|high/.test(v)
    if (isCol)  return /college|tertiary|[5-8]/.test(v)
    return true
  }
  const filtered = schools.filter(s => matchByLevel(s.level) || matchByName(s.name))
  return filtered.length ? filtered : schools
}

// One-tap quick-fill chips that drop a preset string into a plan-field
// input. Used under the SMART Goal card's Frequency / Timeline / Cost
// inputs so the most common values are one click away — the input still
// accepts free text, and the underlying browser datalist still surfaces
// the long list on focus.
//
// Clicking a chip routes through wizard#fillField; if the chip's value
// already matches the current plan value the chip renders selected
// (✓ + indigo) and clicking it clears the field, mirroring the
// already-selected toggle UX on q-chip and q-goalsuggest.
function quickChipsRow(path, values, currentValue = "") {
  if (!Array.isArray(values) || values.length === 0) return ""
  const cur = String(currentValue ?? "")
  return `
    <div class="q-quickchips">
      ${values.map(v => {
        const on = cur === String(v)
        return `
        <button type="button"
                class="q-quickchip${on ? " is-on" : ""}"
                data-action="wizard#fillField"
                data-target-path="${esc(path)}"
                data-fill-value="${esc(v)}"
                data-quickchip-value="${esc(v)}">${esc(v)}</button>`
      }).join("")}
    </div>`
}

// Split a scalar field that holds several values in one string into its
// tokens. Accepts either comma- or middot-separated free text so existing
// hand-typed values ("English, Spanish" or "Latino · Catholic") round-trip.
function splitTokens(str) {
  return String(str || "").split(/\s*[,·]\s*/).map(s => s.trim()).filter(Boolean)
}

// Multi-token version of quickChipsRow for scalar fields that hold SEVERAL
// values in one string (Primary languages "a, b"; Cultural identity "a · b").
// Tapping a chip toggles that token in the joined string via
// wizard#fillFieldToken; tokens already present render selected (✓ indigo).
// `sep` is the canonical join separator for this field.
function tokenChipsRow(path, values, currentValue = "", sep = ", ") {
  const presetList = Array.isArray(values) ? values : []
  const tokens = splitTokens(currentValue)
  const haveLc = new Set(tokens.map(t => t.toLowerCase()))
  const presetLc = new Set(presetList.map(v => String(v).toLowerCase()))
  const chip = (v, on) => `
        <button type="button"
                class="q-quickchip${on ? " is-on" : ""}"
                data-action="wizard#fillFieldToken"
                data-target-path="${esc(path)}"
                data-fill-value="${esc(v)}"
                data-token-sep="${esc(sep)}"
                data-chip-mode="token"
                data-quickchip-value="${esc(v)}">${esc(v)}</button>`
  const presets = presetList.map(v => chip(v, haveLc.has(String(v).toLowerCase())))
  // Custom values the user added via "Other" — shown as lit, removable chips
  // (tap to remove) so what they typed is visible, not just stored.
  const customs = tokens.filter(t => !presetLc.has(t.toLowerCase())).map(t => chip(t, true))
  // "+ Other" reveals a free-text add box (toggleTokenAdd); Enter adds a token.
  const otherBtn = `
        <button type="button" class="q-quickchip q-chip-add"
                data-action="wizard#toggleTokenAdd">+ Other</button>`
  const addInput = `
      <input type="text" class="q-input q-token-add mt-2"
             data-token-add-path="${esc(path)}" data-token-sep="${esc(sep)}"
             data-action="keydown->wizard#tokenAddKey"
             placeholder="Type and press Enter to add…" hidden>`
  return `
    <div class="q-quickchips">
      ${presets.join("")}${customs.join("")}${otherBtn}
    </div>${addInput}`
}

// Living situation: single-select preset chips + an "Other" chip that reveals a
// free-text box (about.livingSituationOther) so a non-preset situation is
// actually captured (issue #48). Uses wizard#pickLiving, which re-renders so the
// box shows/hides.
function livingSituationBlock(a) {
  const presets = ["Family home", "Foster home", "Group home"]
  const val = String((a && a.livingSituation) || "")
  const isOther = val === "Other" || (val !== "" && !presets.includes(val))
  const chip = (label, on, value, extraClass) => `
        <button type="button" class="q-quickchip${extraClass ? " " + extraClass : ""}${on ? " is-on" : ""}"
                data-action="wizard#pickLiving" data-fill-value="${esc(value == null ? label : value)}">${esc(label)}</button>`
  // "Other"/custom reveals a free-text box bound to livingSituation itself —
  // one free-text field, consistent with the editor's text input (no separate
  // detail key).
  const box = isOther ? `
      <input type="text" class="q-input mt-2"
             data-plan-field="about.livingSituation"
             value="${esc(val === "Other" ? "" : val)}"
             placeholder="Please specify the living situation…">` : ""
  return `
    <div class="q-fieldwrap">
      <label class="q-label block">Living situation</label>
      <div class="q-help">Tap an option, or choose Other to type your own.</div>
      <div class="q-quickchips">
        ${presets.map(p => chip(p, val === p)).join("")}
        ${chip("+ Other", isOther, "Other", "q-chip-add")}
      </div>
      ${box}
    </div>`
}

// Multi-select grid (mirrors editor_source/wizard.html#5258 renderCards).
// Each option becomes a 2-column card showing value + description. Selected
// cards show an indigo check. State lives at the dotted path as a list of
// { id, text: value } records — same shape used by the rest of the wizard
// so the editor can read it without conversion.
function multiSelectBlock(selected, path, label, help, options) {
  const sel = (val) => (selected || []).some(it => (typeof it === "string" ? it : it.text) === val)
  const presetValues = new Set(options.map(opt => typeof opt === "string" ? opt : opt.value))
  const presetCards = options.map(opt => {
    const value = typeof opt === "string" ? opt : opt.value
    const desc  = (typeof opt === "object" && opt.desc) || ""
    const icon  = (typeof opt === "object" && opt.icon) || ""
    const on    = sel(value)
    return `
      <button type="button" class="q-msel-card${on ? " is-on" : ""}"
              data-action="wizard#toggleMultiSelect"
              data-list-key="${esc(path)}"
              data-msel-value="${esc(value)}">
        <span class="q-msel-check">${on ? "✓" : ""}</span>
        ${icon ? `<span class="q-msel-icon"><i data-lucide="${esc(icon)}"></i></span>` : ""}
        <span class="q-msel-body">
          <span class="q-msel-value">${esc(value)}</span>
          ${desc ? `<span class="q-msel-desc">${esc(desc)}</span>` : ""}
        </span>
      </button>`
  }).join("")
  // User-added cards: anything in `selected` that isn't a preset. Rendered
  // as ✓-on cards (since they're definitionally selected); clicking removes
  // them via toggleMultiSelect, same as a preset card going off. Dedup by
  // case-insensitive text so two equivalent entries never both render.
  const seenCustom = new Set()
  const customCards = (selected || [])
    .map(it => typeof it === "string" ? it : (it && it.text) || "")
    .filter(t => {
      if (!t || presetValues.has(t)) return false
      const k = t.toLowerCase()
      if (seenCustom.has(k)) return false
      seenCustom.add(k)
      return true
    })
    .map(value => `
      <button type="button" class="q-msel-card is-on q-msel-card-custom"
              data-action="wizard#toggleMultiSelect"
              data-list-key="${esc(path)}"
              data-msel-value="${esc(value)}"
              title="Click to remove">
        <span class="q-msel-check">✓</span>
        <span class="q-msel-body">
          <span class="q-msel-value">${esc(value)}</span>
          <span class="q-msel-desc">Custom</span>
        </span>
      </button>`).join("")
  return `
    <div class="q-listsection">
      <label class="q-label block">${escName(label)}</label>
      ${help ? `<div class="q-help">${esc(help)}</div>` : ""}
      <div class="q-msel mt-4">${presetCards}${customCards}</div>
      <div class="q-msel-add">
        <input type="text"
               class="q-msel-add-input"
               placeholder="Or add your own…"
               data-list-add-input="${esc(path)}"
               data-action="keydown->wizard#mselAddKey">
        <button type="button"
                class="q-msel-add-btn"
                data-action="wizard#addMselCustom"
                data-list-key="${esc(path)}">+ Add</button>
      </div>
    </div>`
}

// Single-field row helper that lays out a label + description + input
// + "Unknown — mark as TBD" checkbox. Mirrors the pattern repeated
// across every wizard step in editor_source/wizard.html#977 onwards.
// Goal-just-added schedule form. Renders the post-pick micro-step
// where the user picks the frequency of the goal they just added.
// Mirrors the legacy editor's "Add Schedule" row shape (dayType +
// times + hours + minutes), then save commits the row to the goal's
// first resource schedule and returns control to the suggestion deck.
const GOAL_SCHED_DAY_TYPES = [
  "Daily", "Mon-Fri", "Weekly", "Bi-Weekly", "Monthly", "Quarterly",
  "Semi-Annually", "Annually", "As Needed",
  "Mon-Wed-Fri", "Tue-Thu", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"
]
// Dispatcher — picks which step renderer to use based on _wizGoalUi.step.
function goalScheduleStepHtml(goal, draft, step) {
  const title = goal.heading || goal.description || "this goal"
  const desc  = goal.description && goal.heading ? goal.description : ""
  const visibleSteps = GOAL_WIZ_STEPS
  const idx = Math.max(visibleSteps.indexOf(step), 0)
  const total = visibleSteps.length
  let body = ""
  switch (step) {
    case "tag":         body = gsTagStep(draft); break
    case "vendor":      body = gsVendorStep(draft); break
    case "service":     body = gsServicePickStep(draft); break
    case "frequency":   body = gsFrequencyStep(draft); break
    default: body = gsTagStep(draft)
  }
  const isLast = step === "frequency"
  return `
    <div class="q-space-y-9">
      <div class="q-listsection q-gsched-card">
        <div class="q-gsched-eyebrow">Step ${idx + 1} of ${total} · Goal added</div>
        <label class="q-label block">${esc(title)}</label>
        ${desc ? `<div class="q-help">${esc(desc)}</div>` : ""}
        <div class="q-gsched-body">${body}</div>
        <div class="q-gsched-actions">
          ${idx > 0 ? `<button type="button" class="q-gsched-cancel" data-action="wizard#goalWizBack">← Back</button>` : `<button type="button" class="q-gsched-cancel" data-action="wizard#cancelGoalSchedule">Skip goal</button>`}
          <span style="flex:1;"></span>
          ${!isLast
            ? `<button type="button" class="q-gsched-save" data-action="wizard#goalWizNext">Next →</button>`
            : `<button type="button" class="q-gsched-save" data-action="wizard#saveGoalSchedule">Submit</button>`}
        </div>
      </div>
    </div>`
}

// --- Per-step body fragments ---
function gsTagStep(d) {
  const tags = [
    { v: "unmet",   label: "Unmet Need", help: "A request for new funding or support." },
    { v: "rc",      label: "RC Funded",  help: "Currently funded by the Regional Center." },
    { v: "general", label: "Generic",    help: "A general resource that supports this goal." }
  ]
  return `
    <div class="q-gsched-question">Pick the funding tag for this resource</div>
    <div class="q-gsched-pills q-gsched-pills-big">
      ${tags.map(t => `
        <button type="button"
                class="q-gsched-pill q-gsched-pill-tag-${t.v}${d.tag === t.v ? " is-on" : ""}"
                data-action="wizard#pickGoalWizPill"
                data-field="tag"
                data-value="${esc(t.v)}">
          <span class="q-gsched-pill-label">${esc(t.label)}</span>
          <span class="q-gsched-pill-help">${esc(t.help)}</span>
        </button>`).join("")}
    </div>`
}

function gsVendorStep(d) {
  // Selected vendor confirmation card.
  if (d.vendorName) {
    return `
      <div class="q-gsched-question">Vendor selected</div>
      <div class="q-gsched-vendor-card">
        <div class="q-gsched-vendor-card-name">${esc(d.vendorName)}</div>
        ${d.vendorAddress ? `<div class="q-gsched-vendor-card-row"><strong>Address:</strong> ${esc(d.vendorAddress)}</div>` : ""}
        ${d.vendorPhone   ? `<div class="q-gsched-vendor-card-row"><strong>Phone:</strong> ${esc(d.vendorPhone)}</div>` : ""}
        ${d.vendorEmail   ? `<div class="q-gsched-vendor-card-row"><strong>Email:</strong> ${esc(d.vendorEmail)}</div>` : ""}
        ${d.vendorWebsite ? `<div class="q-gsched-vendor-card-row"><strong>Website:</strong> <a href="${esc(d.vendorWebsite)}" target="_blank" rel="noopener">${esc(d.vendorWebsite)}</a></div>` : ""}
      </div>
      <button type="button" class="q-gsched-cancel" style="margin-top:0.6rem;"
              data-action="wizard#clearGoalWizVendor">Change vendor</button>`
  }
  // Saved vendors for this library goal — offered first so the user can pick
  // straight from the goal's curated list before the domain search loads.
  const libV = Array.isArray(d.libVendors) ? d.libVendors.filter(v => v && v.name) : []
  const savedBlock = libV.length ? `
    <div class="q-gsched-saved-vendors" style="margin-bottom:0.85rem;">
      <div class="q-gsched-question" style="margin-bottom:0.4rem;">Saved vendors for this goal</div>
      <div class="q-gsched-vendor-grid">
        ${libV.map((v, i) => `
          <button type="button" class="q-gsched-vendor-btn"
                  data-action="wizard#pickGoalWizLibVendor" data-lib-vendor-idx="${i}">
            <div class="q-gsched-vendor-name">${esc(v.name)}</div>
            ${(v.phone || v.email || v.website) ? `<div class="q-gsched-vendor-meta">${esc([v.phone, v.email, v.website].filter(Boolean).join(" · "))}</div>` : ""}
          </button>`).join("")}
      </div>
      <div class="q-help" style="margin:0.5rem 0 0;">…or pick a nearby vendor by distance below.</div>
    </div>` : ""

  // Vendor button grid — populated asynchronously by
  // wizard#loadGoalWizVendors after this step renders. We render a
  // placeholder + a hidden "Search by name" toggle for the
  // less-common case where the desired vendor isn't in the
  // domain-matched results.
  return `
    ${savedBlock}
    <div class="q-gsched-vendor-head">
      <div class="q-gsched-question">Pick a vendor near you</div>
      <button type="button" class="q-gsched-skip-vendor"
              data-action="wizard#skipGoalWizVendor"
              title="No vendor yet — skip to scheduling">Skip &rarr;</button>
    </div>
    <div class="q-help" style="margin-bottom:0.6rem;">
      Filtered by this goal's domain and sorted by distance from your home address.
    </div>
    <div class="q-gsched-vendor-grid" data-vendor-grid data-vendor-grid-loaded="0">
      <div class="q-gsched-vendor-empty">Loading vendors…</div>
    </div>
    <details class="q-gsched-vendor-fallback" style="margin-top:0.75rem;">
      <summary>Can't find the right one? Search by name</summary>
      <div class="q-gsched-vendor-pick" style="margin-top:0.5rem;">
        <input class="q-input" type="text" autocomplete="off"
               placeholder="Search by vendor name…"
               data-action="input->wizard#searchGoalWizVendor blur->wizard#hideGoalWizVendorResults focus->wizard#searchGoalWizVendor">
        <div class="q-gsched-vendor-results" data-vendor-results hidden></div>
      </div>
    </details>`
}

function gsServicePickStep(d) {
  // The vendor's services were captured into the draft when the
  // user picked the vendor on the previous step. Show them as
  // selectable button cards, each showing name + location + cost.
  const services = Array.isArray(d.vendorServices) ? d.vendorServices : []
  const grid = services.length
    ? services.map((s, i) => `
        <button type="button"
                class="q-gsched-service-btn${d.servicePickedIdx === i ? " is-on" : ""}"
                data-action="wizard#pickGoalWizService"
                data-service-idx="${i}">
          <div class="q-gsched-service-name">${esc(s.name)}</div>
          <div class="q-gsched-service-meta">
            📍 ${esc(s.location_label || "")}
            ${s.cost_display ? ` · <strong>${esc(s.cost_display)}</strong>` : ""}
          </div>
        </button>`).join("")
    : `<div class="q-gsched-vendor-empty">This vendor hasn't listed services yet. Edit on the goal card after submit.</div>`
  return `
    <div class="q-gsched-question">Which service from ${esc(d.vendorName || "this vendor")}?</div>
    <div class="q-gsched-service-grid">${grid}</div>
    <div class="q-help" style="margin-top:0.5rem;">
      Tap a service — cost is set by the vendor (per location). Tap again to change.
    </div>`
}

function gsFrequencyStep(d) {
  const pills = GOAL_SCHED_DAY_TYPES.map(dt => `
    <button type="button"
            class="q-gsched-pill${d.dayType === dt ? " is-on" : ""}"
            data-action="wizard#pickGoalScheduleDayType"
            data-value="${esc(dt)}">${esc(dt)}</button>
  `).join("")
  return `
    <div class="q-gsched-question">How often does this happen?</div>
    <div>
      <label class="q-gsched-label">Day type</label>
      <div class="q-gsched-pills">${pills}</div>
    </div>
    <div class="q-gsched-grid q-gsched-grid-3">
      <div>
        <label class="q-gsched-label"># of times</label>
        <input class="q-input" type="text" inputmode="numeric" placeholder="e.g. 2"
               value="${esc(d.times || "")}"
               data-action="input->wizard#updateGoalScheduleField" data-field="times">
      </div>
      <div>
        <label class="q-gsched-label">Hours</label>
        <input class="q-input" type="text" inputmode="numeric" placeholder="hrs"
               value="${esc(d.hours || "")}"
               data-action="input->wizard#updateGoalScheduleField" data-field="hours">
      </div>
      <div>
        <label class="q-gsched-label">Minutes</label>
        <input class="q-input" type="text" inputmode="numeric" placeholder="min"
               value="${esc(d.mins || "")}"
               data-action="input->wizard#updateGoalScheduleField" data-field="mins">
      </div>
    </div>`
}

function qField({ label, help, chips = "", input, tbdFor, tbdChecked = "" }) {
  return `
    <div class="q-fieldwrap">
      <label class="q-label block">${escName(label)}</label>
      ${help ? `<div class="q-help">${escName(help)}</div>` : ""}
      ${chips || ""}
      ${input ? `<div class="q-input-row">${input}</div>` : ""}
      ${tbdFor ? `
        <button type="button" class="q-tbd-flag${tbdChecked ? " is-flagged" : ""}"
                data-action="wizard#toggleTbd" data-tbd-for="${esc(tbdFor)}"
                role="switch" aria-pressed="${tbdChecked ? "true" : "false"}"
                aria-label="Flag as TBD — unknown or to be determined"
                title="Flag as TBD — unknown / to be determined (tracked in Open Questions)">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
        </button>` : ""}
    </div>`
}

// Multi-list textarea bound to a dotted plan path. Items round-trip
// as {id, text} so they match the editor's dynamic-list shape.
// Mirrors editor_source/wizard.html#2833 listSection — label + help +
// list rows + add row + "+ TBD" + chip suggestions (flat and/or grouped).
function dynamicListInput(items, path, label, placeholder = "", opts = {}) {
  const arr = items || []
  const help             = opts.help || ""
  const suggestions      = Array.isArray(opts.suggestions) ? opts.suggestions : null
  const suggestionGroups = opts.suggestionGroups && typeof opts.suggestionGroups === "object" ? opts.suggestionGroups : null
  // The builder's structured sections (barriers) store user-entered text on
  // `description` instead of `text`; passing textKey lets the wizard round-trip
  // straight into the field the editor expects (mirrors editor_source/wizard.html#2544).
  const textKey          = opts.textKey || "text"
  // The row the editor pane just focused (clicked an existing entry) — the
  // wizard highlights + scrolls to it so "click a person on the left → edit
  // that person on the right" is a direct, visible connection.
  const focusRowId       = opts.focusRowId != null ? String(opts.focusRowId) : null
  const readText = (it) => (it && it[textKey] != null) ? it[textKey] : (it?.text || it?.description || "")

  const rows = arr.length
    ? arr.map(it => {
        const isFocused = focusRowId != null && String(it.id) === focusRowId
        return `
        <div class="q-listrow${isFocused ? " is-focused" : ""}"${isFocused ? " data-focus-row" : ""}>
          <textarea class="q-listrow-input" rows="1"
                    data-plan-field="${esc(path)}.${it.id}.${esc(textKey)}"
                    placeholder="${esc(placeholder)}">${esc(readText(it))}</textarea>
          <button type="button" class="q-listrow-edit"
                  data-row-id="${it.id}" data-list-path="${esc(path)}" data-text-key="${esc(textKey)}"
                  data-action="wizard#editEntry" title="Edit details">
            <i data-lucide="pencil"></i>
          </button>
          <button type="button" class="q-listrow-del" data-row-id="${it.id}" data-action="wizard#removeRow" title="Remove">&times;</button>
        </div>`
      }).join("")
    : `<div class="q-list-empty">Nothing added yet.</div>`

  // Multi-add lists never flip the chip into "on" — every click is
  // another add. Other lists keep the toggle UX (✓ = already added,
  // click to remove). See addChip + _isMultiAddList for the matching
  // controller behavior.
  const multiAdd = path === "about.whoLivesWithMe"
  // `n` is the 1-based grid number shown on the chip so a parent can call
  // it out ("add 12") and the facilitator can type it in the add box.
  const chipBtn = (label, n) => {
    const matches = arr.filter(it => {
      const t = readText(it)
      return t === label || (typeof t === "string" && t.includes(`(${label})`))
    }).length
    const on    = !multiAdd && matches > 0
    const count = multiAdd && matches > 0 ? matches : 0
    const icon  = chipIconFor(label)
    return `
    <button type="button" class="q-chip${on ? " is-on" : ""}${count ? " has-count" : ""}"
            data-action="wizard#addChip"
            data-list-key="${esc(path)}"
            data-text-key="${esc(textKey)}"
            data-chip="${esc(label)}"
            ${count ? `title="Already added ${count}. Click to add another."` : ""}>
      ${n ? `<span class="q-chip-num">${n}</span>` : ""}
      <span class="q-chip-plus">${on ? "✓" : "+"}</span>
      ${icon ? `<i data-lucide="${esc(icon)}" class="q-chip-icon"></i>` : ""}
      ${esc(label)}${count ? ` <span class="q-chip-count">${count}</span>` : ""}
    </button>`
  }

  let chipsHtml = ""
  if (suggestions && suggestions.length) {
    chipsHtml += `<div class="q-chips">${suggestions.map((label, i) => chipBtn(label, i + 1)).join("")}</div>`
  }
  if (suggestionGroups) {
    // Continuous numbering across the groups so every chip in this block
    // has a unique number (1..N).
    let n = 0
    chipsHtml += `
      <div class="q-chipgroups">
        ${Object.entries(suggestionGroups).map(([gname, items]) => `
          <div class="q-chipgroup">
            <div class="q-chipgroup-label">${esc(gname)}</div>
            <div class="q-chips">${(items || []).map(label => chipBtn(label, ++n)).join("")}</div>
          </div>`).join("")}
      </div>`
  }

  return `
    <div class="q-listsection">
      <label class="q-label block">${escName(label)}</label>
      ${help ? `<div class="q-help">${esc(help)}</div>` : ""}
      <div class="q-list mt-4" data-list-input="${esc(path)}" data-text-key="${esc(textKey)}">
        <div class="q-list-rows">${rows}</div>
        <div class="q-list-addrow">
          <textarea class="q-input q-list-addinput" rows="1"
                    data-list-add-input="${esc(path)}"
                    data-text-key="${esc(textKey)}"
                    placeholder="${esc(placeholder || "Add an item")}"
                    data-action="keydown->wizard#listAddKey"></textarea>
          <button type="button" class="q-list-addbtn"
                  data-list-key="${esc(path)}" data-text-key="${esc(textKey)}"
                  data-action="wizard#addListItem">+ Add</button>
          <button type="button" class="q-list-tbdbtn"
                  data-list-key="${esc(path)}" data-text-key="${esc(textKey)}"
                  data-action="wizard#addTbdItem"
                  title="Mark this as TBD — shows up in Open Questions for follow-up">+ TBD</button>
        </div>
        <div class="q-list-hint">
          Press <kbd>Enter</kbd> to add · <kbd>Shift</kbd>+<kbd>Enter</kbd> for new line${chipsHtml ? " · type a <strong>number</strong> to add that chip" : ""} · Tap <strong>+ TBD</strong> to mark as unknown
        </div>
        ${chipsHtml}
      </div>
    </div>`
}

// Derive a small deck of one-click goal suggestions from data the
// user already added to earlier wizard steps. Each suggestion is a
// pre-filled goal template (description / domain / timeline) plus
// a `source` label so the user can tell where the prompt came from.
//
// The suggestion text is deliberately broad — the wizard surfaces it
// in the goals editor as an editable card, so the user can tighten
// the wording, swap nouns, or rewrite it entirely after adding.
// Display order + label for the tab bar above the goal suggestion
// deck. "all" is always first; the others render in this order.
const GOAL_GROUPS = {
  adaptive:      "Adaptive Skills",
  socialrec:     "Social Rec",
  nonmedical:    "Non-Medical",
  educational:   "Educational",
  camp:          "Camp",
  therapy:       "Clinical Therapies",
  parenttraining:"Parent Training",
  living:        "Daily Living & Family",
  coordination:  "Coordination",
  access:        "Access",
  employment:    "Employment & Day",
  residential:   "Residential"
}

// The goal-library store + helpers now live in the shared "goal_library" module
// (imported at the top) so the editor's goal cards can reuse them too.

// Overlay saved edits onto the built-in suggestions. The key is computed from the
// BUILT-IN heading (before overlay) and attached as `_key` so a later re-edit keeps
// the same link even after the heading text has changed. `_edited` flags the card.
function applyGoalOverrides(list) {
  const ov = readGoalOverrides()
  const out = (list || []).map(s => {
    const key = goalOverrideKey(s)
    const o = ov[key]
    // A "_custom" entry is a brand-new goal (handled below), NOT an overlay of a built-in.
    const overlay = (o && typeof o === "object" && !o._custom) ? o : null
    const merged = overlay ? Object.assign({}, s, overlay) : Object.assign({}, s)
    merged._key = key
    merged._edited = !!overlay
    return merged
  })
  // Append the user's brand-new custom goals (keyed "custom|…").
  for (const [key, o] of Object.entries(ov)) {
    if (!o || typeof o !== "object" || !o._custom) continue
    const c = Object.assign({ group: "other", icon: "🎯", source: "Custom" }, o)
    c._key = key
    c._customNew = true
    out.push(c)
  }
  // Append the user's LIBRARY goals (serial PCP XXX.YY) as reusable suggestions.
  for (const g of libAllSorted()) {
    out.push({
      group: g.category || "other", icon: g.icon || "📗",
      heading: g.name, domain: g.domain, timeline: g.timeline,
      description: g.description, source: g.source || "Library",
      _serial: g.serial, _libXxx: g.xxx, _fromLibrary: true,
    })
  }
  return out
}

// The "+ Add your own goal" tile at the end of the suggestion deck.
function newGoalCardHtml() {
  return `
    <button type="button" class="q-goalsuggest q-goalsuggest-new"
            data-action="wizard#newGoalSuggestion"
            title="Create your own goal (saved to your library)">
      <div class="q-goalsuggest-new-plus">+</div>
      <div class="q-goalsuggest-new-label">Add your own goal</div>
    </button>`
}

function buildGoalSuggestions(plan) {
  // Always emit the [FirstName] token in goal descriptions — the editor
  // shows the tag, and the preview's _expandPlaceholders sweep swaps in
  // the participant's real first name. (Previously baked the literal
  // name in, so the editor showed "Jack will…" instead of the tag.)
  const firstName = "[FirstName]"
  const out = []
  const seenDesc = new Set()
  const push = (s) => {
    if (!s || !s.description) return
    const key = s.description.toLowerCase()
    if (seenDesc.has(key)) return
    seenDesc.add(key)
    out.push(s)
  }

  // Always-on starter suggestions — these read at the top of the
  // deck regardless of plan data so facilitators get a strong
  // funding-aligned opener even on sparse plans. Each maps to an
  // IRC service category the desktop sidecar already knows about.
  push({
    group:       "adaptive",
    icon:        "🏊",
    heading:     "Adaptive Swim & Water Safety",
    domain:      "Adaptive Skills / Water Safety",
    timeline:    "12 months",
    frequency:   "Weekly",
    description: `${firstName} will develop water-safety awareness and adaptive swimming skills to increase safety, independence, and confidence in community settings where water is present, such as pools, lakes, ponds, beaches, and water parks. Through adaptive swimming instruction, ${firstName} will work toward safely entering, floating, exiting the water, and following water-safety practices with decreasing levels of support over the next 12 months.`,
    source:      "Adaptive Skills Training + Adaptive Swimming"
  })
  // Music / Art / Dance have dedicated SCDD Non-Medical Therapy
  // service codes (693 / 691 / 692) when delivered by a credentialed
  // MT-BC / ATR / DMT against a written treatment plan, but the same
  // activity also gets funded as Social Recreation when it's a
  // community class/lesson. Carry both variants so the facilitator
  // picks whichever the IPP team is actually paying for.
  push({
    group:       "socialrec",
    icon:        "🎵",
    heading:     "Music Lessons (Social Rec)",
    domain:      "Social Recreation",
    timeline:    "12 months",
    frequency:   "Weekly",
    description: `Building on ${firstName}'s interest and enjoyment of music, ${firstName} will develop musical skills, express creativity, build meaningful relationships through shared interests, and increase participation in inclusive community music and recreational activities. Individualized (1:1) adaptive music coaching will support confidence, self-expression, independence, and active participation in community life over the next 12 months. This goal is consistent with the California Department of Developmental Services (DDS) Social Recreation, Camping, and Nonmedical Therapies Directive (Welfare & Institutions Code §4688.22), which recognizes that individualized (1:1) instruction, including private lessons, may be necessary to develop the skills, confidence, and independence needed to participate in integrated community activities, build relationships through shared interests, and achieve meaningful community inclusion.`,
    source:      "Social Recreation"
  })
  push({
    group:       "nonmedical",
    icon:        "🎼",
    heading:     "Music Therapy (Non-Medical)",
    domain:      "Non-Medical Therapies",
    timeline:    "12 months",
    frequency:   "Weekly",
    description: `${firstName} will receive weekly Music Therapy from a board-certified music therapist (MT-BC) as a Non-Medical Therapy intervention to address communication, sensory regulation, attention, and expressive engagement goals from the written treatment plan.`,
    source:      "Non-Medical Therapies"
  })
  push({
    group:       "socialrec",
    icon:        "🎨",
    heading:     "Art Lessons (Social Rec)",
    domain:      "Social Recreation",
    timeline:    "12 months",
    frequency:   "Weekly",
    description: `${firstName} will attend weekly adaptive art lessons in a community setting (Social Recreation) to build fine-motor control, focus, peer interaction, and creative self-expression, with progress tracked through completed projects over the next 12 months.`,
    source:      "Social Recreation"
  })
  push({
    group:       "nonmedical",
    icon:        "🖌️",
    heading:     "Art Therapy (Non-Medical)",
    domain:      "Non-Medical Therapies",
    timeline:    "12 months",
    frequency:   "Weekly",
    description: `${firstName} will receive weekly Art Therapy from a credentialed art therapist (ATR / ATR-BC) as a Non-Medical Therapy intervention to support emotional regulation, communication, and processing skills outlined in a written treatment plan.`,
    source:      "Non-Medical Therapies"
  })
  push({
    group:       "socialrec",
    icon:        "💃",
    heading:     "Dance Lessons (Social Rec)",
    domain:      "Social Recreation",
    timeline:    "12 months",
    frequency:   "Weekly",
    description: `${firstName} will engage in weekly adaptive dance lessons (Social Recreation) to build motor planning, body awareness, social interaction with peers, and physical stamina, with reducing prompts over the next 12 months.`,
    source:      "Social Recreation"
  })
  push({
    group:       "nonmedical",
    icon:        "🩰",
    heading:     "Dance / Movement Therapy (Non-Medical)",
    domain:      "Non-Medical Therapies",
    timeline:    "12 months",
    frequency:   "Weekly",
    description: `${firstName} will receive weekly Dance/Movement Therapy from a credentialed dance therapist (DMT / R-DMT / BC-DMT) as a Non-Medical Therapy intervention to support sensory regulation, body awareness, social-emotional expression, and motor planning goals from the written treatment plan.`,
    source:      "Non-Medical Therapies"
  })
  push({
    group:       "socialrec",
    icon:        "🏺",
    heading:     "Pottery Class (Recreational)",
    domain:      "Social Recreation",
    timeline:    "12 months",
    frequency:   "Weekly",
    description: `${firstName} will attend a weekly adaptive pottery class (Social Recreation) to build hand strength and fine-motor control, follow multi-step processes (wedging, shaping, glazing, firing), interact with peers in a small-group setting, and complete take-home pieces each quarter, with prompts reduced over the next 12 months.`,
    source:      "Social Recreation"
  })
  push({
    group:       "adaptive",
    icon:        "🏋️",
    heading:     "Adaptive Fitness",
    domain:      "Health & Wellness",
    timeline:    "12 months",
    frequency:   "2x weekly",
    description: `${firstName} will participate in an adaptive fitness program at least twice a week (e.g. strength, cardio, coordination drills) to build endurance, muscle tone, and healthy daily-activity habits over the next 12 months.`,
    source:      "Adaptive Skills Training + Social Recreation"
  })
  push({
    group:       "adaptive",
    icon:        "🧼",
    heading:     "Adaptive Self-Care Skills",
    domain:      "Adaptive Skills / Daily Living",
    timeline:    "12 months",
    frequency:   "Daily",
    description: `${firstName} will build self-care skills through an Adaptive Skills Training program — bathing / showering, grooming, dressing, eating routines, and personal-care hygiene — with prompts and physical assistance faded over the next 12 months.`,
    source:      "Adaptive Skills Training"
  })
  push({
    group:       "adaptive",
    icon:        "🚽",
    heading:     "Adaptive Toilet Routine",
    domain:      "Adaptive Skills / Daily Living",
    timeline:    "12 months",
    frequency:   "Daily",
    description: `${firstName} will build a safe, consistent toileting routine through an Adaptive Skills Training program — recognizing the need, transferring to the toilet, hygiene completion, and hand-washing — with prompts and physical assistance faded over the next 12 months.`,
    source:      "Adaptive Skills Training"
  })
  push({
    group:       "living",
    icon:        "🤝",
    heading:     "Personal Assistance Services",
    domain:      "Personal Assistance / Daily Living",
    timeline:    "12 months",
    frequency:   "Daily",
    description: `${firstName}, who is [Age] years old, will receive Personal Assistance Services to support meaningful community integration and participation in inclusive community activities — such as visiting libraries, parks, playgrounds, shopping centers, community centers, recreational programs, and local events. Services will provide individualized support to develop community navigation skills, social engagement, safety awareness, decision-making, and independence while accessing community resources.`,
    source:      "Personal Assistance Services"
  })
  push({
    group:       "living",
    icon:        "🔑",
    heading:     "Independent Living Skills (ILS)",
    domain:      "Independent Living",
    timeline:    "12 months",
    frequency:   "Weekly",
    description: `${firstName} will work with an Independent Living Services provider to build skills for living in the family home — cooking, money management, household routines, community access, and transportation use — with target hours and supports adjusted at each quarterly review.`,
    source:      "Independent Living Services (ILS)"
  })
  push({
    group:       "camp",
    icon:        "☀️",
    heading:     "Day Camp",
    domain:      "Social Recreation / Community Inclusion",
    timeline:    "12 months",
    frequency:   "Seasonal",
    description: `${firstName} will attend an adaptive day camp (Social Recreation / Camping) — returning home each evening — to build social skills, peer relationships, independence, and community experiences through structured recreational activities. This goal is consistent with the California Department of Developmental Services (DDS) Social Recreation, Camping, and Nonmedical Therapies Directive (Welfare & Institutions Code §4688.22).`,
    source:      "Social Recreation"
  })
  push({
    group:       "camp",
    icon:        "🏕️",
    heading:     "Overnight Camp",
    domain:      "Social Recreation / Community Inclusion",
    timeline:    "12 months",
    frequency:   "Annual",
    description: `${firstName} will attend an adaptive overnight (residential) camp (Social Recreation / Camping) — staying overnight for the camp session — to build independence, self-care skills away from home, social relationships, and community experiences. This goal is consistent with the California Department of Developmental Services (DDS) Social Recreation, Camping, and Nonmedical Therapies Directive (Welfare & Institutions Code §4688.22).`,
    source:      "Social Recreation"
  })
  push({
    group:       "living",
    icon:        "🛌",
    heading:     "Respite Services",
    domain:      "Family Support / Respite",
    timeline:    "12 months",
    frequency:   "Weekly",
    description: `Provide regularly scheduled in-home or out-of-home respite hours so the primary caregivers can rest and recharge while ${firstName} is safely supported by a qualified respite provider.`,
    source:      "Respite Services"
  })
  push({
    group:       "living",
    icon:        "🧸",
    heading:     "Specialized Childcare (Disability-Related)",
    domain:      "Family Support / Childcare",
    timeline:    "12 months",
    frequency:   "Weekly",
    description: `Secure consistent, supervised childcare beyond what same-age peers typically require, so ${firstName} is safely engaged during work, appointment, or sibling-event hours. Funded only for the disability-related portion above and beyond generic childcare — most often paid through the Self-Determination Program (SDP) when family resources don't cover it.`,
    source:      "Family Support / Self-Determination Program"
  })

  // Regional-center service-category starters. Wording mirrors the
  // funded service categories families actually see on their IPP/IFSP
  // across California regional centers, kept deliberately generic
  // (no specific RC named). Facilitators refine after adding.
  push({
    group:       "therapy",
    icon:        "🧠",
    heading:     "Behavior Services (ABA)",
    domain:      "Behavior Support",
    timeline:    "12 months",
    frequency:   "Weekly",
    description: `${firstName} will receive Behavior Services (e.g. Applied Behavior Analysis) from a qualified provider to reduce interfering behaviors and build replacement skills, with a written behavior plan, parent training, and quarterly progress review.`,
    source:      "Behavior Services"
  })
  push({
    group:       "therapy",
    icon:        "🗣️",
    heading:     "Speech & Language Therapy",
    domain:      "Communication",
    timeline:    "12 months",
    frequency:   "Weekly",
    description: `${firstName} will receive Speech & Language Therapy to build receptive language, expressive communication, articulation, and social-pragmatic skills against measurable communication targets.`,
    source:      "Speech & Language Therapy"
  })
  push({
    group:       "therapy",
    icon:        "🤚",
    heading:     "Occupational Therapy",
    domain:      "Fine Motor / Sensory",
    timeline:    "12 months",
    frequency:   "Weekly",
    description: `${firstName} will receive Occupational Therapy to build fine-motor control, sensory regulation, and daily-living skills (feeding, dressing, handwriting, self-care), with a home program to generalize gains across settings.`,
    source:      "Occupational Therapy"
  })
  push({
    group:       "therapy",
    icon:        "🦵",
    heading:     "Physical Therapy",
    domain:      "Gross Motor / Mobility",
    timeline:    "12 months",
    frequency:   "Weekly",
    description: `${firstName} will receive Physical Therapy to build gross-motor strength, balance, coordination, and safe mobility, with a home exercise program and quarterly re-assessment of functional movement goals.`,
    source:      "Physical Therapy"
  })
  push({
    group:       "access",
    icon:        "📱",
    heading:     "Assistive Technology (AAC)",
    domain:      "Communication / Access",
    timeline:    "12 months",
    frequency:   "Ongoing",
    description: `${firstName} will be evaluated for, obtain, and learn to use Assistive Technology — including an Augmentative & Alternative Communication (AAC) device or app — to support communication and access to learning, with training for caregivers and educators.`,
    source:      "Assistive Technology / AAC"
  })
  push({
    group:       "educational",
    icon:        "📚",
    heading:     "Tutoring Services",
    domain:      "Education / Access",
    timeline:    "12 months",
    frequency:   "Weekly",
    description: `${firstName} will receive individualized tutoring to support access to grade-level or IEP-aligned content (reading, math, study skills), coordinated with the school program and reviewed each grading period. Note: IEP-aligned tutoring is the school district's responsibility under FAPE; supplemental tutoring is typically funded through the Self-Determination Program (SDP) rather than as a standalone regional-center service.`,
    source:      "Self-Determination Program / School District"
  })
  push({
    group:       "employment",
    icon:        "🚌",
    heading:     "Transportation",
    domain:      "Community Access",
    timeline:    "12 months",
    frequency:   "Daily",
    description: `Arrange Transportation Services so ${firstName} can reliably attend day program, employment, therapy, school, or community activities — including driver, agency, or paratransit support as the IPP team determines appropriate.`,
    source:      "Transportation Services"
  })
  push({
    group:       "employment",
    icon:        "🌞",
    heading:     "Adult Day Activities Program",
    domain:      "Day Program",
    timeline:    "12 months",
    frequency:   "Daily",
    description: `${firstName} will attend an Adult Day Activities Program providing structured community access, social engagement, skill-building, and supervised meaningful day activities, with a person-centered schedule reviewed annually.`,
    source:      "Adult Day Activities Program"
  })
  push({
    group:       "employment",
    icon:        "💼",
    heading:     "Supported Employment",
    domain:      "Employment",
    timeline:    "12 months",
    frequency:   "Ongoing",
    description: `${firstName} will explore competitive integrated employment with Supported Employment services — job development, on-the-job coaching, and fading natural supports — toward sustained paid work in the community.`,
    source:      "Supported Employment"
  })
  push({
    group:       "employment",
    icon:        "🤝",
    heading:     "Social Skills Training",
    domain:      "Social / Behavioral",
    timeline:    "12 months",
    frequency:   "Weekly",
    description: `${firstName} will participate in Social Skills Training (group or 1:1) to build conversation, peer interaction, conflict-resolution, and community-safety skills, with generalization practice in real-world settings.`,
    source:      "Social Skills Training"
  })
  push({
    group:       "employment",
    icon:        "🚶",
    heading:     "Mobility Training",
    domain:      "Community Access",
    timeline:    "12 months",
    frequency:   "Weekly",
    description: `${firstName} will receive Mobility Training to safely navigate the community — using sidewalks, crossings, public transit, and wayfinding — with prompts and supervision faded over the next 12 months.`,
    source:      "Mobility Training"
  })
  push({
    group:       "coordination",
    icon:        "🧭",
    heading:     "Service Coordination",
    domain:      "Care Coordination",
    timeline:    "12 months",
    frequency:   "Quarterly",
    description: `A Regional Center Service Coordinator will partner with ${firstName} and the family to develop, monitor, and update the IPP/IFSP — linking generic resources, vendored services, and benefits — with documented contact at least quarterly.`,
    source:      "Service Coordination"
  })
  push({
    group:       "coordination",
    icon:        "🤲",
    heading:     "Coordinated Family Support Services (CFS)",
    domain:      "Family Coordination",
    timeline:    "12 months",
    frequency:   "Monthly",
    description: `Engage Coordinated Family Support Services (CFS) so an adult living with family receives an Independent Facilitator to coordinate person-centered supports, plan transitions, and link to resources — with regular check-ins.`,
    source:      "Coordinated Family Support Services"
  })
  push({
    group:       "parenttraining",
    icon:        "👨‍👩‍👧",
    heading:     "Parent / Family Training & Education",
    domain:      "Family Coordination",
    timeline:    "12 months",
    frequency:   "Quarterly",
    description: `The family will participate in Parent / Family Training & Education — workshops, 1:1 coaching, or conferences — to build skills supporting ${firstName} at home and in the community. Training material may include resources from organizations such as TACA (The Autism Community in Action), Family Resource Centers, Disability Rights California, and Self-Determination Program training. Topics identified at each IPP review.`,
    source:      "Parent / Family Training & Education"
  })
  push({
    group:       "coordination",
    icon:        "🧾",
    heading:     "IHSS Coordination",
    domain:      "Benefits Coordination",
    timeline:    "12 months",
    frequency:   "Annual",
    description: `The Service Coordinator will help the family apply for, maintain, or appeal In-Home Supportive Services (IHSS) hours so ${firstName} receives funded personal-care attendant time at home, with renewal and reassessment dates tracked on the IPP.`,
    source:      "Service Coordination / IHSS"
  })
  push({
    group:       "employment",
    icon:        "🎯",
    heading:     "Tailored Day Services (TDS)",
    domain:      "Day Program",
    timeline:    "12 months",
    frequency:   "Daily",
    description: `${firstName} will use Tailored Day Services to customize a day schedule — community classes, volunteer placements, employment exploration, or skill-building — that fits chosen interests, instead of a standard vendored day program. Reviewed annually.`,
    source:      "Tailored Day Services"
  })
  push({
    group:       "employment",
    icon:        "🌐",
    heading:     "Community Integration Supports",
    domain:      "Community Inclusion",
    timeline:    "12 months",
    frequency:   "Weekly",
    description: `${firstName} will receive Community Integration Supports to learn and practice the skills needed to take part in community life — accessing public spaces, attending events, joining groups, and building reciprocal relationships — with prompts faded over the next 12 months.`,
    source:      "Community Integration Supports"
  })
  push({
    group:       "residential",
    icon:        "🏠",
    heading:     "Supported Living Services",
    domain:      "Residential",
    timeline:    "12 months",
    frequency:   "Ongoing",
    description: `${firstName} will receive Supported Living Services to live in a chosen home in the community — with personalized supports for daily living, health, safety, and community participation, reviewed at least annually.`,
    source:      "Supported Living Services"
  })
  push({
    group:       "residential",
    icon:        "🏘️",
    heading:     "Family Home Agency (FHA) / FFA",
    domain:      "Residential",
    timeline:    "12 months",
    frequency:   "Ongoing",
    description: `Explore a Family Home Agency (FHA) or Family Foster Agency (FFA) placement where ${firstName} lives in a screened, supported family home — with agency oversight of training, monitoring, and ongoing supports.`,
    source:      "Family Home Agency / Family Foster Agency"
  })

  return out
}

const TEMPLATES = {
  text: () => ({ text: "" }),
  cos:  () => ({ name: "", relationship: "Family" }),
  comm: () => ({ happening: "", behavior: "", meaning: "", support: "" }),
  bar:  () => ({ description: "", strategy: "" }),
  goal: () => ({ heading: "", description: "", domain: "", timeline: "", frequency: "", cost: "", preface: "" }),
  cs:   () => ({ tag: "general", text: "" }),
  un:   () => ({ serviceType: "", text: "" })
}

// ---------- controller ----------
export default class extends Controller {
  static targets = [
    "nav", "screen", "prevBtn", "nextBtn", "progress",
    // Header-actions targets (production parity)
    "undoBtn", "redoBtn",
    "nextTbdBtn", "nextTbdCount",
    "loadFileInput",
    "status",
    "menuBtn", "menu",
    // Communication-picker targets (only present while the
    // communication step is showing the picker)
    "commSearch", "commCustom", "commBody", "commGroups", "commVisible",
    // Inline "City, ST" readout under the client ZIP field.
    "zipStatus"
  ]

  connect() {
    this.plan = this._load()
    if (!this.plan.meta) this.plan.meta = {}
    // Issue #7 / #12 follow-up: one-shot migration that rewrites
    // any existing about.whoLivesWithMe rows to the new
    // "Name [Age] (Role)" format. Old saves used "[Age] Name (Role)"
    // (Issue #1 prefix order) or "Name (Role) [Age]" (legacy
    // pre-Issue #1). Runs once per plan via a flag in meta.
    if (!this.plan.meta.lwmFormatV2) {
      const rows = (this.plan.about && this.plan.about.whoLivesWithMe) || []
      let touched = false
      rows.forEach(r => {
        if (!r || typeof r.text !== "string") return
        const t = r.text
        // Leading age: "[Age] Name (Role)" → "Name [Age] (Role)"
        let m = t.match(/^\s*(\[[^\]]+\])\s+(.+?)\s*(\([^)]+\))\s*$/)
        if (m) { r.text = `${m[2]} ${m[1]} ${m[3]}`.replace(/\s+/g, " "); touched = true; return }
        // Trailing age: "Name (Role) [Age]" → "Name [Age] (Role)"
        m = t.match(/^\s*(.+?)\s*(\([^)]+\))\s+(\[[^\]]+\])\s*$/)
        if (m) { r.text = `${m[1]} ${m[3]} ${m[2]}`.replace(/\s+/g, " "); touched = true; return }
      })
      this.plan.meta.lwmFormatV2 = true
      if (touched) this._save("about.whoLivesWithMe")
      else         this._save("meta.lwmFormatV2")
    }
    // Editor-is-the-master: read the active section directly from the
    // editor element's data-active-section-value attribute. Rails
    // renders that attribute on every editor load (native.html.erb:29),
    // so it's present in the DOM before any controller connects. This
    // eliminates the queueMicrotask race that previously let the
    // wizard render to its saved meta.wizardStep before the editor's
    // section-change event arrived. Fallback to URL segment (for
    // standalone wizard mounts on routes without the editor element)
    // then to "client".
    const editorEl = document.querySelector('[data-controller~="editor-native"]')
    const fromEditor = editorEl?.getAttribute("data-editor-native-active-section-value")
    const urlSection = this._sectionFromUrl()
    const wantId = fromEditor || urlSection || "client"
    this.step = SECTIONS.findIndex(s => s.id === wantId)
    if (this.step < 0) this.step = 0
    // Keep meta.wizardStep written so external save-file consumers
    // (e.g. the .sdp export) still see a "last viewed" hint, but
    // initial paint no longer reads from it.
    this.plan.meta.wizardStep = SECTIONS[this.step].id
    this._save("meta.wizardStep")
    // Dev-only parity check — fail-loud if someone added a section to
    // editor SECTION_KEYS without updating the wizard SECTIONS array
    // (or vice versa). PCPM_SECTION_COUNT is injected from Rails.
    if (typeof window.PCPM_SECTION_COUNT === "number" &&
        window.PCPM_SECTION_COUNT !== SECTIONS.length) {
      console.warn(
        `PCPM: wizard SECTIONS (${SECTIONS.length}) ≠ ` +
        `editor SECTION_KEYS (${window.PCPM_SECTION_COUNT}) — ` +
        `someone added a section to one side and not the other.`
      )
    }
    this._renderNav()
    this._renderStep()
    this._updateNextTbdBtn()
    // Close kebab menu on any outside click.
    this._onDocClick = (e) => {
      if (!this.hasMenuTarget) return
      if (this.menuTarget.hidden) return
      if (this.menuTarget.contains(e.target) || (this.hasMenuBtnTarget && this.menuBtnTarget.contains(e.target))) return
      this.menuTarget.hidden = true
    }
    document.addEventListener("click", this._onDocClick, true)

    // Editor-native section sync. When the embedded wizard sits next to
    // the editor section forms, the editor dispatches
    // "editor-native:section-change" whenever the active section flips.
    // Jump the wizard to the matching step so the right pane always
    // reflects the same section the left pane is editing.
    this._onSectionChange = (e) => {
      const id = e.detail?.sectionId
      if (!id) return
      // Section flip clears any sub-block focus from the previous
      // section — start the new section in its full-view state.
      _wizSubFocus.section  = null
      _wizSubFocus.path     = null
      _wizSubFocus.category = null
      _wizSubFocus.rowId    = null
      const idx = SECTIONS.findIndex(s => s.id === id)
      if (idx < 0 || idx === this.step) return
      this.step = idx
      this._persistStep()
      this._renderNav()
      this._renderStep()
      this._updateNextTbdBtn()
    }
    document.addEventListener("editor-native:section-change", this._onSectionChange)

    // Sub-block focus from the editor pane: when the user clicks into
    // a specific block (e.g. "Who Lives With Me?" inside About, or a
    // Family row inside Circle of Support), scope the wizard's
    // suggestions to that block. See editor_native_controller#_wireSubblockFocus.
    this._onSubblockFocus = (e) => {
      const d = e.detail || {}
      const changed = _wizSubFocus.path !== d.path ||
                      _wizSubFocus.category !== d.category ||
                      _wizSubFocus.section !== d.section ||
                      _wizSubFocus.rowId !== d.rowId
      _wizSubFocus.section  = d.section || null
      _wizSubFocus.path     = d.path || null
      _wizSubFocus.category = d.category || null
      _wizSubFocus.rowId    = d.rowId || null
      // Circle-of-Support: when the focused row carries a category,
      // mirror it into the inline accordion so the right card opens.
      if (d.section === "circleOfSupport" && d.category) {
        _wizCosUi.expand = d.category
      }
      if (changed) {
        this._renderStep()
        // Clicking a specific existing entry on the left surfaces THAT entry on
        // the right: scroll it into view (once, on focus change — not on every
        // keystroke re-render) without stealing the editor's caret.
        if (_wizSubFocus.rowId) {
          const el = this.screenTarget?.querySelector?.("[data-focus-row]")
          el?.scrollIntoView?.({ block: "nearest", behavior: "smooth" })
        }
      }
    }
    document.addEventListener("editor-native:subblock-focus", this._onSubblockFocus)

    // Cross-tab sync with the editor. The `storage` event fires in OTHER
    // tabs whenever localStorage[STORAGE_KEY] is written by the editor's
    // plan_store_controller, so we re-read the plan and re-render
    // without a refresh.
    this._onStorage = (e) => {
      if (e.key !== STORAGE_KEY || e.newValue == null) return
      try {
        const next = JSON.parse(e.newValue)
        if (next && typeof next === "object") {
          this.plan = next
          if (!this.plan.meta) this.plan.meta = {}
          this._renderNav()
          this._renderStep()
          this._updateNextTbdBtn()
          this._flashStatus("Synced from builder")
        }
      } catch (_) { /* malformed payload — ignore */ }
    }
    window.addEventListener("storage", this._onStorage)
    // Last-chance flush of a pending field save when the tab hides/closes.
    this._unregFlush = registerFlush(() => this._debouncedFieldSave?.flush())

    // Same-tab sync — if any other controller dispatches plan:change on
    // the document (e.g. plan_store_controller embedded for shared
    // widgets), use the in-memory plan from the event detail when
    // present and fall back to localStorage. Skip self-fired events
    // and skip when an input INSIDE this wizard pane is focused so we
    // don't rebuild the DOM out from under the user mid-keystroke.
    this._onPlanChange = (e) => {
      if (e?.detail?.source === "wizard") return
      if (e?.detail?.nested) return
      const active = document.activeElement
      const isEditable = active && (
        active.tagName === "INPUT" ||
        active.tagName === "TEXTAREA" ||
        active.tagName === "SELECT" ||
        active.isContentEditable
      )
      if (isEditable && this.element.contains(active)) {
        // Focused in the wizard pane — refresh the model but DON'T rebuild
        // the DOM mid-keystroke. Flush our own pending field save FIRST so the
        // just-typed value is persisted before we reload (otherwise reloading
        // this.plan would discard the unflushed keystroke). Reloading keeps
        // this.plan from going stale so the next _save() doesn't clobber an
        // async editor write.
        this._debouncedFieldSave?.flush()
        this.plan = this._load()
        if (!this.plan.meta) this.plan.meta = {}
        return
      }
      // Editor-pane edits mirror into the wizard. DEBOUNCE the re-render:
      // one editor action can fan out several plan:change events, and
      // rebuilding the whole wizard step (plus two full-plan JSON.stringify
      // diffs) on each was a major source of lag. Coalesce the burst into a
      // single re-render.
      ;(this._debRerender ||= debounce(() => {
        // Re-load into an INDEPENDENT object (never the dispatcher's live
        // reference) so the diff below isn't comparing an object to itself.
        const fresh = this._load()
        if (JSON.stringify(fresh) === JSON.stringify(this.plan)) return
        this.plan = fresh
        if (!this.plan.meta) this.plan.meta = {}
        this._renderNav()
        this._renderStep()
        this._updateNextTbdBtn()
      }, 150))()
    }
    document.addEventListener("plan:change", this._onPlanChange)

    // Communication-chart wiring. When the editor pane's comm-chart
    // controller flags a cell as active, jump the wizard to the
    // communication step (if not already) and render the cascading
    // picker. cell-deselect / row-complete swap back to the empty state.
    this._commActive = null
    this._commBypass = false
    this._onCommCellSelect = (e) => {
      const d = e.detail || {}
      if (d.rowId == null || !d.col) return
      this._commActive = { rowId: d.rowId, col: d.col, state: d.state || {} }
      this._commBypass = false
      const idx = SECTIONS.findIndex(s => s.id === "communication")
      if (idx >= 0 && this.step !== idx) {
        this.step = idx
        this._persistStep()
        this._renderNav()
        this._syncEditorSection()
      }
      this._renderStep()
    }
    this._onCommCellDeselect = () => {
      this._commActive = null
      this._commBypass = false
      if (SECTIONS[this.step]?.id === "communication") this._renderStep()
    }
    this._onCommRowComplete = (e) => {
      const rowIdx = this._commRowIndex(e.detail?.rowId)
      this._commActive = { done: true, rowIdx }
      this._commBypass = false
      if (SECTIONS[this.step]?.id === "communication") this._renderStep()
    }
    document.addEventListener("comm-chart:cell-select",   this._onCommCellSelect)
    document.addEventListener("comm-chart:cell-deselect", this._onCommCellDeselect)
    document.addEventListener("comm-chart:row-complete",  this._onCommRowComplete)
  }

  disconnect() {
    // Persist any keystroke still sitting in the debounce window so a
    // navigation away never drops the last edit.
    this._debouncedFieldSave?.flush()
    this._unregFlush?.()
    document.removeEventListener("click", this._onDocClick, true)
    window.removeEventListener("storage", this._onStorage)
    document.removeEventListener("plan:change", this._onPlanChange)
    document.removeEventListener("editor-native:section-change", this._onSectionChange)
    document.removeEventListener("editor-native:subblock-focus", this._onSubblockFocus)
    document.removeEventListener("comm-chart:cell-select",   this._onCommCellSelect)
    document.removeEventListener("comm-chart:cell-deselect", this._onCommCellDeselect)
    document.removeEventListener("comm-chart:row-complete",  this._onCommRowComplete)
  }

  next() {
    if (this.step < SECTIONS.length - 1) {
      this.step++
      this._persistStep()
      this._renderNav()
      this._renderStep()
      this._syncEditorSection()
    }
  }
  prev() {
    if (this.step > 0) {
      this.step--
      this._persistStep()
      this._renderNav()
      this._renderStep()
      this._syncEditorSection()
    }
  }

  jump(event) {
    const idx = parseInt(event.currentTarget.dataset.stepIdx, 10)
    if (Number.isFinite(idx) && idx >= 0 && idx < SECTIONS.length) {
      this.step = idx
      this._persistStep()
      this._renderNav()
      this._renderStep()
      this._syncEditorSection()
    }
  }

  // Input handler — dotted plan-field path, with optional .<id>. middle
  // segment for list items.
  fieldChanged(event) {
    const el = event.target
    const path = el.dataset.planField
    if (!path) return
    const val = el.type === "checkbox" ? !!el.checked : el.value
    this._setByPath(path, val)
    // The model is updated above immediately; debounce only the persist +
    // cross-section broadcast so typing doesn't serialize the whole plan and
    // fan out a re-render on every keystroke. Flushed on disconnect.
    ;(this._debouncedFieldSave ||= debounce((p) => this._save(p), 200, { dirty: true }))(path)
    // ZIP → city/state: resolve via zippopotam and refresh the inline
    // "City, ST" readout under the ZIP field. The wizard renders its own
    // ZIP field, so the editor pane's client-city controller can't update
    // THIS readout — the wizard has to run the lookup itself.
    if (path === "client.zip") this._resolveZipCity(val)
    // Keep the quick-fill chips under this input visually in sync as the
    // user types into the field directly (e.g. types "Weekly" — that
    // chip lights up; clears the field — chips reset).
    const tokenSet = new Set(splitTokens(val).map(t => t.toLowerCase()))
    this.screenTarget.querySelectorAll(`.q-quickchip[data-target-path="${CSS.escape(path)}"]`).forEach(chip => {
      const on = chip.dataset.chipMode === "token"
        ? tokenSet.has(String(chip.dataset.quickchipValue || "").toLowerCase())
        : (chip.dataset.quickchipValue === val && val !== "")
      chip.classList.toggle("is-on", on)
    })
    // Update the Next-TBD count + the per-section badges live as the
    // user types, so resolving a TBD removes the question from the
    // toolbar / sidebar immediately.
    this._updateNextTbdBtn()
    this._renderNav()
    this._toast(`Saved`)
  }

  addRow(event) {
    const btn = event.currentTarget
    const listKey = btn.dataset.listKey
    const tplKey  = btn.dataset.rowTemplate
    if (!listKey) return
    const template = TEMPLATES[tplKey] || TEMPLATES.text
    let arr = this._getByPath(listKey)
    if (!Array.isArray(arr)) {
      arr = []
      this._setByPath(listKey, arr)
    }
    arr.push({ id: Date.now() + Math.random(), ...template() })
    this._save(listKey)
    this._renderStep()
  }

  // Reorder a row up (-1) or down (+1) within its list. Mirrors the
  // editor's dynamic-list moveUp/moveDown buttons so the wizard goal
  // cards behave the same way.
  moveRow(event) {
    const btn = event.currentTarget
    const listKey = btn.dataset.listKey
    const rowId   = btn.dataset.rowId
    const dir     = parseInt(btn.dataset.direction, 10)
    if (!listKey || !rowId || (dir !== -1 && dir !== 1)) return
    const arr = this._getByPath(listKey)
    if (!Array.isArray(arr)) return
    const idx = arr.findIndex(r => String(r.id) === String(rowId))
    if (idx < 0) return
    const swap = idx + dir
    if (swap < 0 || swap >= arr.length) return
    const tmp = arr[idx]
    arr[idx] = arr[swap]
    arr[swap] = tmp
    this._save(listKey)
    this._renderStep()
  }

  // Tab bar above the suggestion deck — flip the active group, then
  // toggle [hidden] on each card so the deck filters in place without
  // a re-render. "all" shows everything.
  // Edit a suggested goal's wording (pencil on the card). Saves to the user's
  // library (OneDrive-synced SQLite in PCPDesk; localStorage in a browser) and
  // overlays the built-in suggestion everywhere it appears, on this and future plans.
  editGoalSuggestion(event) {
    event.preventDefault()
    event.stopPropagation() // must NOT also trigger the card's add
    const el = event.currentTarget
    const ix = parseInt(el.dataset.goalIndex, 10)
    const deck = el.closest("[data-wizard-goal-suggestions]")
    if (!deck || Number.isNaN(ix)) return
    let suggestions
    try { suggestions = JSON.parse(deck.dataset.wizardGoalSuggestions || "[]") } catch (_) { return }
    const s = suggestions[ix]
    if (s) this._openGoalEditModal(s)
  }

  // "+ Add your own goal" tile — opens the editor blank to create a new goal.
  newGoalSuggestion(event) {
    event.preventDefault()
    this._openGoalEditModal(
      { heading: "", domain: "", timeline: "12 months", frequency: "Weekly", description: "", group: "socialrec", source: "Custom", icon: "🎯" },
      { isNew: true }
    )
  }

  // "Start goal walkthrough" button — opens the full-space SMART goal
  // walkthrough that guides users through existing services + unmet needs
  // before defining goals.
  openGoalWalkthrough(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    // Find the goal-walkthrough controller in the goals section and trigger open
    const walkthroughEl = document.querySelector("[data-controller*='goal-walkthrough']")
    if (walkthroughEl) {
      const ctrl = this.application.getControllerForElementAndIdentifier(walkthroughEl, "goal-walkthrough")
      if (ctrl && typeof ctrl.open === "function") {
        ctrl.open()
      }
    }
  }

  _openGoalEditModal(s, opts = {}) {
    const isNew = !!opts.isNew
    const isCustom = isNew || !!s._customNew   // a brand-new goal (vs. an overlay of a built-in)
    const key = isNew ? null : (s._key || goalOverrideKey(s))

    const subtitle = `Saved to your library${goalLibrary() ? " (synced via OneDrive)" : " (this browser)"} and reused on future plans.`
    const html = wizardGoalEditorHtml({
      goal: {
        heading: s.heading || "",
        domain: s.domain || "",
        timeline: s.timeline || "",
        frequency: s.frequency || "",
        description: s.description || "",
        group: s.group || "other"
      },
      isNew,
      isCustom,
      groups: GOAL_GROUPS,
      subtitle,
      variant: "sheet"
    })

    const overlay = document.createElement("div")
    overlay.setAttribute("role", "dialog")
    overlay.setAttribute("aria-modal", "true")
    overlay.innerHTML = html
    document.body.appendChild(overlay)

    const editor = overlay.querySelector(".goal-editor")
    const val = (name) => { const e = editor.querySelector(`[data-f="${name}"]`); return e ? e.value : "" }
    const close = () => { document.removeEventListener("keydown", onKey); overlay.remove() }
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); close() } }
    document.addEventListener("keydown", onKey)
    editor.querySelector('[data-role="cancel"]').addEventListener("click", close)
    editor.querySelector('[data-role="save"]').addEventListener("click", () => {
      const heading = val("heading").trim()
      if (!heading) { const h = editor.querySelector('[data-f="heading"]'); if (h) h.focus(); return }
      const obj = {
        heading,
        domain:      val("domain").trim(),
        timeline:    val("timeline").trim(),
        frequency:   val("frequency").trim(),
        description: val("description"),
        group:       val("group") || "other",
      }
      if (isCustom) { obj._custom = true; obj.icon = s.icon || "🎯"; obj.source = s.source || "Custom" }
      const saveKey = key || ("custom|" + Date.now() + "-" + Math.random().toString(36).slice(2))
      writeGoalOverride(saveKey, obj)
      close(); this._renderStep()
    })
    const resetBtn = editor.querySelector('[data-role="reset"]')
    if (resetBtn) resetBtn.addEventListener("click", () => { if (key) deleteGoalOverride(key); close(); this._renderStep() })
    setTimeout(() => { const h = editor.querySelector('[data-f="heading"]'); if (h) { h.focus(); h.select() } }, 0)
  }

  // ★ Add this suggestion to the user's library (serial PCP XXX.YY). A goal that
  // came FROM the library prompts new-version vs new-goal (saveGoalToLibrary).
  addGoalToLibrary(event) {
    event.preventDefault()
    event.stopPropagation()
    const el = event.currentTarget
    const ix = parseInt(el.dataset.goalIndex, 10)
    const deck = el.closest("[data-wizard-goal-suggestions]")
    if (!deck || Number.isNaN(ix)) return
    let suggestions
    try { suggestions = JSON.parse(deck.dataset.wizardGoalSuggestions || "[]") } catch (_) { return }
    const s = suggestions[ix]
    if (!s) return
    saveGoalToLibrary(s, (typeof s._libXxx === "number") ? s._libXxx : null, () => this._renderStep())
  }

  filterGoalSuggestions(event) {
    const tab = event.currentTarget
    const group = tab.dataset.group || "all"
    const tabs = this.screenTarget.querySelectorAll(".q-goalsuggest-tab")
    tabs.forEach(t => t.classList.toggle("is-active", t === tab))
    const deck = this.screenTarget.querySelector("[data-wizard-goal-deck]")
    if (!deck) return
    deck.dataset.activeGroup = group
    this._applyGoalDeckFilter(deck)
  }

  // Default ↔ user-defined (library / custom) source switch. Resets the
  // category filter to "All" so a scope with no cards in the prior group
  // isn't shown blank.
  filterGoalScope(event) {
    const btn = event.currentTarget
    const scope = btn.dataset.scope || "default"
    this.screenTarget.querySelectorAll(".q-goalsuggest-scope-btn")
      .forEach(b => b.classList.toggle("is-active", b === btn))
    const deck = this.screenTarget.querySelector("[data-wizard-goal-deck]")
    if (!deck) return
    deck.dataset.activeScope = scope
    deck.dataset.activeGroup = "all"
    this.screenTarget.querySelectorAll(".q-goalsuggest-tab")
      .forEach(t => t.classList.toggle("is-active", (t.dataset.group || "all") === "all"))
    this._applyGoalDeckFilter(deck)
  }

  // Show cards matching BOTH the active scope and active group; keep the
  // "+ Add your own goal" tile always visible; recompute the category-tab
  // counts within the active scope and hide tabs empty in that scope.
  _applyGoalDeckFilter(deck) {
    const scope = deck.dataset.activeScope || "default"
    const group = deck.dataset.activeGroup || "all"
    const cards = Array.from(deck.querySelectorAll(".q-goalsuggest"))
      .filter(c => !c.classList.contains("q-goalsuggest-new"))
    cards.forEach(card => {
      const cScope = card.dataset.scope || "default"
      const g = card.dataset.group || "other"
      card.hidden = !(cScope === scope && (group === "all" || g === group))
    })
    this.screenTarget.querySelectorAll(".q-goalsuggest-tab").forEach(t => {
      const key = t.dataset.group || "all"
      const n = cards.filter(c =>
        (c.dataset.scope || "default") === scope &&
        (key === "all" || (c.dataset.group || "other") === key)
      ).length
      const cnt = t.querySelector(".q-goalsuggest-tab-count")
      if (cnt) cnt.textContent = n
      if (key !== "all") t.hidden = n === 0
    })
  }

  // Click on a "Suggested goal" card on the Goals step — read the
  // pre-built suggestion at the given index (the deck stores the full
  // suggestion array as JSON on its container), push a goal row, and
  // re-render so the user can edit / refine it in place.
  addGoalFromSuggestion(event) {
    const btn = event.currentTarget
    const ix  = parseInt(btn.dataset.goalIndex, 10)
    const deck = btn.closest("[data-wizard-goal-suggestions]")
    if (!deck || Number.isNaN(ix)) return
    let suggestions
    try { suggestions = JSON.parse(deck.dataset.wizardGoalSuggestions || "[]") }
    catch (_) { return }
    const s = suggestions[ix]
    if (!s) return

    if (!Array.isArray(this.plan.goals)) this.plan.goals = []
    // Toggle off if this suggestion is already in plan.goals (matched by
    // heading + description). Keeps the card's ✓ check honest: clicking
    // an already-added suggestion removes the goal instead of duplicating it.
    const sigKey = `${s.heading || ""}|${s.description || ""}`
    const existingIdx = this.plan.goals.findIndex(g =>
      `${g.heading || ""}|${g.description || ""}` === sigKey
    )
    if (existingIdx >= 0) {
      this.plan.goals.splice(existingIdx, 1)
      this._save("goals")
      this._updateNextTbdBtn()
      this._renderNav()
      this._renderStep()
      this._toast(`Removed goal from ${s.source || "suggestion"}`)
      return
    }
    const newId = Date.now() + Math.random()
    const newGoal = {
      id:          newId,
      heading:     s.heading     || "",
      description: s.description || "",
      domain:      s.domain      || "",
      timeline:    s.timeline    || "",
      frequency:   s.frequency   || "",
      cost:        s.cost        || "",
      preface:     s.preface     || ""
    }
    // Library goal → carry the family link, auto-fill its Reference Documents,
    // and stage its saved vendor list for the walkthrough's vendor step.
    let libVendors = []
    if (s._fromLibrary && typeof s._libXxx === "number") {
      newGoal.libSerial = s._serial || ""
      newGoal.libXxx    = s._libXxx
      const fam = libFamily(s._libXxx)
      const refs = (Array.isArray(fam.references) ? fam.references : [])
        .map(r => ({ id: Date.now() + Math.random(), title: (r.title || "").trim(), url: (r.url || "").trim(), chip: (r.chip || "").trim() }))
        .filter(r => r.title || r.url)
      if (refs.length) newGoal.references = refs
      // Vendors are { vendorId } refs — resolve to full objects from vendor library
      libVendors = libFamilyVendorsResolved(s._libXxx)
    }
    this.plan.goals.push(newGoal)
    // Switch the wizard pane into the step-by-step walkthrough for
    // this goal. Suggestion deck is hidden; user clicks through tag →
    // service type → location → vendor → address → schedule, then
    // Submit returns to the deck.
    _wizGoalUi.pendingSchedGoalId = newId
    _wizGoalUi.step = "tag"
    _wizGoalUi.draft = {
      tag: "general",
      vendorId: null, vendorName: "", vendorWebsite: "", vendorPhone: "",
      vendorEmail: "", vendorAddress: "",
      serviceType: "", costDisplay: "",
      libVendors,
      dayType: s.frequency && /weekly|daily|monthly/i.test(s.frequency) ? s.frequency : "Weekly",
      times: "", hours: "", mins: ""
    }
    this._save("goals")
    this._updateNextTbdBtn()
    this._renderNav()
    this._renderStep()
    this._toast(`Added goal from ${s.source || "suggestion"}`)
  }

  // ---- Goal post-add walkthrough actions ------------------------
  _goalWizVisibleSteps() {
    return GOAL_WIZ_STEPS
  }
  goalWizNext(event) {
    event?.preventDefault?.()
    const steps = this._goalWizVisibleSteps()
    const i = steps.indexOf(_wizGoalUi.step)
    if (i >= 0 && i < steps.length - 1) {
      _wizGoalUi.step = steps[i + 1]
      this._renderStep()
    }
  }
  goalWizBack(event) {
    event?.preventDefault?.()
    const steps = this._goalWizVisibleSteps()
    const i = steps.indexOf(_wizGoalUi.step)
    if (i > 0) {
      _wizGoalUi.step = steps[i - 1]
      this._renderStep()
    }
  }
  updateGoalWizField(event) {
    const f = event.currentTarget.dataset.field
    if (!f) return
    _wizGoalUi.draft[f] = event.currentTarget.value
  }

  // Loads vendor BUTTONS for the current goal, filtered by the goal's
  // domain and sorted by distance from the home address. Called from
  // goals() renderer right after re-rendering when we're on the
  // "vendor" step and no grid has been populated yet.
  async _loadGoalWizVendors() {
    if (!_wizGoalUi.pendingSchedGoalId) return
    if (_wizGoalUi.step !== "vendor") return
    const wiz = this.element
    const grid = wiz?.querySelector("[data-vendor-grid]")
    if (!grid || grid.dataset.vendorGridLoaded === "1") return
    grid.dataset.vendorGridLoaded = "1"
    const goal = (this.plan.goals || []).find(g => g.id === _wizGoalUi.pendingSchedGoalId)
    const domain = goal?.domain || ""
    let lat = null, lng = null
    try {
      const home = this._composeHomeAddressForWiz()
      if (home) {
        const r = await fetch(`/api/geocode?q=${encodeURIComponent(home)}`, { headers: { "Accept": "application/json" } })
        if (r.ok) {
          const j = await r.json()
          const hit = Array.isArray(j?.results) ? j.results[0] : null
          if (hit) { lat = parseFloat(hit.lat); lng = parseFloat(hit.lon) }
        }
      }
    } catch (_) {}
    const params = new URLSearchParams()
    if (domain) params.set("domain", domain)
    if (lat && lng) { params.set("lat", String(lat)); params.set("lng", String(lng)) }
    let arr = []
    try {
      const r = await fetch(`/api/vendors?${params.toString()}`, { headers: { "Accept": "application/json" } })
      if (r.ok) {
        const j = await r.json()
        arr = Array.isArray(j?.results) ? j.results : []
      }
    } catch (_) {}
    // If domain-filtered search produced nothing, fall back to ALL
    // vendors so the grid isn't empty.
    if (!arr.length && domain) {
      try {
        const params2 = new URLSearchParams()
        if (lat && lng) { params2.set("lat", String(lat)); params2.set("lng", String(lng)) }
        const r2 = await fetch(`/api/vendors?${params2.toString()}`, { headers: { "Accept": "application/json" } })
        if (r2.ok) {
          const j2 = await r2.json()
          arr = Array.isArray(j2?.results) ? j2.results : []
        }
      } catch (_) {}
    }
    if (!arr.length) {
      grid.innerHTML = `<div class="q-gsched-vendor-empty">No vendors in the directory yet. An admin can add some at /admin/vendors.</div>`
      return
    }
    grid.dataset.payload = JSON.stringify(arr)
    grid.innerHTML = arr.map((v, i) => `
      <button type="button" class="q-gsched-vendor-btn"
              data-action="wizard#pickGoalWizVendorBtn"
              data-vendor-idx="${i}">
        <div class="q-gsched-vendor-btn-head">
          <span class="q-gsched-vendor-btn-name">${esc(v.name)}</span>
          ${v.distance_mi != null ? `<span class="q-gsched-vendor-btn-dist">${v.distance_mi} mi</span>` : ""}
        </div>
        <div class="q-gsched-vendor-btn-meta">
          ${v.city ? `📍 ${esc(v.city)}` : ""}
          ${v.service_type ? ` · ${esc(v.service_type)}` : ""}
          ${v.cost_display ? ` · ${esc(v.cost_display)}` : ""}
        </div>
      </button>
    `).join("")
  }

  // Click handler for vendor BUTTONS in the grid. Re-uses the same
  // pickGoalWizVendor logic by injecting the payload via the grid.
  pickGoalWizVendorBtn(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const grid = btn.closest("[data-vendor-grid]")
    const idx = parseInt(btn.dataset.vendorIdx, 10)
    let arr = []
    try { arr = JSON.parse(grid?.dataset.payload || "[]") } catch (_) {}
    const v = arr[idx]
    if (!v) return
    _wizGoalUi.draft.vendorId      = v.id
    _wizGoalUi.draft.vendorName    = v.name || ""
    _wizGoalUi.draft.vendorWebsite = v.website || ""
    _wizGoalUi.draft.vendorPhone   = v.phone || ""
    _wizGoalUi.draft.vendorEmail   = v.email || ""
    _wizGoalUi.draft.vendorAddress = v.service_address || ""
    _wizGoalUi.draft.serviceType   = v.service_type || _wizGoalUi.draft.serviceType
    _wizGoalUi.draft.costDisplay   = v.cost_display || _wizGoalUi.draft.costDisplay
    // Cache the vendor's services list on the draft so the next step
    // can render them as buttons without another API round-trip.
    _wizGoalUi.draft.vendorServices  = Array.isArray(v.services) ? v.services : []
    _wizGoalUi.draft.servicePickedIdx = null
    // Cost-proof pamphlets the admin uploaded for this vendor.
    // Cached on the draft so saveGoalSchedule can persist them
    // onto the resource (and the PDF appendix can render them).
    _wizGoalUi.draft.vendorPamphlets = Array.isArray(v.pamphlets) ? v.pamphlets : []
    this._renderStep()
  }

  // Click on a service button (Step 3). Sets serviceType + cost from
  // the picked service so the editor card + PDF show the chosen
  // option.
  pickGoalWizService(event) {
    event.preventDefault()
    const idx = parseInt(event.currentTarget.dataset.serviceIdx, 10)
    const arr = _wizGoalUi.draft.vendorServices || []
    const s = arr[idx]
    if (!s) return
    _wizGoalUi.draft.servicePickedIdx = idx
    _wizGoalUi.draft.serviceType  = s.name
    _wizGoalUi.draft.costDisplay  = s.cost_display || ""
    _wizGoalUi.draft.serviceLocation = s.location_type || ""
    // Refresh just the .is-on highlight in place — no full re-render.
    const card = event.currentTarget.closest(".q-gsched-card")
    card?.querySelectorAll(".q-gsched-service-btn").forEach(el => el.classList.remove("is-on"))
    event.currentTarget.classList.add("is-on")
  }

  // Compose the home address from plan.client for the geocode call.
  _composeHomeAddressForWiz() {
    const c = this.plan?.client || {}
    return [c.address, c.city, c.state, c.zip].map(s => (s || "").trim()).filter(Boolean).join(", ")
  }

  // Vendor directory picker — hits /api/vendors and shows matches in a
  // dropdown. Selecting one fills vendorDetails + serviceAddress.
  searchGoalWizVendor(event) {
    const el = event.currentTarget
    const q = (el.value || "").trim()
    _wizGoalUi.draft.vendorDetails = el.value
    const card = el.closest(".q-gsched-card")
    const results = card?.querySelector("[data-vendor-results]")
    if (!results) return
    clearTimeout(this._vendorSearchTimer)
    if (q.length < 2) { results.hidden = true; return }
    this._vendorSearchTimer = setTimeout(() => {
      fetch(`/api/vendors?q=${encodeURIComponent(q)}`, { headers: { "Accept": "application/json" } })
        .then(r => r.ok ? r.json() : { results: [] })
        .then(json => {
          const arr = Array.isArray(json?.results) ? json.results : []
          if (!arr.length) { results.hidden = true; return }
          results.innerHTML = arr.map((v, i) => `
            <button type="button" class="q-gsched-vendor-item" data-vendor-idx="${i}"
                    data-action="mousedown->wizard#pickGoalWizVendor">
              <span class="q-gsched-vendor-name">${esc(v.name)}</span>
              <span class="q-gsched-vendor-meta">
                ${v.service_type ? esc(v.service_type) : ""}
                ${v.cost_display ? ` · ${esc(v.cost_display)}` : ""}
                ${v.phone ? ` · ${esc(v.phone)}` : ""}
              </span>
            </button>`).join("")
          results.dataset.payload = JSON.stringify(arr)
          results.hidden = false
        })
        .catch(() => { results.hidden = true })
    }, 300)
  }

  hideGoalWizVendorResults(event) {
    setTimeout(() => {
      const card = event.currentTarget.closest(".q-gsched-card")
      const results = card?.querySelector("[data-vendor-results]")
      if (results) results.hidden = true
    }, 200)
  }

  pickGoalWizVendor(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const results = btn.closest("[data-vendor-results]")
    const idx = parseInt(btn.dataset.vendorIdx, 10)
    let arr = []
    try { arr = JSON.parse(results?.dataset.payload || "[]") } catch (_) {}
    const v = arr[idx]
    if (!v) return
    // Vendor auto-fill — the next step (Service & cost) reads
    // serviceType + costDisplay from these.
    _wizGoalUi.draft.vendorId      = v.id
    _wizGoalUi.draft.vendorName    = v.name      || ""
    _wizGoalUi.draft.vendorWebsite = v.website   || ""
    _wizGoalUi.draft.vendorPhone   = v.phone     || ""
    _wizGoalUi.draft.vendorEmail   = v.email     || ""
    _wizGoalUi.draft.vendorAddress = v.service_address || ""
    _wizGoalUi.draft.serviceType   = v.service_type    || _wizGoalUi.draft.serviceType
    _wizGoalUi.draft.costDisplay   = v.cost_display    || _wizGoalUi.draft.costDisplay
    // Cache the vendor's services + pamphlets so the Service step and
    // saveGoalSchedule behave the same as the grid path (pickGoalWizVendorBtn).
    // Without this, a Search-by-name pick landed on an empty Service step
    // even when the vendor had services.
    _wizGoalUi.draft.vendorServices   = Array.isArray(v.services) ? v.services : []
    _wizGoalUi.draft.servicePickedIdx = null
    _wizGoalUi.draft.vendorPamphlets  = Array.isArray(v.pamphlets) ? v.pamphlets : []
    if (results) results.hidden = true
    // Stay on the vendor step so the user can confirm the auto-filled
    // card; they click Next → to advance to Service & cost.
    this._renderStep()
  }

  // Pick from the goal's SAVED (library) vendor list — fills the draft the
  // same way a search/grid pick does, then stays on the step to confirm.
  pickGoalWizLibVendor(event) {
    event.preventDefault()
    const idx = parseInt(event.currentTarget.dataset.libVendorIdx, 10)
    const arr = Array.isArray(_wizGoalUi.draft.libVendors) ? _wizGoalUi.draft.libVendors : []
    const v = arr[idx]
    if (!v) return
    _wizGoalUi.draft.vendorId      = v.id || null
    _wizGoalUi.draft.vendorName    = v.name    || ""
    _wizGoalUi.draft.vendorWebsite = v.website || ""
    _wizGoalUi.draft.vendorPhone   = v.phone   || ""
    _wizGoalUi.draft.vendorEmail   = v.email   || ""
    _wizGoalUi.draft.vendorAddress = v.address || ""
    _wizGoalUi.draft.vendorServices   = []
    _wizGoalUi.draft.servicePickedIdx = null
    _wizGoalUi.draft.vendorPamphlets  = []
    this._renderStep()
  }

  // "Change vendor" — clears the selection so the search input
  // re-appears. Doesn't auto-advance.
  clearGoalWizVendor(event) {
    event?.preventDefault?.()
    _wizGoalUi.draft.vendorId      = null
    _wizGoalUi.draft.vendorName    = ""
    _wizGoalUi.draft.vendorWebsite = ""
    _wizGoalUi.draft.vendorPhone   = ""
    _wizGoalUi.draft.vendorEmail   = ""
    _wizGoalUi.draft.vendorAddress = ""
    this._renderStep()
  }

  // "Skip" on the vendor step — the facilitator has no vendor yet. Clear
  // any partial vendor/service draft and jump past BOTH vendor-dependent
  // steps (vendor + service, since service just lists the vendor's
  // offerings) straight to frequency, which stands on its own. Same
  // reachable end-state as clicking Next → through the empty steps, but
  // one tap. Mirrors clearGoalWizVendor's reset + clears the service pick.
  skipGoalWizVendor(event) {
    event?.preventDefault?.()
    const d = _wizGoalUi.draft
    if (d) {
      d.vendorId         = null
      d.vendorName       = ""
      d.vendorWebsite    = ""
      d.vendorPhone      = ""
      d.vendorEmail      = ""
      d.vendorAddress    = ""
      d.vendorServices   = []
      d.servicePickedIdx = null
      d.serviceType      = ""
      d.costDisplay      = ""
    }
    _wizGoalUi.step = "frequency"
    this._renderStep()
  }
  pickGoalWizPill(event) {
    event?.preventDefault?.()
    const f = event.currentTarget.dataset.field
    const v = event.currentTarget.dataset.value
    if (!f || v == null) return
    _wizGoalUi.draft[f] = v
    // For pill-only fields (tag / location), auto-advance to the next
    // step on a single click. For a field that also has a typed input
    // (serviceType), don't auto-advance — let the user keep typing.
    const autoAdvance = (f === "tag" || f === "location")
    if (autoAdvance) {
      const steps = this._goalWizVisibleSteps()
      const i = steps.indexOf(_wizGoalUi.step)
      if (i >= 0 && i < steps.length - 1) _wizGoalUi.step = steps[i + 1]
      this._renderStep()
    } else {
      // Just refresh the is-on highlight without a full re-render so
      // any typed input keeps focus.
      const card = event.currentTarget.closest(".q-gsched-card")
      if (card) {
        card.querySelectorAll(`.q-gsched-pill[data-field='${CSS.escape(f)}']`)
          .forEach(el => el.classList.remove("is-on"))
        event.currentTarget.classList.add("is-on")
      }
      const input = card?.querySelector(`input[data-field='${CSS.escape(f)}']`)
      if (input) input.value = v
    }
  }

  pickGoalScheduleDayType(event) {
    event?.preventDefault?.()
    const val = event.currentTarget.dataset.value
    if (!val) return
    _wizGoalUi.draft.dayType = val
    // Toggle just the visible "is-on" state on the pills so we don't
    // re-render the whole pane and lose focus on the # / hrs / min
    // inputs the user may have started typing into.
    const card = event.currentTarget.closest(".q-gsched-card")
    if (card) {
      card.querySelectorAll(".q-gsched-pill").forEach(el => el.classList.remove("is-on"))
      event.currentTarget.classList.add("is-on")
    }
  }

  updateGoalScheduleField(event) {
    const el = event.currentTarget
    const f  = el.dataset.field
    if (!f) return
    _wizGoalUi.draft[f] = el.value
  }
  saveGoalSchedule(event) {
    event?.preventDefault?.()
    const id = _wizGoalUi.pendingSchedGoalId
    const goal = id && (this.plan.goals || []).find(g => g.id === id)
    const d = _wizGoalUi.draft || {}
    if (goal) {
      // Create / reuse the goal's first resource and stamp it with
      // every field the walkthrough collected.
      if (!Array.isArray(goal.resources)) goal.resources = []
      if (!goal.resources[0]) {
        goal.resources.push({
          id: Date.now() + Math.random(),
          tag: "general", text: "", comment: "",
          servicesLocation: "", vendorDetails: "",
          vendorAddress: "", mileage: "", ratio: "", schedule: []
        })
      }
      const r = goal.resources[0]
      r.tag              = d.tag         || r.tag || "general"
      r.text             = d.serviceType || r.text || ""
      // Map the service's location_type to the editor-pane's
      // Services Location label so the editor card matches.
      const LOC_MAP = { in_home: "In Home", in_center: "In Center", community: "Community-Based", telehealth: "Telehealth", hybrid: "Hybrid", school: "Community-Based" }
      if (d.serviceLocation && LOC_MAP[d.serviceLocation]) {
        r.servicesLocation = LOC_MAP[d.serviceLocation]
      }
      // Write each vendor detail to its own cell (the editor card
      // renders them as separate inputs now). Legacy vendorDetails
      // string stays in sync for older readers (PDF / preview).
      r.vendorName       = d.vendorName    || r.vendorName    || ""
      r.vendorPhone      = d.vendorPhone   || r.vendorPhone   || ""
      r.vendorEmail      = d.vendorEmail   || r.vendorEmail   || ""
      r.vendorWebsite    = d.vendorWebsite || r.vendorWebsite || ""
      r.vendorAddress    = d.vendorAddress || r.vendorAddress || ""
      r.vendorId         = d.vendorId      || null
      r.cost             = d.costDisplay   || r.cost || ""
      // Snapshot the vendor's cost-proof pamphlets onto the resource
      // so the PDF appendix can list them without a re-fetch.
      if (Array.isArray(d.vendorPamphlets) && d.vendorPamphlets.length) {
        r.vendorPamphlets = d.vendorPamphlets
      }
      const vparts = [r.vendorName, r.vendorPhone, r.vendorEmail].filter(Boolean)
      r.vendorDetails    = vparts.length ? vparts.join(" · ") : (r.vendorDetails || "")
      r.schedule = Array.isArray(r.schedule) ? r.schedule : []
      if (d.dayType || d.times || d.hours || d.mins) {
        r.schedule.push({
          id: Date.now() + Math.random(),
          dayType: d.dayType || "Weekly",
          times:   d.times   || "",
          hours:   d.hours   || "",
          mins:    d.mins    || "",
          cost:    d.costDisplay || ""
        })
      }
      // Human-readable frequency on the goal itself for the editor
      // card's chip and the suggestion deck's labels.
      goal.frequency = `${d.dayType || "Weekly"}${d.times ? ` × ${d.times}` : ""}`
    }
    // Reset the walkthrough and return to the deck — no auto-advance.
    _wizGoalUi.pendingSchedGoalId = null
    _wizGoalUi.step = "tag"
    _wizGoalUi.draft = {
      tag: "general",
      vendorId: null, vendorName: "", vendorWebsite: "", vendorPhone: "",
      vendorEmail: "", vendorAddress: "",
      vendorServices: [], servicePickedIdx: null, serviceLocation: "",
      serviceType: "", costDisplay: "",
      dayType: "Weekly", times: "", hours: "", mins: ""
    }
    this._save("goals")
    this._renderStep()
    this._toast("Goal saved")
  }

  // Return the first suggestion built from the current plan that
  // hasn't been added yet (heading+description signature mismatch).
  _nextUnaddedSuggestion() {
    try {
      const suggestions = buildGoalSuggestions(this.plan) || []
      const goals = this.plan.goals || []
      const key = (x) => `${x.heading || ""}|${x.description || ""}`
      const added = new Set(goals.map(key))
      return suggestions.find(s => !added.has(key(s))) || null
    } catch (_) {
      return null
    }
  }
  cancelGoalSchedule(event) {
    event?.preventDefault?.()
    _wizGoalUi.pendingSchedGoalId = null
    _wizGoalUi.step = "tag"
    _wizGoalUi.draft = {
      tag: "general",
      vendorId: null, vendorName: "", vendorWebsite: "", vendorPhone: "",
      vendorEmail: "", vendorAddress: "",
      vendorServices: [], servicePickedIdx: null, serviceLocation: "",
      serviceType: "", costDisplay: "",
      dayType: "Weekly", times: "", hours: "", mins: ""
    }
    this._renderStep()
  }

  // Single-input quick-fill chip (e.g. SMART Goal's Frequency / Cost /
  // Timeline chips). Routes through _setByPath so plan-store + the
  // editor pane both re-render live, then syncs the visible <input>
  // so the user sees the new value without a step re-render. Clicking
  // a chip whose value already matches the field clears the field
  // (toggle), matching the q-chip / q-goalsuggest ✓-state UX.
  fillField(event) {
    const btn = event.currentTarget
    const path = btn.dataset.targetPath
    const value = btn.dataset.fillValue
    if (!path || value == null) return
    const current = this._getByPath(path)
    const next = (String(current ?? "") === String(value)) ? "" : value
    this._setByPath(path, next)
    this._save(path)
    // Sync the visible input (and any sibling chips' on-state) without
    // tearing down the goal-card DOM the user is interacting with.
    const input = this.screenTarget.querySelector(`[data-plan-field="${CSS.escape(path)}"]`)
    if (input) input.value = next
    this.screenTarget.querySelectorAll(`.q-quickchip[data-target-path="${CSS.escape(path)}"]`).forEach(chip => {
      chip.classList.toggle("is-on", chip.dataset.quickchipValue === next && next !== "")
    })
    this._updateNextTbdBtn()
    this._renderNav()
  }

  // Multi-token variant of fillField for scalar fields that hold several
  // values in one string (Primary languages, Cultural identity). Toggles the
  // chip's token in / out of the separator-joined value while leaving any
  // hand-typed tokens intact, then re-syncs the visible input + sibling chips.
  fillFieldToken(event) {
    const btn = event.currentTarget
    const path = btn.dataset.targetPath
    const value = btn.dataset.fillValue
    const sep = btn.dataset.tokenSep || ", "
    if (!path || value == null) return
    const tokens = splitTokens(this._getByPath(path))
    const lc = String(value).toLowerCase()
    const at = tokens.findIndex(t => t.toLowerCase() === lc)
    if (at >= 0) tokens.splice(at, 1)
    else tokens.push(value)
    const next = tokens.join(sep)
    this._setByPath(path, next)
    this._save(path)
    const input = this.screenTarget.querySelector(`[data-plan-field="${CSS.escape(path)}"]`)
    if (input) input.value = next
    const present = new Set(splitTokens(next).map(t => t.toLowerCase()))
    this.screenTarget.querySelectorAll(`.q-quickchip[data-chip-mode="token"][data-target-path="${CSS.escape(path)}"]`).forEach(chip => {
      chip.classList.toggle("is-on", present.has(String(chip.dataset.quickchipValue || "").toLowerCase()))
    })
    this._updateNextTbdBtn()
    this._renderNav()
  }

  // Reveal/hide the free-text "Other" add box next to a token field's chips.
  toggleTokenAdd(event) {
    const chips = event.currentTarget.closest(".q-quickchips")
    const input = chips ? chips.nextElementSibling : null
    if (!input || !input.classList.contains("q-token-add")) return
    input.hidden = !input.hidden
    event.currentTarget.classList.toggle("is-on", !input.hidden)
    if (!input.hidden) input.focus()
  }

  // Enter in a token field's "Other" add box appends the typed value as a token
  // (deduped), then re-renders so it shows as a removable custom chip; the add
  // box stays open + focused so the user can keep adding.
  tokenAddKey(event) {
    if (event.key !== "Enter") return
    event.preventDefault()
    const el = event.currentTarget
    const path = el.dataset.tokenAddPath
    const sep = el.dataset.tokenSep || ", "
    const value = (el.value || "").trim()
    if (!path || !value) return
    const tokens = splitTokens(this._getByPath(path))
    if (!tokens.some(t => t.toLowerCase() === value.toLowerCase())) tokens.push(value)
    this._setByPath(path, tokens.join(sep))
    this._save(path)
    this._renderStep()
    renderIcons(this.element)
    const box = this.screenTarget.querySelector(`.q-token-add[data-token-add-path="${CSS.escape(path)}"]`)
    if (box) {
      box.hidden = false
      const addBtn = box.previousElementSibling ? box.previousElementSibling.querySelector(".q-chip-add") : null
      if (addBtn) addBtn.classList.add("is-on")
      box.focus()
    }
  }

  // Living situation is single-select; tapping "Other" reveals a free-text
  // detail box (about.livingSituationOther). Re-renders so the box shows/hides.
  pickLiving(event) {
    const val = event.currentTarget.dataset.fillValue
    const cur = String(this._getByPath("about.livingSituation") || "")
    const next = (cur === String(val)) ? "" : val
    this._setByPath("about.livingSituation", next)
    this._save("about.livingSituation")
    this._renderStep()
    renderIcons(this.element)
    if (next === "Other") {
      const inp = this.screenTarget.querySelector('[data-plan-field="about.livingSituation"]')
      if (inp) inp.focus()
    }
  }

  // Toggle the chip's label in the matching list. If a row with this
  // text already exists, clicking the (✓-marked) chip removes it; if
  // not, it appends a new {id, [textKey]: chip} row. Mirrors
  // editor_source/wizard.html#2773 handleChipClick (no-modal branch)
  // but with the "already selected" indicator we lacked.
  //
  // Special case: the School-detail step's level chips (Elementary /
  // Middle / High / College) open a nearby-schools picker instead of
  // adding the literal label, so the user can pick an actual school
  // near the participant's ZIP. Falls back to literal-add when the
  // picker can't resolve a location.
  addChip(event) {
    const btn = event.currentTarget
    const listKey = btn.dataset.listKey
    const chip    = btn.dataset.chip
    const textKey = btn.dataset.textKey || "text"
    if (!listKey || !chip) return
    if (listKey === "about.schoolDetails" && SCHOOL_LEVEL_CHIPS.has(chip)) {
      this._toggleSchoolPicker(btn, chip)
      return
    }
    // Family chips on "Who lives with me?" launch the focused 3-step
    // micro-flow (age → first name → last name) instead of dropping a
    // blank-but-roled row. Non-family chips (Pet dog/cat, Roommate,
    // Other) fall through to the legacy chip-append behavior.
    if (listKey === "about.whoLivesWithMe" && FAMILY_PERSON_CHIPS.has(chip)) {
      this._launchPersonFlow(chip, listKey)
      return
    }
    let arr = this._getByPath(listKey)
    if (!Array.isArray(arr)) {
      arr = []
      this._setByPath(listKey, arr)
    }
    const readText = (it) => (it && it[textKey] != null) ? it[textKey] : (it?.text || it?.description || "")
    // Per-path scaffold: "Who Lives With Me?" pre-fills the row in
    // "Name (Role) [Age]" shape so the user only has to fill in their
    // own bits. Other lists get the chip label verbatim — current
    // behavior preserved.
    const insertText = this._formatChipForList(listKey, chip)
    // Multi-add lists (about.whoLivesWithMe) allow more than one of
    // the same role — two brothers, two grandmas, etc. Each click
    // appends a fresh row; removal happens via the row's × button.
    // Toggle-remove behavior (the original UX) still applies to all
    // other dynamic-list chips so they stay deduped against re-adds.
    if (this._isMultiAddList(listKey)) {
      const newId = Date.now() + Math.random()
      arr.push({ id: newId, [textKey]: insertText })
      this._pendingFocusListRow = { listKey, id: newId, textKey }
    } else {
      const idx = arr.findIndex(it => {
        const t = readText(it)
        return t === chip || (typeof t === "string" && t.includes(`(${chip})`))
      })
      if (idx >= 0) {
        arr.splice(idx, 1)
      } else {
        const newId = Date.now() + Math.random()
        arr.push({ id: newId, [textKey]: insertText })
        this._pendingFocusListRow = { listKey, id: newId, textKey }
      }
    }
    this._save(listKey)
    this._updateNextTbdBtn()
    this._renderNav()
    this._renderStep()
    // After the editor pane re-renders from the plan change, drop the
    // caret right before the "(Role)" group so the user can start
    // typing the name immediately. Editor lives outside the wizard
    // controller's element, so we walk the document to find the
    // matching row by data-id.
    this._focusPendingListRow()
  }

  // -------- Focused "Add family member" micro-flow --------
  // Launched from addChip when the user taps a family chip on the
  // "Who lives with me?" list. Renders a single full-screen card
  // that walks them through age → first name → last name, then
  // commits one structured row to the list.
  _launchPersonFlow(chip, listKey) {
    _wizPersonFlow.active    = true
    _wizPersonFlow.chip      = chip
    _wizPersonFlow.listKey   = listKey
    // Only ask the age step for likely-minor relationships
    // (Brother / Sister / Stepbrother / Stepsister / Cousin). For
    // adults (Mom / Dad / Grandma / Spouse / Caregiver / …) skip
    // directly to the first-name step.
    _wizPersonFlow.isMinor   = FAMILY_MINOR_CHIPS.has(chip)
    _wizPersonFlow.step      = _wizPersonFlow.isMinor ? "age" : "first"
    _wizPersonFlow.age       = ""
    _wizPersonFlow.firstName = ""
    _wizPersonFlow.lastName  = ""
    this._renderStep()
  }

  // Collect surnames already in the plan so we can offer them as
  // chips on the last-name step ("Pick the same surname instead of
  // retyping"). Drawn from client.lastName + any existing person rows
  // on whoLivesWithMe that follow the "First Last (Role)" shape.
  _extractKnownSurnames() {
    const out = new Set()
    const cn = (this.plan?.client?.lastName || "").trim()
    if (cn) out.add(cn)
    const rows = (this.plan?.about?.whoLivesWithMe) || []
    rows.forEach(r => {
      const t = (r?.text || "").trim()
      const m = t.match(/^(\S[^\s(]*)\s+(\S[^\s(]*)\s*\(/) // "First Last (Role)"
      if (m) {
        const last = m[2]
        if (last && last !== "Age") out.add(last)
      }
    })
    return [...out]
  }

  _renderPersonFlow() {
    const f = _wizPersonFlow
    // Total step count + current step index depend on whether the
    // chosen relationship needs an age step. Adults skip it.
    const totalSteps = f.isMinor ? 3 : 2
    const stepIdx = f.isMinor
      ? ({ age: 1, first: 2, last: 3 })[f.step]
      : ({ first: 1, last: 2 })[f.step]
    let body = ""
    switch (f.step) {
      case "age":   body = this._renderPersonAgeStep(f);   break
      case "first": body = this._renderPersonFirstStep(f); break
      case "last":  body = this._renderPersonLastStep(f);  break
    }
    this.screenTarget.innerHTML = `
      <div class="q-person-focus" data-step="${esc(f.step)}">
        <div class="q-person-head">
          <span class="q-person-eyebrow">Adding family member · ${esc(f.chip)}</span>
          <button type="button" class="q-person-close"
                  data-action="wizard#cancelPersonFlow"
                  title="Cancel">&times;</button>
        </div>
        <div class="q-person-progress">
          <div class="q-person-progress-bar" style="width:${(stepIdx / totalSteps) * 100}%"></div>
        </div>
        <div class="q-person-progress-label">Step ${stepIdx} of ${totalSteps}</div>
        <div class="q-person-card" key="${esc(f.step)}">${body}</div>
      </div>`
    // Auto-focus the typed inputs on Step 2 / 3 so the user can
    // start typing immediately. Step 1 (age) skips auto-focus — the
    // chip cluster is the primary affordance and the optional exact
    // age input is secondary.
    if (f.step !== "age") {
      const input = this.screenTarget.querySelector(".q-person-input")
      input?.focus()
    }
    // Footer nav controls stay disabled while the flow owns the pane —
    // the flow has its own Back/Continue buttons.
    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = true
    if (this.hasNextBtnTarget) this.nextBtnTarget.disabled = true
  }

  _renderPersonAgeStep(f) {
    return `
      <h2 class="q-person-title">How old is your ${esc(f.chip.toLowerCase())}?</h2>
      <p class="q-person-sub">Tap the age — under 1, each year, or 18+.</p>
      <div class="q-person-chips q-person-chips-age">
        ${AGE_GROUPS.map(g => `
          <button type="button"
                  class="q-person-chip ${f.age === g ? "is-on" : ""}"
                  data-action="wizard#pickPersonAge"
                  data-age="${esc(g)}">${esc(g)}</button>
        `).join("")}
      </div>`
  }

  _renderPersonFirstStep(f) {
    const canNext = !!f.firstName.trim()
    // No age step → no Back button on Step 1 for adults (would lead nowhere).
    const backHtml = f.isMinor
      ? `<button type="button" class="q-person-back" data-action="wizard#personFlowBack">← Back</button>`
      : `<span></span>`
    return `
      <h2 class="q-person-title">${esc(f.chip)}'s first name?</h2>
      <p class="q-person-sub">Type their first name.</p>
      <input type="text" class="q-person-input"
             value="${esc(f.firstName)}"
             data-action="input->wizard#updatePersonField keydown->wizard#personFlowKey"
             data-field="firstName"
             placeholder="e.g. Alex">
      <div class="q-person-actions">
        ${backHtml}
        <button type="button" class="q-person-next"
                data-action="wizard#personFlowNext"
                ${canNext ? "" : "disabled"}>Continue →</button>
      </div>`
  }

  _renderPersonLastStep(f) {
    const surnames = this._extractKnownSurnames()
    const canSave  = !!f.lastName.trim()
    return `
      <h2 class="q-person-title">${esc(f.chip)}'s last name?</h2>
      <p class="q-person-sub">${surnames.length ? "Pick a known surname or type a new one." : "Type their last name."}</p>
      ${surnames.length ? `
        <div class="q-person-chips">
          ${surnames.map(sn => `
            <button type="button"
                    class="q-person-chip ${f.lastName === sn ? "is-on" : ""}"
                    data-action="wizard#pickPersonSurname"
                    data-surname="${esc(sn)}">${esc(sn)}</button>
          `).join("")}
        </div>` : ""}
      <input type="text" class="q-person-input"
             value="${esc(f.lastName)}"
             data-action="input->wizard#updatePersonField keydown->wizard#personFlowKey"
             data-field="lastName"
             placeholder="${surnames.length ? "Or type a different surname…" : "e.g. Smith"}">
      <div class="q-person-actions">
        <button type="button" class="q-person-back"
                data-action="wizard#personFlowBack">← Back</button>
        <button type="button" class="q-person-next q-person-save"
                data-action="wizard#finishPersonFlow"
                ${canSave ? "" : "disabled"}>Save family member</button>
      </div>`
  }

  pickPersonAge(event) {
    _wizPersonFlow.age = event.currentTarget.dataset.age
    _wizPersonFlow.step = "first"
    this._renderPersonFlow()
  }

  pickPersonSurname(event) {
    _wizPersonFlow.lastName = event.currentTarget.dataset.surname
    this._renderPersonFlow()
  }

  updatePersonField(event) {
    const field = event.currentTarget.dataset.field
    if (!field) return
    _wizPersonFlow[field] = event.currentTarget.value
    // Live-update the Continue/Save button enabled state without a
    // full re-render so the input keeps focus + caret position.
    const nextBtn = this.screenTarget.querySelector(".q-person-next")
    if (nextBtn) {
      const ok = !!event.currentTarget.value.trim()
      nextBtn.disabled = !ok
    }
  }

  personFlowKey(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      const nextBtn = this.screenTarget.querySelector(".q-person-next")
      if (nextBtn && !nextBtn.disabled) nextBtn.click()
    } else if (event.key === "Escape") {
      this.cancelPersonFlow(event)
    }
  }

  personFlowBack(event) {
    event.preventDefault()
    const f = _wizPersonFlow
    if (f.step === "last") {
      f.step = "first"
    } else if (f.step === "first" && f.isMinor) {
      // Adults have no age step to return to.
      f.step = "age"
    }
    this._renderPersonFlow()
  }

  personFlowNext(event) {
    event.preventDefault()
    if (_wizPersonFlow.step === "first")     _wizPersonFlow.step = "last"
    else if (_wizPersonFlow.step === "age")  _wizPersonFlow.step = "first"
    this._renderPersonFlow()
  }

  cancelPersonFlow(event) {
    event?.preventDefault?.()
    _wizPersonFlow.active = false
    this._renderStep()
  }

  finishPersonFlow(event) {
    event.preventDefault()
    const f = _wizPersonFlow
    if (!f.lastName.trim()) return
    const fullName = `${f.firstName.trim()} ${f.lastName.trim()}`.trim()
    // Issue #7: age sits to the LEFT of the relationship — "Name [Age]
    // (Role)". Name leads (natural scan order), age tucks in between
    // name and role so siblings still scan by age but row identity
    // reads as a name first.
    const ageLabel = f.isMinor ? f.age : ""
    const ageMid   = ageLabel ? ` [${ageLabel}]` : ""
    // Numeric years for single-year chips; null for "<1" / "18+" / adult.
    const numeric  = /^\d+$/.test(ageLabel) ? parseInt(ageLabel, 10) : null
    const newRow = {
      id: Date.now() + Math.random(),
      text: `${fullName}${ageMid} (${f.chip})`,
      firstName: f.firstName.trim(),
      lastName:  f.lastName.trim(),
      relationship: f.chip,
      ageGroup:  ageLabel,
      ageYears:  numeric
    }
    let arr = this._getByPath(f.listKey)
    if (!Array.isArray(arr)) {
      arr = []
      this._setByPath(f.listKey, arr)
    }
    arr.push(newRow)
    this._save(f.listKey)
    _wizPersonFlow.active = false
    this._renderNav()
    this._renderStep()
    this._updateNextTbdBtn()
  }
  // -------- end focused person flow --------

  // Map (listKey, chipLabel) → the text we put in the new row. The
  // only path with a scaffold today is about.whoLivesWithMe; every
  // other dynamicListInput caller still drops the chip label in raw.
  _formatChipForList(listKey, chip) {
    if (listKey !== "about.whoLivesWithMe") return chip
    // Issue #7 supersedes #1: age now sits to the LEFT of the
    // relationship in "Name [Age] (Role)" order — name leads, age
    // tucks in between name and role. Empty placeholders so the user
    // can type "Alex" → "Alex [8] (Brother)".
    const minorSibling = ["Brother", "Sister", "Stepbrother", "Stepsister"]
    if (minorSibling.includes(chip)) return ` [Age] (${chip})`
    return ` (${chip})`
  }

  // Lists where the same chip can be added more than once. Each chip
  // click on these paths appends a fresh row instead of toggling an
  // existing one off — so a user can have two brothers, two grandmas,
  // etc. The chip surface shows a "(N)" count badge in that case.
  _isMultiAddList(listKey) {
    return listKey === "about.whoLivesWithMe"
  }

  // Drop the caret at the start of the row's input on the editor pane
  // (the freshly-inserted row that addChip just pushed). The editor
  // lives outside this controller, so we walk the document. Bails
  // silently if the editor pane isn't on screen (standalone wizard
  // page).
  _focusPendingListRow() {
    const pending = this._pendingFocusListRow
    if (!pending) return
    this._pendingFocusListRow = null
    const start = Date.now()
    const tryFocus = () => {
      const host = document.querySelector(
        `.en-main [data-controller="dynamic-list"][data-dynamic-list-path-value="${pending.listKey}"]`
      )
      if (host) {
        const row = host.querySelector(`[data-row][data-id="${pending.id}"]`)
        const input = row?.querySelector(`[data-field="${pending.textKey}"]`)
                  || row?.querySelector('textarea, input[type="text"]')
        if (input) {
          input.focus()
          // Caret at position 0 so the user types the name before "(Role)".
          try { input.setSelectionRange(0, 0) } catch (_) {}
          return true
        }
      }
      return false
    }
    if (tryFocus()) return
    // The editor's dynamic-list controller re-renders on plan-store
    // change; that's async-ish under Stimulus. Retry briefly.
    const retry = () => {
      if (tryFocus()) return
      if (Date.now() - start > 800) return
      requestAnimationFrame(retry)
    }
    requestAnimationFrame(retry)
  }

  // Weekend "Same as weekday" link toggle. Stored as a meta flag rather
  // than a data copy — the linked bucket reads from its weekday twin at
  // render / preview time, so a single edit updates both views. Mirrors
  // editor_source/wizard.html#2104 data-day-link handler.
  toggleWeekendLink(event) {
    const btn = event.currentTarget
    const key = btn.dataset.weekendKey
    if (!key) return
    if (!this.plan.meta) this.plan.meta = {}
    if (!this.plan.meta.weekendSameAsWeekday) this.plan.meta.weekendSameAsWeekday = {}
    this.plan.meta.weekendSameAsWeekday[key] = !this.plan.meta.weekendSameAsWeekday[key]
    this._save(`meta.weekendSameAsWeekday.${key}`)
    this._renderStep()
  }

  // Open / close the inline nearby-schools panel under a school-level
  // chip. Clicking the same chip again closes the panel; clicking a
  // different level swaps the result list to that level.
  // Issue #6: clicking a school-level chip now launches a focused
  // micro-flow (Grade → School) that takes over the wizard pane,
  // matching the family flow's screen-takeover pattern. The legacy
  // inline picker is no longer used.
  _toggleSchoolPicker(chipBtn, level) {
    this._launchSchoolFlow(level)
  }

  _launchSchoolFlow(level) {
    _wizSchoolFlow.active   = true
    _wizSchoolFlow.level    = level
    _wizSchoolFlow.step     = "grade"
    _wizSchoolFlow.grade    = ""
    _wizSchoolFlow.school   = ""
    _wizSchoolFlow.nearby   = null
    _wizSchoolFlow.loadError = ""
    this._renderStep()
    // Kick off nearby-school lookup in the background so the chips
    // are ready by the time the user reaches Step 2.
    this._prefetchNearbySchools(level)
  }

  async _prefetchNearbySchools(level) {
    try {
      const latLon = await _resolveLatLon(this.plan)
      if (!latLon) {
        _wizSchoolFlow.nearby = []
        _wizSchoolFlow.loadError = "Add a ZIP / City on the Client step to see nearby schools."
        if (_wizSchoolFlow.active && _wizSchoolFlow.step === "school") this._renderStep()
        return
      }
      const all = await _fetchNearbySchools(latLon.lat, latLon.lon)
      _wizSchoolFlow.nearby = _filterSchoolsByLevel(all, level).slice(0, 12)
      if (_wizSchoolFlow.active && _wizSchoolFlow.step === "school") this._renderStep()
    } catch (e) {
      _wizSchoolFlow.nearby = []
      _wizSchoolFlow.loadError = "Couldn't load nearby schools — type the name instead."
      if (_wizSchoolFlow.active && _wizSchoolFlow.step === "school") this._renderStep()
    }
  }

  _renderSchoolFlow() {
    const f = _wizSchoolFlow
    const totalSteps = 2
    const stepIdx = ({ grade: 1, school: 2 })[f.step] || 1
    let body = ""
    switch (f.step) {
      case "grade":  body = this._renderSchoolGradeStep(f);  break
      case "school": body = this._renderSchoolNameStep(f);   break
    }
    this.screenTarget.innerHTML = `
      <div class="q-person-focus" data-step="${esc(f.step)}">
        <div class="q-person-head">
          <span class="q-person-eyebrow">Adding school · ${esc(f.level)}</span>
          <button type="button" class="q-person-close"
                  data-action="wizard#cancelSchoolFlow"
                  title="Cancel">&times;</button>
        </div>
        <div class="q-person-progress">
          <div class="q-person-progress-bar" style="width:${(stepIdx / totalSteps) * 100}%"></div>
        </div>
        <div class="q-person-progress-label">Step ${stepIdx} of ${totalSteps}</div>
        <div class="q-person-card" key="${esc(f.step)}">${body}</div>
      </div>`
    if (f.step === "school") {
      const input = this.screenTarget.querySelector(".q-person-input")
      input?.focus()
    }
    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = true
    if (this.hasNextBtnTarget) this.nextBtnTarget.disabled = true
  }

  _renderSchoolGradeStep(f) {
    const grades = GRADES_BY_LEVEL[f.level] || []
    return `
      <h2 class="q-person-title">What grade — ${esc(f.level.replace(/ school$/, "").toLowerCase())}?</h2>
      <p class="q-person-sub">Tap a grade to continue.</p>
      <div class="q-person-chips">
        ${grades.map(g => `
          <button type="button"
                  class="q-person-chip ${f.grade === g ? "is-on" : ""}"
                  data-action="wizard#pickSchoolGrade"
                  data-grade="${esc(g)}">${esc(g)}</button>
        `).join("")}
      </div>`
  }

  _renderSchoolNameStep(f) {
    const nearby   = Array.isArray(f.nearby) ? f.nearby : []
    const isLoading = f.nearby === null
    const canSave  = !!(f.school || "").trim()
    let pickerHtml = ""
    if (isLoading) {
      pickerHtml = `<p class="q-person-sub">Loading schools near you…</p>`
    } else if (f.loadError) {
      pickerHtml = `<p class="q-person-sub">${esc(f.loadError)}</p>`
    } else if (nearby.length) {
      pickerHtml = `
        <div class="q-person-chips">
          ${nearby.map(s => `
            <button type="button"
                    class="q-person-chip ${f.school === s.name ? "is-on" : ""}"
                    data-action="wizard#pickSchoolName"
                    data-school-name="${esc(s.name)}">
              ${esc(s.name)}${isFinite(s.dist) ? ` · ${(s.dist/1609.34).toFixed(1)} mi` : ""}
            </button>
          `).join("")}
        </div>`
    } else {
      pickerHtml = `<p class="q-person-sub">No nearby schools found — type the name below.</p>`
    }
    return `
      <h2 class="q-person-title">Which ${esc(f.level)}?</h2>
      <p class="q-person-sub">Pick a nearby school or type the name.</p>
      ${pickerHtml}
      <input type="text" class="q-person-input"
             value="${esc(f.school)}"
             data-action="input->wizard#updateSchoolField keydown->wizard#schoolFlowKey"
             placeholder="Or type a school name…">
      <div class="q-person-actions">
        <button type="button" class="q-person-back"
                data-action="wizard#schoolFlowBack">← Back</button>
        <button type="button" class="q-person-next q-person-save"
                data-action="wizard#finishSchoolFlow"
                ${canSave ? "" : "disabled"}>Save school</button>
      </div>`
  }

  pickSchoolGrade(event) {
    _wizSchoolFlow.grade = event.currentTarget.dataset.grade
    _wizSchoolFlow.step  = "school"
    this._renderStep()
  }

  pickSchoolName(event) {
    _wizSchoolFlow.school = event.currentTarget.dataset.schoolName
    this._renderStep()
  }

  updateSchoolField(event) {
    _wizSchoolFlow.school = event.currentTarget.value
    const nextBtn = this.screenTarget.querySelector(".q-person-save")
    if (nextBtn) nextBtn.disabled = !event.currentTarget.value.trim()
  }

  schoolFlowKey(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      const btn = this.screenTarget.querySelector(".q-person-save")
      if (btn && !btn.disabled) btn.click()
    } else if (event.key === "Escape") {
      this.cancelSchoolFlow(event)
    }
  }

  schoolFlowBack(event) {
    event.preventDefault()
    if (_wizSchoolFlow.step === "school") _wizSchoolFlow.step = "grade"
    this._renderStep()
  }

  cancelSchoolFlow(event) {
    event?.preventDefault?.()
    _wizSchoolFlow.active = false
    this._renderStep()
  }

  finishSchoolFlow(event) {
    event.preventDefault()
    const f = _wizSchoolFlow
    const name = (f.school || "").trim()
    if (!name) return
    // Issue #10: row text now reads "Name (Grade) — e.g.
    // "Lincoln Elementary (4th grade)" — instead of bare "Name, 4th".
    // College grades (Freshman / Sophomore / Junior / Senior / Grad)
    // don't get the " grade" suffix since they're already
    // self-describing.
    const isCollege = ["Freshman","Sophomore","Junior","Senior","Grad"].includes(f.grade)
    const gradeText = (!f.grade || f.grade === "N/A")
      ? ""
      : (isCollege ? f.grade : `${f.grade} grade`)
    const entry = gradeText ? `${name} (${gradeText})` : name
    const listKey = "about.schoolDetails"
    let arr = this._getByPath(listKey)
    if (!Array.isArray(arr)) { arr = []; this._setByPath(listKey, arr) }
    arr.push({
      id: Date.now() + Math.random(),
      text: entry,
      level: f.level,
      grade: f.grade,
      schoolName: name
    })
    this._save(listKey)
    _wizSchoolFlow.active = false
    this._renderNav()
    this._renderStep()
    this._updateNextTbdBtn()
  }

  async _loadNearbySchools(panel, level) {
    const latLon = await _resolveLatLon(this.plan)
    if (!latLon) {
      panel.innerHTML = `<div class="q-school-picker-status q-school-picker-empty">
        Enter the participant's <strong>ZIP code</strong> (or City + State) on the Client step to see nearby schools.
      </div>`
      return
    }
    let schools
    try {
      schools = await _fetchNearbySchools(latLon.lat, latLon.lon)
    } catch (e) {
      console.warn("[wizard/schools] Overpass error", e)
      panel.innerHTML = `<div class="q-school-picker-status q-school-picker-empty">
        Couldn't load nearby schools (offline?). Type the school name above.
      </div>`
      return
    }
    const where = this.plan?.client?.city || `(${latLon.lat.toFixed(2)}, ${latLon.lon.toFixed(2)})`
    if (!schools.length) {
      panel.innerHTML = `<div class="q-school-picker-status q-school-picker-empty">
        No schools found near <strong>${esc(where)}</strong> within 30km. Type the school name above.
      </div>`
      return
    }
    const filtered = _filterSchoolsByLevel(schools, level).slice(0, 12)
    // _filterSchoolsByLevel already falls back to the full list if no
    // entry matches the level filter, so this branch only fires when
    // the fetch above genuinely returned zero schools.
    if (!filtered.length) {
      panel.innerHTML = `<div class="q-school-picker-status q-school-picker-empty">
        No ${esc(level.toLowerCase())} matches near <strong>${esc(where)}</strong>. Type the school name above.
      </div>`
      return
    }
    // Pick the default grade from the level chip the user tapped so the
    // pill row already highlights a sensible option (Elementary → N/A,
    // not picked; user can refine). This matches the legacy modal's
    // "askGrade" behavior where grade was always offered for any school
    // chip but defaulted to N/A on the inputs.
    panel.innerHTML = `
      <div class="q-school-picker-head">${esc(level)} near ${esc(where)}</div>
      <div class="q-school-picker-grades" role="group" aria-label="Grade">
        <span class="q-school-picker-grades-label">Grade</span>
        ${SCHOOL_GRADES.map(g => `
          <button type="button"
                  class="q-school-grade-pill"
                  data-action="wizard#pickGrade"
                  data-grade="${esc(g)}">${esc(g)}</button>
        `).join("")}
      </div>
      <div class="q-school-picker-list">
        ${filtered.map(s => {
          const miles = isFinite(s.dist) ? (s.dist / 1609.34).toFixed(1) + " mi" : ""
          return `
            <button type="button"
                    class="q-school-picker-item"
                    data-action="wizard#pickSchool"
                    data-school-name="${esc(s.name)}">
              <span class="q-school-picker-name">${esc(s.name)}</span>
              ${miles ? `<span class="q-school-picker-dist">${miles}</span>` : ""}
            </button>`
        }).join("")}
      </div>`
  }

  // Toggle a grade pill in the school picker. Only one grade can be
  // selected at a time; tapping a selected pill clears it. The chosen
  // grade is stored on the panel's dataset so pickSchool can read it
  // when the user clicks a school entry.
  pickGrade(event) {
    const btn = event.currentTarget
    const grade = btn.dataset.grade
    const panel = btn.closest(".q-school-picker")
    if (!panel || !grade) return
    const current = panel.dataset.selectedGrade
    if (current === grade) {
      delete panel.dataset.selectedGrade
      btn.classList.remove("is-on")
      return
    }
    panel.dataset.selectedGrade = grade
    panel.querySelectorAll(".q-school-grade-pill.is-on").forEach(b => b.classList.remove("is-on"))
    btn.classList.add("is-on")
  }

  // Click a school in the picker → append it as a row on schoolDetails,
  // formatted "Lincoln Elementary, 4th" when a grade pill is selected.
  // Mirrors editor_source/wizard.html#5117 — `${name}, ${grade}` (grade
  // omitted when N/A or unset).
  pickSchool(event) {
    const btn = event.currentTarget
    const name = btn.dataset.schoolName
    if (!name) return
    const panel = btn.closest(".q-school-picker")
    const grade = panel?.dataset.selectedGrade
    // Issue #10: same "Name (Grade)" / "Name (4th grade)" format as
    // the focused school flow, so all entry paths look identical.
    const isCollege = ["Freshman","Sophomore","Junior","Senior","Grad"].includes(grade)
    const gradeText = (!grade || grade === "N/A")
      ? ""
      : (isCollege ? grade : `${grade} grade`)
    const entry = gradeText ? `${name} (${gradeText})` : name
    const listKey = "about.schoolDetails"
    let arr = this._getByPath(listKey)
    if (!Array.isArray(arr)) {
      arr = []
      this._setByPath(listKey, arr)
    }
    const dup = arr.some(it => ((it && it.text) || "").toLowerCase() === entry.toLowerCase())
    if (dup) {
      this._toast(`"${entry}" is already on the list`)
      return
    }
    arr.push({ id: Date.now() + Math.random(), text: entry })
    this._save(listKey)
    this._updateNextTbdBtn()
    this._renderNav()
    this._renderStep()
    this._toast(`Added ${entry}`)
  }

  // "+ Add" button next to the add-row textarea. Reads the textarea, pushes
  // a row, and clears the input. Mirrors editor_source/wizard.html#2685.
  addListItem(event) {
    const btn = event.currentTarget
    const listKey = btn.dataset.listKey
    const textKey = btn.dataset.textKey || "text"
    if (!listKey) return
    const input = this.screenTarget.querySelector(`[data-list-add-input="${CSS.escape(listKey)}"]`)
    const raw = (input?.value || "").trim()
    if (!raw) { input?.focus(); return }
    const value = this._resolveChipNumber(input, raw)
    let arr = this._getByPath(listKey)
    if (!Array.isArray(arr)) {
      arr = []
      this._setByPath(listKey, arr)
    }
    if (value !== raw && this._listHasText(arr, textKey, value)) { if (input) input.value = ""; return }
    arr.push({ id: Date.now() + Math.random(), [textKey]: value })
    this._save(listKey)
    this._updateNextTbdBtn()
    this._renderNav()
    this._renderStep()
  }

  // "+ TBD" button — adds an explicit TBD placeholder so it surfaces in
  // the Open Questions section for follow-up.
  addTbdItem(event) {
    const btn = event.currentTarget
    const listKey = btn.dataset.listKey
    const textKey = btn.dataset.textKey || "text"
    if (!listKey) return
    let arr = this._getByPath(listKey)
    if (!Array.isArray(arr)) {
      arr = []
      this._setByPath(listKey, arr)
    }
    arr.push({ id: Date.now() + Math.random(), [textKey]: "TBD" })
    this._save(listKey)
    this._updateNextTbdBtn()
    this._renderNav()
    this._renderStep()
  }

  // Smart Calendar: add a default block of the chosen category. Window
  // categories (school, sleep) use the category defaults; hours categories
  // (respite, ihss, pa, other) use the default hours + frequency.
  // The full time-picker modal lives in production but isn't ported yet —
  // the wizard's add action produces a usable default which the user can
  // tune in the editor.
  addCalItem(event) {
    const id = event.currentTarget.dataset.calCat
    const cat = CALENDAR_CATEGORIES.find(c => c.id === id)
    if (!cat) return
    if (!this.plan.smartCalendar) this.plan.smartCalendar = {}
    if (!Array.isArray(this.plan.smartCalendar.items)) this.plan.smartCalendar.items = []
    const base = { id: Date.now() + Math.random(), categoryId: cat.id, kind: cat.kind, label: cat.label }
    if (cat.kind === "window") {
      base.timeIn24    = cat.defaultStart
      base.timeOut24   = cat.defaultEnd
      base.daysPerWeek = cat.defaultDays
    } else {
      base.hours     = cat.defaultHours
      base.frequency = cat.defaultFreq
    }
    this.plan.smartCalendar.items.push(base)
    const newIdx = this.plan.smartCalendar.items.length - 1
    this._save("smartCalendar.items")
    this._updateNextTbdBtn()
    this._renderNav()
    this._renderStep()
    // Open the edit modal on the just-added item so the user lands in a
    // refine-the-defaults state immediately. Mirrors the legacy wizard's
    // "click category → modal" flow without losing the new quick-add
    // shortcut for power users (they can hit Cancel).
    this._openCalModal(newIdx)
  }

  // Open the edit modal for an existing calendar row. Mirrors the legacy
  // editor_source/wizard.html#1777 data-cal-edit handler — the row's
  // current state is loaded, the user edits, Save commits the snapshot.
  editCalItem(event) {
    const idx = parseInt(event.currentTarget.dataset.calIdx, 10)
    if (Number.isNaN(idx)) return
    this._openCalModal(idx)
  }

  // Build and mount the calendar item modal — covers both add (the
  // category quick-add lands here after seeding defaults) and edit.
  // Live computes weekly hours as the user types, including the PS
  // deduction for Sleep. Save writes the snapshot back to the array
  // at the given index, Cancel rolls back to the pre-modal state.
  _openCalModal(idx) {
    const items = this.plan.smartCalendar?.items || []
    const original = items[idx]
    if (!original) return
    const cat = CALENDAR_CATEGORIES.find(c => c.id === original.categoryId) || { icon: "⏱", label: "Item", kind: original.kind || "hours" }
    const isWindow = (original.kind || cat.kind) === "window"
    const isSleep  = original.categoryId === "sleep"

    // Snapshot of the original so Cancel can restore it (the modal mutates
    // items[idx] live so the preview math always reflects the current state).
    const snapshotOriginal = JSON.parse(JSON.stringify(original))

    document.getElementById("wizard-cal-modal")?.remove()
    const modal = document.createElement("div")
    modal.id = "wizard-cal-modal"
    modal.className = "q-cal-modal-overlay"
    modal.innerHTML = `
      <div class="q-cal-modal" role="dialog" aria-modal="true" aria-labelledby="q-cal-modal-title">
        <h3 id="q-cal-modal-title" class="q-cal-modal-title">
          <span>${cat.icon}</span> Edit ${esc(cat.label)}
        </h3>

        <div class="q-cal-modal-body">
          <label class="q-cal-modal-label">Label</label>
          <input type="text" class="q-cal-modal-input" data-cal-field="label"
                 value="${esc(original.label || cat.label)}">

          ${isWindow ? `
            <div class="q-cal-modal-row">
              <div>
                <label class="q-cal-modal-label">Start (24h)</label>
                <input type="text" class="q-cal-modal-input" data-cal-field="timeIn24"
                       placeholder="07:30" value="${esc(original.timeIn24 || "")}">
              </div>
              <div>
                <label class="q-cal-modal-label">End (24h)</label>
                <input type="text" class="q-cal-modal-input" data-cal-field="timeOut24"
                       placeholder="15:00" value="${esc(original.timeOut24 || "")}">
              </div>
              <div>
                <label class="q-cal-modal-label">Days / week</label>
                <input type="number" min="0" max="7" step="1" class="q-cal-modal-input" data-cal-field="daysPerWeek"
                       value="${esc(original.daysPerWeek != null ? original.daysPerWeek : 0)}">
              </div>
            </div>
          ` : `
            <div class="q-cal-modal-row">
              <div>
                <label class="q-cal-modal-label">Hours</label>
                <input type="number" min="0" step="0.25" class="q-cal-modal-input" data-cal-field="hours"
                       value="${esc(original.hours != null ? original.hours : 0)}">
              </div>
              <div>
                <label class="q-cal-modal-label">Frequency</label>
                <select class="q-cal-modal-input" data-cal-field="frequency">
                  ${["weekly","monthly","quarterly","annually"].map(f => `
                    <option value="${f}"${(original.frequency || "weekly") === f ? " selected" : ""}>${f[0].toUpperCase()}${f.slice(1)}</option>`).join("")}
                </select>
              </div>
            </div>
          `}

          ${isSleep ? `
            <div class="q-cal-modal-extras">
              <div class="q-cal-modal-extra">
                <div class="q-cal-modal-extra-q">Needs protective supervision at night?</div>
                <div class="q-cal-modal-extra-btns">
                  <button type="button" class="q-cal-extra-btn q-cal-extra-btn--yes${original.protectiveSupervision === true ? " is-on" : ""}"
                          data-cal-bool="protectiveSupervision" data-bool-val="yes">✓ Yes</button>
                  <button type="button" class="q-cal-extra-btn q-cal-extra-btn--no${original.protectiveSupervision === false ? " is-on" : ""}"
                          data-cal-bool="protectiveSupervision" data-bool-val="no">✗ No</button>
                </div>
                <div class="q-cal-modal-extra-help" data-ps-help>
                  ${original.protectiveSupervision === true ? `Those hours are deducted from Sleep since they're providing PS, not resting.`
                    : original.protectiveSupervision === false ? `Sleeps safely without overnight monitoring.`
                    : ``}
                </div>
                <div class="q-cal-modal-extra-nested" data-ps-when-yes
                     style="${original.protectiveSupervision === true ? "" : "display:none;"}">
                  <label class="q-cal-modal-label">PS hours per week (deducted from Sleep)</label>
                  <input type="number" min="0" max="168" step="0.25" class="q-cal-modal-input"
                         data-cal-field="psHours" placeholder="e.g. 28"
                         value="${esc(original.psHours != null ? original.psHours : "")}">
                </div>
              </div>
              <div class="q-cal-modal-extra">
                <label class="q-cal-modal-label">Notes (elopement risk, sleep disturbances)</label>
                <textarea rows="2" class="q-cal-modal-input" data-cal-field="notes"
                          placeholder="e.g. Elopes if door not chimed; wakes 2–3 times">${esc(original.notes || "")}</textarea>
              </div>
            </div>
          ` : ""}

          <div class="q-cal-modal-preview">
            <span class="q-cal-modal-preview-label">Computed weekly hours:</span>
            <span class="q-cal-modal-preview-value" data-cal-weekly>${formatHrs(weeklyHoursForCalItem(original))}</span>
          </div>
        </div>

        <div class="q-cal-modal-foot">
          <button type="button" class="q-cal-modal-cancel" data-cal-modal-action="cancel">Cancel</button>
          <button type="button" class="q-cal-modal-save"   data-cal-modal-action="save">Save</button>
        </div>
      </div>`
    document.body.appendChild(modal)
    document.body.style.overflow = "hidden"

    // Live-mutate items[idx] as the user types so the preview math is
    // always current. On Cancel we restore from snapshotOriginal.
    const recompute = () => {
      const wkEl = modal.querySelector("[data-cal-weekly]")
      if (wkEl) wkEl.textContent = formatHrs(weeklyHoursForCalItem(items[idx]))
    }
    modal.querySelectorAll("[data-cal-field]").forEach(el => {
      el.addEventListener("input", () => {
        const field = el.dataset.calField
        let val = el.value
        if (el.type === "number") val = el.value === "" ? "" : parseFloat(el.value)
        items[idx][field] = val
        recompute()
      })
    })
    modal.querySelectorAll("[data-cal-bool]").forEach(btn => {
      btn.addEventListener("click", () => {
        const field = btn.dataset.calBool
        const yes = btn.dataset.boolVal === "yes"
        items[idx][field] = yes
        // Refresh the two-button group's on-state.
        modal.querySelectorAll(`[data-cal-bool="${field}"]`).forEach(b => {
          b.classList.toggle("is-on", b === btn)
        })
        // Show / hide the nested PS-hours input when this is the PS toggle.
        if (field === "protectiveSupervision") {
          const nested = modal.querySelector("[data-ps-when-yes]")
          if (nested) nested.style.display = yes ? "" : "none"
          const help = modal.querySelector("[data-ps-help]")
          if (help) {
            help.textContent = yes
              ? "Those hours are deducted from Sleep since they're providing PS, not resting."
              : "Sleeps safely without overnight monitoring."
          }
        }
        recompute()
      })
    })

    const close = () => {
      document.body.style.overflow = ""
      modal.remove()
      document.removeEventListener("keydown", escListener)
    }
    const escListener = (e) => { if (e.key === "Escape") cancel() }
    const cancel = () => {
      items[idx] = snapshotOriginal
      close()
      this._save("smartCalendar.items")
      this._renderStep()
    }
    const save = () => {
      close()
      this._save("smartCalendar.items")
      this._updateNextTbdBtn()
      this._renderNav()
      this._renderStep()
    }

    modal.querySelector('[data-cal-modal-action="cancel"]').addEventListener("click", cancel)
    modal.querySelector('[data-cal-modal-action="save"]').addEventListener("click", save)
    modal.addEventListener("click", e => { if (e.target === modal) cancel() })
    document.addEventListener("keydown", escListener)
  }

  removeCalItem(event) {
    const idx = parseInt(event.currentTarget.dataset.calIdx, 10)
    const items = this.plan.smartCalendar?.items
    if (!Array.isArray(items) || idx < 0 || idx >= items.length) return
    items.splice(idx, 1)
    this._save("smartCalendar.items")
    this._updateNextTbdBtn()
    this._renderNav()
    this._renderStep()
  }

  // Legacy modal-trigger kept for compatibility — wired by the
  // editor pane's edit-row button (editCircleEntry) and any callers
  // outside the wizard pane. The wizard pane itself now uses the
  // inline accordion below (toggleCircleCat / submitCircleInline).
  addCircleEntry(event) {
    const cat = event.currentTarget.dataset.cosCat
    if (!cat) return
    this._openCircleModal(cat, (display, category) => {
      if (!display) return
      this._appendCircleEntry(display, category)
    })
  }

  // Inline accordion handlers --------------------------------------
  // Expand one category at a time so the wizard pane doesn't grow
  // taller than the viewport. Clicking the already-open category
  // collapses it. Draft state lives on the controller instance so
  // the user's in-flight chip picks survive the re-render.
  // Wired to the "Show all" button on the sub-focus pill in the
  // wizard pane — drops the focused sub-block so the renderer goes
  // back to showing every block in the current section.
  clearSubFocus() {
    _wizSubFocus.section  = null
    _wizSubFocus.path     = null
    _wizSubFocus.category = null
    _wizSubFocus.rowId    = null
    this._renderStep()
  }

  // Service Calendar palette in the wizard pane: click a chip → this
  // service becomes the "active brush" for the editor's grid. The
  // editor's resource_calendar_controller listens for the
  // `rc:service-select` event and stores the chosen service so that
  // a click + drag on the grid paints with it. Clicking the same chip
  // again deselects (mode: nothing).
  selectRcService(event) {
    event?.preventDefault?.()
    const svc = event.currentTarget?.dataset?.rcSvc
    if (!svc) return
    _wizRcUi.active = _wizRcUi.active === svc ? null : svc
    this._renderStep()
    document.dispatchEvent(new CustomEvent("rc:service-select", {
      bubbles: true,
      detail: { service: _wizRcUi.active }
    }))
  }

  // "Auto" button on the school legend chip — reads the school Start/End
  // times from A Day in the Life (plan.dayInLife.day) and asks the editor's
  // resource_calendar_controller to paint SCH blocks onto the grid for each
  // school day. Parsing lives here because the wizard already owns the
  // school-schedule parser + the school name; the calendar controller owns
  // the slot format, so we hand it ready-made day/time blocks via an event.
  autoAddSchoolCalendar(event) {
    event.preventDefault()
    event.stopPropagation()
    const schoolName = rcSchoolName(this.plan)
    const parsed = parseSchoolSchedule((this.plan.dayInLife || {}).day)
    // Snap a "HH:MM" time to the grid's half-hour resolution as a float
    // hour (e.g. "14:30" → 14.5, "08:15" → 8.0).
    const toHalfHour = (t24) => {
      if (!t24) return null
      const [h, m] = String(t24).split(":").map(Number)
      if (isNaN(h)) return null
      return h + Math.round((m || 0) / 30) * 0.5
    }
    const perDay = {}
    parsed.forEach(e => {
      const hr = toHalfHour(e.time24)
      if (hr == null) return
      ;(e.days || []).forEach(d => {
        if (!WEEKDAYS.includes(d)) return
        if (!perDay[d]) perDay[d] = {}
        perDay[d][e.kind] = hr
      })
    })
    const blocks = []
    Object.entries(perDay).forEach(([dayName, v]) => {
      if (v.start == null || v.end == null || v.end <= v.start) return
      blocks.push({ dayName, startHour: v.start, endHour: v.end })
    })
    if (!blocks.length) {
      window.alert("No school Start/End times found in “A Day in the Life.” Add the school schedule there first, then try Auto again.")
      return
    }
    document.dispatchEvent(new CustomEvent("rc:auto-add-school", {
      bubbles: true,
      detail: { service: schoolName, blocks }
    }))
  }

  // Legend abbreviation editing. The pencil on a service chip enters edit
  // mode (swaps the abbr for an inline input); Enter/blur commits, Escape
  // cancels. The override is stored per-plan on
  // resourceCalendar.abbrev[serviceText] and read back by rcAbbrev (here)
  // and resource_calendar_controller#_abbrev (the grid), so the legend,
  // the grid cells, and the picker all show the custom abbreviation.
  editRcAbbrev(event) {
    event.preventDefault()
    event.stopPropagation()   // don't also select the service as the brush
    const svc = event.currentTarget?.dataset?.rcSvc
    if (!svc) return
    _wizRcUi.editing = svc
    this._renderStep()
    // Focus + select the fresh input so the user can type immediately.
    requestAnimationFrame(() => {
      const inp = this.screenTarget.querySelector(".q-rc-svc-abbrinput")
      if (inp) { inp.focus(); inp.select() }
    })
  }

  abbrevKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      event.currentTarget.blur()          // commit via the blur handler
    } else if (event.key === "Escape") {
      event.preventDefault()
      _wizRcUi.editing = null
      this._renderStep()
    }
  }

  commitRcAbbrev(event) {
    const inp = event.currentTarget
    const svc = inp?.dataset?.rcSvc
    // Guard against a stray blur firing after Escape already cleared editing.
    if (svc == null || _wizRcUi.editing == null) return
    const val = (inp.value || "").trim().slice(0, 6)
    if (!this.plan.resourceCalendar) this.plan.resourceCalendar = { enabled: true, slots: [], notes: "" }
    if (!this.plan.resourceCalendar.abbrev || typeof this.plan.resourceCalendar.abbrev !== "object") {
      this.plan.resourceCalendar.abbrev = {}
    }
    // Store "" to fall back to the default — a delete wouldn't survive the
    // savePlan deep-merge (merge only adds/updates keys, never removes).
    this.plan.resourceCalendar.abbrev[svc] = val
    _wizRcUi.editing = null
    this._save("resourceCalendar.abbrev")
    this._renderStep()
  }

  // School Time input handler. Reads every visible <input type="time">
  // in the School card and writes a fresh set of School Start / End
  // entries on plan.dayInLife.day. In "Same every weekday" mode the
  // two top-level inputs map to a single Start + single End entry
  // covering Mon–Fri (no "on …" suffix, so legacy .sdp data
  // round-trips). In "Different per day" mode each weekday row
  // produces its own pair, and writeSchoolSchedule merges any
  // entries that share kind + time into one "on Mon, Wed, Fri"
  // line.
  updateSchoolTime(event) {
    const card = event.currentTarget.closest(".q-day-school")
    if (!card) return
    const mode = card.dataset.schoolMode || "same"
    if (!this.plan.dayInLife) this.plan.dayInLife = {}
    if (!Array.isArray(this.plan.dayInLife.day)) this.plan.dayInLife.day = []
    const entries = []
    if (mode === "same") {
      const t1 = card.querySelector('[data-school-time="in"]:not([data-school-day])')?.value || ""
      const t2 = card.querySelector('[data-school-time="out"]:not([data-school-day])')?.value || ""
      if (t1) entries.push({ kind: "start", time12: format12h(t1), days: [...WEEKDAYS] })
      if (t2) entries.push({ kind: "end",   time12: format12h(t2), days: [...WEEKDAYS] })
    } else {
      WEEKDAYS.forEach(day => {
        const t1 = card.querySelector(`[data-school-day="${day}"][data-school-time="in"]`)?.value || ""
        const t2 = card.querySelector(`[data-school-day="${day}"][data-school-time="out"]`)?.value || ""
        if (t1) entries.push({ kind: "start", time12: format12h(t1), days: [day] })
        if (t2) entries.push({ kind: "end",   time12: format12h(t2), days: [day] })
      })
    }
    writeSchoolSchedule(this.plan.dayInLife.day, entries)
    this._save("dayInLife.day")
  }

  // Flip the same/different toggle on the School Time card. Persists
  // the preference on plan.meta.schoolSameAllWeek so the next render
  // remembers it, and re-renders so the body swaps form layout.
  setSchoolMode(event) {
    event.preventDefault()
    const mode = event.currentTarget.dataset.schoolMode
    if (!this.plan.meta) this.plan.meta = {}
    this.plan.meta.schoolSameAllWeek = (mode !== "different")
    this._save("meta.schoolSameAllWeek")
    this._renderStep()
  }

  // Remove a single day-in-life entry by index. Re-renders the step
  // so the chip area refreshes.
  removeDayItem(event) {
    event.preventDefault()
    const field = event.currentTarget.dataset.dayField
    const idx   = parseInt(event.currentTarget.dataset.dayIdx, 10)
    if (!field) return
    const arr = this.plan.dayInLife?.[field]
    if (!Array.isArray(arr) || idx < 0 || idx >= arr.length) return
    arr.splice(idx, 1)
    this._save(`dayInLife.${field}`)
    this._renderStep()
  }

  // Opens the day-builder modal for a (bucket, category) pair.
  // Mirrors editor_source/wizard.html#3223 showDayBuilderModal:
  //   - time field (free text, defaults to category.defaultTime)
  //   - one checkbox per activity
  //   - Indep / Needs help toggle per checked activity (or Yes / No
  //     for activities listed in category.yesNo)
  //   - pickOnly categories skip the per-activity toggle and emit one
  //     entry per checked activity
  // Saves into plan.dayInLife[field] using the legacy serialisation
  // (`[time] <act>(0)|<act>(1)`) so .sdp files round-trip.
  openDayBuilder(event) {
    event.preventDefault()
    const field = event.currentTarget.dataset.dayField
    const catId = event.currentTarget.dataset.dayCat
    const cat   = (DAY_CATEGORIES[field] || []).find(c => c.id === catId)
    if (!cat) return
    document.getElementById("wizard-day-modal")?.remove()
    const isPickOnly = !!cat.pickOnly
    const yesNoSet   = new Set((cat.yesNo || []).map(s => s.toLowerCase()))
    const isYesNo    = (a) => yesNoSet.has(a.toLowerCase())
    const support    = {}   // per-activity-index → mark
    const modal = document.createElement("div")
    modal.id = "wizard-day-modal"
    modal.className = "q-day-modal-overlay"
    modal.innerHTML = `
      <div class="q-day-modal" role="dialog" aria-modal="true" aria-labelledby="q-day-modal-title">
        <h3 id="q-day-modal-title" class="q-day-modal-title">
          <span class="q-day-modal-ico">${cat.icon}</span> ${esc(cat.name)}
        </h3>
        <p class="q-day-modal-sub">${isPickOnly
          ? "Pick a time and one or more options."
          : "Pick a time, then check each activity and mark whether they do it independently or need help."}</p>
        <div class="q-day-modal-row">
          <label class="q-day-modal-label">Time</label>
          <input type="text" class="q-day-modal-input" data-day-modal-time
                 value="${esc(cat.defaultTime || "")}" placeholder="e.g. 7:00 AM">
        </div>
        <div class="q-day-modal-row">
          <label class="q-day-modal-label">Activities</label>
          <div class="q-day-modal-acts">
            ${cat.activities.map((a, i) => {
              const yn = isYesNo(a)
              const toggleHtml = isPickOnly ? "" : (yn ? `
                  <span class="q-day-modal-toggle" data-day-toggle="${i}" hidden>
                    <button type="button" class="q-day-modal-tbtn is-yes"   data-day-sup="${i}" data-day-mark="Y">Yes</button>
                    <button type="button" class="q-day-modal-tbtn is-no"    data-day-sup="${i}" data-day-mark="N">No</button>
                  </span>` : `
                  <span class="q-day-modal-toggle" data-day-toggle="${i}" hidden>
                    <button type="button" class="q-day-modal-tbtn is-indep" data-day-sup="${i}" data-day-mark="0">Independently</button>
                    <button type="button" class="q-day-modal-tbtn is-help"  data-day-sup="${i}" data-day-mark="1">Needs help</button>
                  </span>`)
              return `
                <label class="q-day-modal-act">
                  <input type="checkbox" data-day-act="${i}">
                  <span class="q-day-modal-act-name">${esc(a)}</span>
                  ${toggleHtml}
                </label>`
            }).join("")}
          </div>
        </div>
        <div class="q-day-modal-foot">
          <button type="button" class="q-day-modal-cancel" data-day-modal-action="cancel">Cancel</button>
          <button type="button" class="q-day-modal-save"   data-day-modal-action="save">Add</button>
        </div>
      </div>`
    document.body.appendChild(modal)
    document.body.style.overflow = "hidden"
    const close = () => {
      document.body.style.overflow = ""
      modal.remove()
      document.removeEventListener("keydown", onKey)
    }
    const onKey = (e) => { if (e.key === "Escape") close() }
    document.addEventListener("keydown", onKey)
    modal.addEventListener("click", (e) => { if (e.target === modal) close() })
    // Reveal the per-activity toggle when its checkbox flips on, and
    // pre-pick a default support state so saving without clicking
    // doesn't push an unflagged entry.
    modal.querySelectorAll("[data-day-act]").forEach(cb => {
      cb.addEventListener("change", () => {
        const i = parseInt(cb.dataset.dayAct, 10)
        const tog = modal.querySelector(`[data-day-toggle="${i}"]`)
        if (tog) tog.hidden = !cb.checked
        if (cb.checked && !isPickOnly && !support[i]) {
          const a = cat.activities[i]
          support[i] = isYesNo(a) ? "Y" : "0"
          paintToggle(i)
        }
        if (!cb.checked) delete support[i]
      })
    })
    const paintToggle = (i) => {
      modal.querySelectorAll(`[data-day-sup="${i}"]`).forEach(btn => {
        btn.classList.toggle("is-on", btn.dataset.dayMark === support[i])
      })
    }
    modal.querySelectorAll("[data-day-sup]").forEach(btn => {
      btn.addEventListener("click", (e) => {
        e.preventDefault()
        const i = parseInt(btn.dataset.daySup, 10)
        support[i] = btn.dataset.dayMark
        paintToggle(i)
      })
    })
    modal.querySelector("[data-day-modal-action=cancel]").addEventListener("click", close)
    modal.querySelector("[data-day-modal-action=save]").addEventListener("click", () => {
      const time = (modal.querySelector("[data-day-modal-time]")?.value || "").trim()
      const checked = []
      cat.activities.forEach((a, i) => {
        const cb = modal.querySelector(`[data-day-act="${i}"]`)
        if (cb && cb.checked) checked.push(i)
      })
      if (!checked.length) { close(); return }
      const timeChip = time ? `[${time}] ` : ""
      const arr = (this.plan.dayInLife ||= {})[field] ||= []
      if (isPickOnly) {
        checked.forEach(i => {
          arr.push({ id: Date.now() + Math.random(), text: `${timeChip}<${cat.activities[i]}>` })
        })
      } else {
        const chunks = checked.map(i => {
          const a = cat.activities[i]
          const mark = support[i] || (isYesNo(a) ? "Y" : "0")
          return `<${a}>(${mark})`
        })
        arr.push({ id: Date.now() + Math.random(), text: timeChip + chunks.join(" | ") })
      }
      this._save(`dayInLife.${field}`)
      close()
      this._renderStep()
    })
  }

  toggleRcMonthly(event) {
    event?.preventDefault?.()
    _wizRcUi.monthly = !_wizRcUi.monthly
    this._renderStep()
  }

  toggleCircleCat(event) {
    const cat = event.currentTarget.dataset.cosCat
    if (!cat) return
    _wizCosUi.expand = (_wizCosUi.expand === cat) ? null : cat
    _wizCosUi.more   = null
    this._captureCircleDrafts()
    this._renderStep()
  }

  toggleCircleMore(event) {
    const cat = event.currentTarget.dataset.cosCat
    if (!cat) return
    _wizCosUi.more = (_wizCosUi.more === cat) ? null : cat
    this._captureCircleDrafts()
    this._renderStep()
  }

  pickCircleChip(event) {
    const btn = event.currentTarget
    const cat = btn.dataset.cosCat
    const fid = btn.dataset.cosFid
    const val = btn.dataset.cosChip
    if (!cat || !fid || val == null) return
    // "Not yet" is a one-click marker: record that no one in this category has
    // been identified yet, rather than setting a role and waiting for a name.
    if (val === "Not yet") {
      const list = (this.plan.about && this.plan.about.circleOfSupport) || []
      const already = list.some(p => p && p.relationship === cat && /^\s*not yet\b/i.test(String(p.name || "")))
      if (!already) this._appendCircleEntry("Not yet", cat)
      return
    }
    this._captureCircleDrafts()
    if (!_wizCosUi.drafts[cat]) _wizCosUi.drafts[cat] = {}
    _wizCosUi.drafts[cat][fid] = val
    this._renderStep()
    // Focus the Name field so the user can keep typing after picking a chip.
    requestAnimationFrame(() => {
      const nameEl = this.screenTarget.querySelector(
        `[data-wiz-cos-name][data-cos-cat="${cat}"]`
      )
      if (nameEl) { nameEl.focus(); nameEl.select?.() }
    })
  }

  toggleCircleUnder18(event) {
    const cat = event.currentTarget.dataset.cosCat
    if (cat !== "Family") return
    this._captureCircleDrafts()
    if (!_wizCosUi.drafts.Family) _wizCosUi.drafts.Family = {}
    _wizCosUi.drafts.Family.under18 = !!event.currentTarget.checked
    if (!_wizCosUi.drafts.Family.under18) _wizCosUi.drafts.Family.age = ""
    this._renderStep()
  }

  // "Family who lives outside the home" — routes the add: unchecked (in home)
  // → Who Lives With Me; checked → Circle of Support "Family (not in home)".
  toggleCircleNotInHome(event) {
    const cat = event.currentTarget.dataset.cosCat
    if (cat !== "Family") return
    this._captureCircleDrafts()
    if (!_wizCosUi.drafts.Family) _wizCosUi.drafts.Family = {}
    _wizCosUi.drafts.Family.notInHome = !!event.currentTarget.checked
    this._renderStep()
  }

  submitCircleInline(event) {
    const btn = event.currentTarget
    const cat = btn.dataset.cosCat
    const skip = btn.dataset.cosSkip === "1"
    if (!cat) return
    this._captureCircleDrafts()
    const draft = _wizCosUi.drafts[cat] || {}
    const fid   = cat === "Family" ? "relationship" : "role"
    let role = (draft[fid] || "").trim()
    let name = (draft.fullname || "").trim()
    if (skip) {
      if (!role) role = "TBD"
      if (!name) name = "TBD"
    } else {
      if (!role || !name) {
        this._toast(`${cat}: ${role ? "Name" : (cat === "Family" ? "Relationship" : "Role")} is required`)
        return
      }
    }
    let ageSuffix = ""
    if (cat === "Family" && draft.under18) {
      const age = (draft.age || "").toString().trim()
      if (!skip && !age) { this._toast("Family: age required when Under 18 is checked"); return }
      if (age) ageSuffix = `, age ${age}`
    }
    if (cat === "Family" && !draft.notInHome) {
      // Family who lives IN the home → the Who Lives With Me list.
      this._appendHouseholdEntry(name, role, draft.under18 ? (draft.age || "").toString().trim() : "")
    } else {
      const display = cat === "Family"
        ? `${name} (${role}${ageSuffix})`
        : `${name} (${role})`
      this._appendCircleEntry(display, cat)
    }
    // Reset just this category's draft + collapse other categories'
    // "more roles" expander so the next add starts clean.
    _wizCosUi.drafts[cat] = {}
    _wizCosUi.more = null
    this._renderStep()
  }

  // Read every input inside the currently-expanded accordion body
  // back into the draft cache before we re-render. Stimulus replaces
  // the screen target's innerHTML on every render, so any value the
  // user typed since the last paint would otherwise be lost.
  _captureCircleDrafts() {
    const scope = this.screenTarget
    if (!scope) return
    scope.querySelectorAll("[data-wiz-cos-role]").forEach(el => {
      const c = el.dataset.cosCat
      if (!_wizCosUi.drafts[c]) _wizCosUi.drafts[c] = {}
      const fid = c === "Family" ? "relationship" : "role"
      _wizCosUi.drafts[c][fid] = el.value
    })
    scope.querySelectorAll("[data-wiz-cos-name]").forEach(el => {
      const c = el.dataset.cosCat
      if (!_wizCosUi.drafts[c]) _wizCosUi.drafts[c] = {}
      _wizCosUi.drafts[c].fullname = el.value
    })
    scope.querySelectorAll("[data-wiz-cos-age]").forEach(el => {
      const c = el.dataset.cosCat
      if (!_wizCosUi.drafts[c]) _wizCosUi.drafts[c] = {}
      _wizCosUi.drafts[c].age = el.value
    })
  }

  _appendCircleEntry(display, category) {
    if (!this.plan.about) this.plan.about = {}
    if (!Array.isArray(this.plan.about.circleOfSupport)) this.plan.about.circleOfSupport = []
    this.plan.about.circleOfSupport.push({
      id: Date.now() + Math.random(),
      name: display,
      relationship: category
    })
    this._save("about.circleOfSupport")
    this._updateNextTbdBtn()
    this._renderNav()
    this._renderStep()
  }

  // Add a family member who lives IN the home to Who Lives With Me. Matches the
  // household row shape written by finishPersonFlow (text + firstName/lastName +
  // relationship + age) so the WLM list, the household→circle mirror, and the
  // PDF all render it. _save runs syncWhoLivesIntoCircle for the mirror row.
  _appendHouseholdEntry(name, role, age) {
    if (!this.plan.about) this.plan.about = {}
    if (!Array.isArray(this.plan.about.whoLivesWithMe)) this.plan.about.whoLivesWithMe = []
    const parts = name.trim().split(/\s+/)
    const firstName = parts.shift() || ""
    const lastName  = parts.join(" ")
    const ageStr = (age || "").toString().trim()
    const ageMid = ageStr ? ` [${ageStr}]` : ""
    const numeric = /^\d+$/.test(ageStr) ? parseInt(ageStr, 10) : null
    this.plan.about.whoLivesWithMe.push({
      id: Date.now() + Math.random(),
      text: `${name.trim()}${ageMid} (${role})`,
      firstName,
      lastName,
      relationship: role,
      ageGroup: ageStr,
      ageYears: numeric
    })
    this._save("about.whoLivesWithMe")
    this._updateNextTbdBtn()
    this._renderNav()
    this._renderStep()
  }

  editCircleEntry(event) {
    const rowId = parseFloat(event.currentTarget.dataset.rowId)
    const list  = (this.plan.about && this.plan.about.circleOfSupport) || []
    const entry = list.find(p => p.id === rowId)
    if (!entry) return
    const category = entry.relationship || "Family"
    const prefill  = parseCircleEntry(entry.name, category)
    this._openCircleModal(category, (display) => {
      if (!display) return
      entry.name = display
      this._save("about.circleOfSupport")
      this._updateNextTbdBtn()
      this._renderNav()
      this._renderStep()
    }, { prefill })
  }

  // ✎ on any wizard list row → open a structured, multi-field editor for that
  // exact entry (household = Name/Relationship/Age, school = School/Type/Grade,
  // barrier = Barrier/Strategy). Lists with no bespoke schema get one roomy
  // field so every entry is still cleanly editable. See ENTRY_EDIT_SCHEMAS.
  editEntry(event) {
    event.preventDefault()
    const btn     = event.currentTarget
    const path    = btn.dataset.listPath
    const rowId   = btn.dataset.rowId
    const textKey = btn.dataset.textKey || "text"
    if (!path || rowId == null) return
    this._openStructuredEditModal(path, rowId, textKey)
  }

  _schemaForPath(path, textKey) {
    if (ENTRY_EDIT_SCHEMAS[path]) return ENTRY_EDIT_SCHEMAS[path]
    const key = textKey || "text"
    return {
      title: "Edit",
      fields: [{ key, label: "Details", placeholder: "", required: true, type: "textarea" }],
      parse:    (it) => ({ [key]: (it && (it[key] != null ? it[key] : it.text)) || "" }),
      assemble: (v)  => ({ [key]: (v[key] || "").trim() })
    }
  }

  _openStructuredEditModal(path, rowId, textKey) {
    const arr = this._getByPath(path)
    if (!Array.isArray(arr)) return
    const item = arr.find(it => String(it.id) === String(rowId))
    if (!item) return
    const schema  = this._schemaForPath(path, textKey)
    const prefill = (schema.parse && schema.parse(item)) || {}
    this._renderStructuredModal(schema, prefill, (values) => {
      const patch = (schema.assemble && schema.assemble(values)) || {}
      Object.assign(item, patch)
      this._save(path)
      this._updateNextTbdBtn()
      this._renderNav()
      this._renderStep()
    })
  }

  // Generic structured-edit modal — the same proven shell as _openCircleModal
  // (labelled fields + optional suggestion chips + required validation + Enter
  // to advance / submit + Esc / backdrop to close), driven by a field schema.
  _renderStructuredModal(schema, prefill, onSave) {
    document.getElementById("wizard-prompt-modal")?.remove()
    const modal = document.createElement("div")
    modal.id = "wizard-prompt-modal"
    modal.className = "fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"

    const fieldHtml = (f) => {
      const initial = prefill[f.key] != null ? prefill[f.key] : ""
      const control = f.type === "textarea"
        ? `<textarea id="se-f-${esc(f.key)}" rows="2" placeholder="${esc(f.placeholder || "")}"
                   class="w-full px-3 py-2 border border-slate-300 rounded-lg text-base focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200">${esc(initial)}</textarea>`
        : `<input id="se-f-${esc(f.key)}" type="text" value="${esc(initial)}" placeholder="${esc(f.placeholder || "")}"
                 class="w-full px-3 py-2 border border-slate-300 rounded-lg text-base focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200">`
      const chips = (Array.isArray(f.suggestions) && f.suggestions.length)
        ? `<div class="mt-2 flex flex-wrap gap-1.5">${f.suggestions.map(s => `
             <button type="button" data-se-fill="${esc(f.key)}" data-se-val="${esc(s)}"
                     class="px-2.5 py-1 rounded-full text-xs font-medium border border-slate-200 bg-white text-slate-700 hover:border-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 transition">${esc(s)}</button>`).join("")}</div>`
        : ""
      return `
        <div>
          <label class="block text-base font-semibold text-slate-700 mb-1.5">${esc(f.label)}${f.required ? ' <span class="text-rose-500">*</span>' : ""}</label>
          ${control}
          ${chips}
        </div>`
    }

    modal.innerHTML = `
      <div class="bg-white border border-slate-200 rounded-2xl shadow-2xl p-7 w-full max-w-md">
        <h3 class="text-2xl font-bold text-slate-900 mb-5">${esc(schema.title || "Edit")}</h3>
        <div class="space-y-4">${schema.fields.map(fieldHtml).join("")}</div>
        <div class="flex items-center justify-end gap-2 mt-6">
          <button id="se-cancel" type="button" class="px-5 py-2.5 text-base font-medium text-slate-600 hover:text-slate-900">Cancel</button>
          <button id="se-save"   type="button" class="px-5 py-2.5 text-base font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Save</button>
        </div>
      </div>`

    document.body.appendChild(modal)
    document.body.style.overflow = "hidden"

    const els        = schema.fields.map(f => ({ ...f, el: modal.querySelector("#se-f-" + CSS.escape(f.key)) })).filter(f => f.el)
    const textInputs = els.filter(f => f.type !== "textarea")

    const clearError = (el) => { el.classList.remove("border-rose-400"); const e = el.parentNode.querySelector(".se-error"); if (e) e.remove() }
    const showError  = (el, msg) => {
      let e = el.parentNode.querySelector(".se-error")
      if (!e) { e = document.createElement("p"); e.className = "se-error text-sm text-rose-500 mt-1"; el.parentNode.appendChild(e) }
      e.textContent = msg; el.classList.add("border-rose-400"); el.focus()
    }
    const keyHandler = (e) => { if (e.key === "Escape") close() }
    const close = () => {
      document.body.style.overflow = ""
      modal.remove()
      document.removeEventListener("keydown", keyHandler)
    }

    modal.querySelectorAll("[data-se-fill]").forEach(btn => {
      btn.addEventListener("click", () => {
        const target = modal.querySelector("#se-f-" + CSS.escape(btn.dataset.seFill))
        if (!target) return
        target.value = btn.dataset.seVal
        clearError(target)
        target.focus()
      })
    })
    els.forEach(f => f.el.addEventListener("input", () => clearError(f.el)))

    const submit = () => {
      const values = {}
      for (const f of els) {
        if (f.required && !(f.el.value || "").trim()) { showError(f.el, "This field is required"); return }
        values[f.key] = f.el.value
      }
      close()
      onSave(values)
    }

    modal.querySelector("#se-save").addEventListener("click", submit)
    modal.querySelector("#se-cancel").addEventListener("click", close)
    modal.addEventListener("click", e => { if (e.target === modal) close() })
    document.addEventListener("keydown", keyHandler)
    textInputs.forEach((f, i) => f.el.addEventListener("keydown", e => {
      if (e.key === "Enter") {
        e.preventDefault()
        if (i < textInputs.length - 1) textInputs[i + 1].el.focus(); else submit()
      }
    }))

    els[0]?.el?.focus()
    renderIcons(modal)
  }

  // Ports editor_source/wizard.html#4646 showCircleModal. Opens a
  // category-aware modal with suggestion chips + a Name field; submits
  // a builder-friendly display string ("Maria Rivera (Mom)" /
  // "Dr. Sarah Chen (Pediatrician)") via callback. Skip-as-TBD path
  // fills empty required fields with "TBD" before submit so Open
  // Questions can pick them up.
  _openCircleModal(category, callback, opts = {}) {
    const prefill = opts.prefill || {}
    const editing = !!opts.prefill
    const fieldsByCat = {
      "Family":       [{ id: "relationship", label: "Relationship *", placeholder: "e.g. Mom, Uncle, Cousin",
                         suggestions: FAMILY_RELATIONSHIP_SUGGESTIONS },
                       { id: "fullname",     label: "Name *",          placeholder: "e.g. Maria Rivera" }],
      "Friend":       [{ id: "role",         label: "Friend type *",   placeholder: "e.g. School friend, Soccer friend",
                         suggestions: FRIEND_CONTEXT_SUGGESTIONS },
                       { id: "fullname",     label: "Name *",          placeholder: "e.g. Jordan Lee" }],
      "Professional": [{ id: "role",         label: "Role / Title *",  placeholder: "e.g. Speech Therapist (SLP)",
                         suggestions: PROFESSIONAL_STANDARD,
                         moreGroupedSuggestions: PROFESSIONAL_MORE_GROUPS },
                       { id: "fullname",     label: "Name *",          placeholder: "e.g. Dr. Sarah Chen" }],
      "Community":    [{ id: "role",         label: "Role / Connection *", placeholder: "e.g. Coach, Mentor, Neighbor",
                         suggestions: COMMUNITY_ROLE_SUGGESTIONS },
                       { id: "fullname",     label: "Name *",          placeholder: "e.g. Coach Martinez" }]
    }
    const fields = fieldsByCat[category] || []
    const showUnder18 = category === "Family"

    document.getElementById("wizard-prompt-modal")?.remove()
    const modal = document.createElement("div")
    modal.id = "wizard-prompt-modal"
    modal.className = "fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"

    const planRef = this.plan
    const inCircleFor = (fid, s) => {
      if (fid !== "relationship" && fid !== "role") return false
      const lc = (s || "").toLowerCase()
      const list = (planRef.about && planRef.about.circleOfSupport) || []
      return list.some(p => {
        if (!p || p.relationship !== category) return false
        const parsed = parseCircleEntry(p.name, category)
        return ((parsed[fid] || "").toLowerCase() === lc)
      })
    }
    const renderFlat = (fid, items) => `
      <div class="flex flex-wrap gap-1.5">
        ${items.map(s => {
          const sel = inCircleFor(fid, s)
          const cls = sel
            ? "border-emerald-500 bg-emerald-50 text-emerald-800"
            : "border-slate-200 bg-white text-slate-700 hover:border-indigo-400 hover:text-indigo-700 hover:bg-indigo-50"
          return `
            <button type="button" data-fill-target="cos-f-${fid}" data-fill="${esc(s)}"
                    data-cos-chip="${esc(s)}"
                    class="px-2.5 py-1 rounded-full text-xs font-medium border transition flex items-center gap-1 ${cls}">
              ${sel ? '<span class="text-emerald-600 font-bold">&#10003;</span>' : ""}
              ${esc(s)}
            </button>`
        }).join("")}
      </div>`
    const renderGrouped = (fid, groups) => `
      <div class="mt-2 max-h-44 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2 space-y-2">
        ${Object.entries(groups).map(([group, items]) => `
          <div>
            <div class="text-[11px] font-bold uppercase tracking-wider text-slate-500 px-1 mb-1">${esc(group)}</div>
            ${renderFlat(fid, items)}
          </div>
        `).join("")}
      </div>`

    modal.innerHTML = `
      <div class="bg-white border border-slate-200 rounded-2xl shadow-2xl p-7 w-full max-w-md" data-cos-modal-card>
        <h3 class="text-2xl font-bold text-slate-900 mb-5">${editing ? "Edit" : "Add"} ${esc(category)} Member</h3>
        <div class="space-y-4">
          ${fields.map(f => {
            let suggestionsHtml = ""
            if (f.suggestions && f.suggestions.length) {
              suggestionsHtml += `<div class="mt-2">${renderFlat(f.id, f.suggestions)}</div>`
            }
            if (f.moreGroupedSuggestions && Object.keys(f.moreGroupedSuggestions).length) {
              suggestionsHtml += `
                <div class="mt-2">
                  <button type="button" data-toggle-more="cos-more-${f.id}"
                          class="text-sm font-medium text-indigo-600 hover:text-indigo-700">
                    + Show more roles
                  </button>
                  <div id="cos-more-${f.id}" class="hidden">
                    ${renderGrouped(f.id, f.moreGroupedSuggestions)}
                  </div>
                </div>`
            }
            const initial = prefill[f.id] != null ? prefill[f.id] : ""
            return `
              <div>
                <label class="block text-base font-semibold text-slate-700 mb-1.5">${esc(f.label)}</label>
                <input id="cos-f-${f.id}" type="text" value="${esc(initial)}" placeholder="${esc(f.placeholder)}"
                       class="w-full px-3 py-2 border border-slate-300 rounded-lg text-base focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200">
                ${suggestionsHtml}
              </div>`
          }).join("")}
          ${showUnder18 ? `
            <div class="flex items-center gap-2">
              <input id="cos-under18" type="checkbox" class="w-5 h-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500">
              <label for="cos-under18" class="text-base text-slate-700 cursor-pointer">Under 18?</label>
            </div>
            <div id="cos-age-wrap" class="hidden">
              <label class="block text-base font-semibold text-slate-700 mb-1.5">Age *</label>
              <input id="cos-minor-age" type="number" min="0" max="17" placeholder="e.g. 12"
                     class="w-full px-3 py-2 border border-slate-300 rounded-lg text-base focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200">
            </div>
          ` : ""}
        </div>
        <div class="flex items-center justify-between gap-2 mt-6">
          <button id="cos-skip" type="button"
                  class="px-4 py-2.5 text-sm font-medium text-amber-700 hover:text-amber-900 hover:bg-amber-50 rounded-lg"
                  title="Skip — fill missing fields with TBD; tracked in Open Questions">
            Skip — mark as TBD
          </button>
          <div class="flex gap-2">
            <button id="cos-cancel" type="button" class="px-5 py-2.5 text-base font-medium text-slate-600 hover:text-slate-900">Cancel</button>
            <button id="cos-add"    type="button" class="px-5 py-2.5 text-base font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">${editing ? "Save" : "Add"}</button>
          </div>
        </div>
      </div>`

    document.body.appendChild(modal)
    document.body.style.overflow = "hidden"

    const inputs = fields.map(f => ({ ...f, el: modal.querySelector(`#cos-f-${f.id}`) }))

    modal.querySelectorAll("[data-toggle-more]").forEach(btn => {
      btn.addEventListener("click", () => {
        const target = modal.querySelector("#" + btn.dataset.toggleMore)
        if (!target) return
        const wasHidden = target.classList.contains("hidden")
        target.classList.toggle("hidden", !wasHidden)
        btn.textContent = wasHidden ? "− Hide extra roles" : "+ Show more roles"
      })
    })

    modal.querySelectorAll("[data-fill-target]").forEach(btn => {
      btn.addEventListener("click", () => {
        const target = modal.querySelector("#" + btn.dataset.fillTarget)
        if (!target) return
        target.value = btn.dataset.fill
        target.dispatchEvent(new Event("input", { bubbles: true }))
        const idx = inputs.findIndex(f => f.el === target)
        if (idx >= 0 && idx < inputs.length - 1) inputs[idx + 1].el.focus()
        else target.focus()
      })
    })

    const under18  = modal.querySelector("#cos-under18")
    const ageWrap  = modal.querySelector("#cos-age-wrap")
    const minorAge = modal.querySelector("#cos-minor-age")

    if (under18) {
      under18.addEventListener("change", () => {
        ageWrap.classList.toggle("hidden", !under18.checked)
        if (!under18.checked && minorAge) minorAge.value = ""
      })
      if (prefill.under18) {
        under18.checked = true
        if (ageWrap) ageWrap.classList.remove("hidden")
        if (minorAge && prefill.age) minorAge.value = prefill.age
      }
    }

    const close = () => {
      document.body.style.overflow = ""
      modal.remove()
      document.removeEventListener("keydown", keyHandler)
    }
    const showError = (input, msg) => {
      let err = input.parentNode.querySelector(".field-error")
      if (!err) {
        err = document.createElement("p")
        err.className = "field-error text-sm text-red-500 mt-1"
        input.parentNode.appendChild(err)
      }
      err.textContent = msg
      input.classList.add("border-red-400")
      input.focus()
    }
    const clearError = (input) => {
      const err = input.parentNode.querySelector(".field-error")
      if (err) err.remove()
      input.classList.remove("border-red-400")
    }
    inputs.forEach(f => f.el.addEventListener("input", () => clearError(f.el)))
    if (minorAge) minorAge.addEventListener("input", () => clearError(minorAge))

    const submit = () => {
      const values = {}
      for (const f of inputs) {
        const v = f.el.value.trim()
        if (!v) { showError(f.el, "This field is required"); return }
        values[f.id] = v
      }
      let ageSuffix = ""
      if (under18 && under18.checked) {
        const age = minorAge.value.trim()
        if (!age) { showError(minorAge, "Please enter the age"); return }
        ageSuffix = `, age ${age}`
      }
      let display
      if (category === "Family") {
        display = `${values.fullname} (${values.relationship}${ageSuffix})`
      } else {
        display = `${values.fullname} (${values.role})`
      }
      close()
      callback(display, category)
    }

    modal.querySelector("#cos-add").addEventListener("click", submit)
    modal.querySelector("#cos-cancel").addEventListener("click", close)
    modal.querySelector("#cos-skip").addEventListener("click", () => {
      inputs.forEach(f => { if (!f.el.value.trim()) f.el.value = "TBD" })
      submit()
    })
    modal.addEventListener("click", e => { if (e.target === modal) close() })

    const keyHandler = (e) => {
      if (e.key === "Escape") close()
    }
    inputs.forEach((f, i) => {
      f.el.addEventListener("keydown", e => {
        if (e.key === "Enter") {
          e.preventDefault()
          if (i < inputs.length - 1) inputs[i + 1].el.focus(); else submit()
        }
      })
    })
    if (minorAge) {
      minorAge.addEventListener("keydown", e => {
        if (e.key === "Enter") { e.preventDefault(); submit() }
      })
    }
    document.addEventListener("keydown", keyHandler)

    requestAnimationFrame(() => { if (inputs[0]) inputs[0].el.focus() })
  }

  removeCircleEntry(event) {
    const rowId = parseFloat(event.currentTarget.dataset.rowId)
    const list  = (this.plan.about && this.plan.about.circleOfSupport) || []
    const idx = list.findIndex(p => p.id === rowId)
    if (idx < 0) return
    const removed = list[idx]
    list.splice(idx, 1)
    // Remember dismissed standard roles so seeding doesn't bring them back.
    if (removed) {
      const parsed = parseCircleEntry(removed.name, removed.relationship)
      const removedRole = parsed.role || parsed.relationship || ""
      const match = PROFESSIONAL_STANDARD.find(r => r.toLowerCase() === removedRole.toLowerCase())
      if (match) {
        if (!this.plan.meta) this.plan.meta = {}
        if (!Array.isArray(this.plan.meta.standardTeamDismissed)) this.plan.meta.standardTeamDismissed = []
        if (!this.plan.meta.standardTeamDismissed.includes(match)) {
          this.plan.meta.standardTeamDismissed.push(match)
        }
      }
    }
    this._save("about.circleOfSupport")
    this._updateNextTbdBtn()
    this._renderNav()
    this._renderStep()
  }

  // Append a free-text entry to a multi-select list (e.g. a custom
  // communication style the preset cards don't cover). Dedupes against
  // existing array members (case-insensitive) so the user can't
  // accidentally add "Verbal" twice or duplicate one of the preset
  // cards. Clears the input on success, toasts on duplicate.
  addMselCustom(event) {
    const btn = event.currentTarget
    const listKey = btn.dataset.listKey
    if (!listKey) return
    const input = this.screenTarget.querySelector(`[data-list-add-input="${CSS.escape(listKey)}"]`)
    const value = (input?.value || "").trim()
    if (!value) { input?.focus(); return }
    let arr = this._getByPath(listKey)
    if (!Array.isArray(arr)) {
      arr = []
      this._setByPath(listKey, arr)
    }
    const dup = arr.some(it => {
      const t = (typeof it === "string" ? it : (it && it.text) || "").toLowerCase()
      return t === value.toLowerCase()
    })
    if (dup) {
      this._toast(`"${value}" is already on the list`)
      if (input) { input.value = ""; input.focus() }
      return
    }
    arr.push({ id: Date.now() + Math.random(), text: value })
    if (input) input.value = ""
    this._save(listKey)
    this._updateNextTbdBtn()
    this._renderNav()
    this._renderStep()
  }

  // Enter inside a multi-select add-row input commits the same way the
  // "+ Add" button does. Shift+Enter is ignored so users can paste
  // multi-line text without firing.
  mselAddKey(event) {
    if (event.key !== "Enter" || event.shiftKey) return
    event.preventDefault()
    const input = event.currentTarget
    const listKey = input.dataset.listAddInput
    if (!listKey) return
    // Reuse addMselCustom's dedup + save flow — synthesize the button
    // context it expects so we don't duplicate the body.
    this.addMselCustom({ currentTarget: { dataset: { listKey } } })
  }

  // Multi-select card toggle — flips the value's presence in the array
  // stored at the dotted path. Mirrors editor_source/wizard.html#5226
  // toggleCard.
  toggleMultiSelect(event) {
    const btn = event.currentTarget
    const listKey = btn.dataset.listKey
    const value   = btn.dataset.mselValue
    if (!listKey || value == null) return
    let arr = this._getByPath(listKey)
    if (!Array.isArray(arr)) {
      arr = []
      this._setByPath(listKey, arr)
    }
    const idx = arr.findIndex(it => (typeof it === "string" ? it : it.text) === value)
    if (idx >= 0) arr.splice(idx, 1)
    else arr.push({ id: Date.now() + Math.random(), text: value })
    this._save(listKey)
    this._updateNextTbdBtn()
    this._renderNav()
    this._renderStep()
  }

  // Enter to commit, Shift+Enter for newline. Bound on the add-row textarea.
  // Type-to-add: when the add box holds just a number N, resolve it to
  // the Nth suggestion chip's label (chips render their 1-based number in
  // DOM order, so index N-1 is the chip badged N). Non-numeric or
  // out-of-range input passes through unchanged.
  _resolveChipNumber(input, value) {
    if (!/^\d+$/.test(value)) return value
    const grid = input?.closest?.(".q-list")
    if (!grid) return value
    const chips = grid.querySelectorAll(".q-chip[data-chip]")
    const idx = parseInt(value, 10) - 1
    return (idx >= 0 && idx < chips.length) ? (chips[idx].dataset.chip || value) : value
  }

  // Whether `value` is already in the list (avoid a number adding a dup).
  _listHasText(arr, textKey, value) {
    return Array.isArray(arr) && arr.some(it => (it?.[textKey] ?? it?.text ?? it?.description) === value)
  }

  listAddKey(event) {
    if (event.key !== "Enter" || event.shiftKey) return
    event.preventDefault()
    const input = event.currentTarget
    const listKey = input.dataset.listAddInput
    const textKey = input.dataset.textKey || "text"
    if (!listKey) return
    const raw = (input.value || "").trim()
    if (!raw) return
    const value = this._resolveChipNumber(input, raw)
    let arr = this._getByPath(listKey)
    if (!Array.isArray(arr)) {
      arr = []
      this._setByPath(listKey, arr)
    }
    // A number that resolved to an already-added chip is a no-op (just clear).
    if (value !== raw && this._listHasText(arr, textKey, value)) { input.value = ""; return }
    arr.push({ id: Date.now() + Math.random(), [textKey]: value })
    this._save(listKey)
    this._updateNextTbdBtn()
    this._renderNav()
    this._renderStep()
  }

  removeRow(event) {
    const btn = event.currentTarget
    // Prefer the explicit data-list-key the row was rendered with —
    // the dynamicListInput rows still scope their key on a parent
    // [data-list-input] wrapper, so fall back to that for the
    // single-field lists. Goal cards (multi-field, no wrapper) only
    // worked once data-list-key was honored straight off the button.
    const listKey = btn.dataset.listKey ||
                    btn.closest("[data-list-input]")?.dataset?.listInput
    if (!listKey) return
    const rowId = parseFloat(btn.dataset.rowId)
    let arr = this._getByPath(listKey) || []
    arr = arr.filter(it => it.id !== rowId)
    this._setByPath(listKey, arr)
    this._save(listKey)
    this._updateNextTbdBtn()
    this._renderNav()
    this._renderStep()
  }

  // ---- render ----
  _renderNav() {
    const counts = this._tbdCountsBySection()
    const total = Object.values(counts).reduce((a, b) => a + b, 0)
    const html = SECTIONS.map((s, i) => {
      const count = (s.id === "openQuestions") ? total : (counts[s.id] || 0)
      const badge = count > 0
        ? `<span class="en-wiz-nav-badge">${count}</span>`
        : ""
      return `
        <button type="button"
                class="en-wiz-nav-item${i === this.step ? " is-active" : ""}${this._sectionHasData(s.id) ? " has-data" : ""}"
                data-step-idx="${i}"
                data-action="wizard#jump"
                title="${esc(s.label)}">
          <span class="en-wiz-nav-dot"></span>
          ${sectionIconHtml(s.icon)}
          <span class="en-wiz-nav-label">${esc(s.short)}</span>
          ${badge}
        </button>`
    }).join("")
    this.navTarget.innerHTML = html
    renderIcons(this.element)
  }

  // ----- Communication picker -----

  // Swap the communication step's placeholder shell with the picker
  // (when a chart cell is active) or the empty / done state. Called
  // from _renderStep after the screen HTML is in place.
  _renderCommStep() {
    const host = this.screenTarget.querySelector("[data-wizard-comm-host]")
    if (!host) return
    const active = this._commActive
    if (!active) {
      host.innerHTML = communicationEmptyHtml()
      return
    }
    if (active.done) {
      host.innerHTML = communicationDoneHtml(active.rowIdx ?? 0)
      return
    }
    host.innerHTML = communicationPickerHtml(active.rowId, active.col, active.state || {}, !!this._commBypass)
  }

  // Map a row id (Date.now()+random) back to its zero-based index in
  // plan.about.communicationChart. Used by the "row complete" banner.
  _commRowIndex(rowId) {
    const arr = (this.plan.about && this.plan.about.communicationChart) || []
    const idx = arr.findIndex(r => r.id === rowId)
    return idx < 0 ? 0 : idx
  }

  // Click on an option (quick pick or grouped item). Dispatches a
  // cell-fill event up to the chart controller, which writes the
  // value and (for non-final columns) re-emits cell-select for the
  // next column so the picker chains automatically.
  pickCommOption(event) {
    const btn = event.currentTarget
    const value = btn.dataset.ccValue
    const icon  = btn.dataset.ccIcon || "edit-3"
    if (!this._commActive || this._commActive.done) return
    document.dispatchEvent(new CustomEvent("comm-chart:cell-fill", {
      bubbles: true,
      detail: {
        rowId: this._commActive.rowId,
        col:   this._commActive.col,
        value, icon
      }
    }))
  }

  // "Type your own" → pick a custom (non-registry) value with a
  // neutral pencil icon.
  pickCommCustom() {
    if (!this.hasCommCustomTarget) return
    const v = (this.commCustomTarget.value || "").trim()
    if (!v) { this.commCustomTarget.focus(); return }
    if (!this._commActive || this._commActive.done) return
    document.dispatchEvent(new CustomEvent("comm-chart:cell-fill", {
      bubbles: true,
      detail: {
        rowId: this._commActive.rowId,
        col:   this._commActive.col,
        value: v, icon: "edit-3"
      }
    }))
  }

  commCustomKey(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.pickCommCustom()
    }
  }

  // Search box — hide options that don't match, hide groups with no
  // matches, and keep the quick-picks row out of the way while a
  // query is active.
  filterCommOptions(event) {
    const needle = (event.currentTarget.value || "").trim().toLowerCase()
    const clear = this.screenTarget.querySelector(".q-cc-search-clear")
    if (clear) clear.hidden = needle.length === 0
    if (!this.hasCommGroupsTarget) return
    let totalVisible = 0
    this.commGroupsTarget.querySelectorAll(".q-cc-group").forEach(g => {
      let matches = 0
      g.querySelectorAll(".q-cc-option").forEach(o => {
        const t = o.querySelector(".q-cc-option-label").textContent.toLowerCase()
        const match = !needle || t.includes(needle)
        o.classList.toggle("is-hidden", !match)
        if (match) matches++
      })
      g.style.display = matches === 0 ? "none" : ""
      const countEl = g.querySelector(".q-cc-group-count")
      if (countEl) {
        const orig = parseInt(countEl.dataset.original, 10) || matches
        countEl.textContent = needle ? `${matches}` : `${orig}`
      }
      if (needle && matches > 0) g.classList.add("is-open")
      totalVisible += matches
    })
    const quick = this.screenTarget.querySelector(".q-cc-quick")
    if (quick) quick.style.display = needle ? "none" : ""
    if (this.hasCommVisibleTarget) this.commVisibleTarget.textContent = totalVisible
  }

  // Enter inside the search box submits the typed text as a custom
  // value (same as the "+ Add" button).
  commSearchKey(event) {
    if (event.key !== "Enter") return
    const v = (event.currentTarget.value || "").trim()
    if (!v || !this._commActive || this._commActive.done) return
    document.dispatchEvent(new CustomEvent("comm-chart:cell-fill", {
      bubbles: true,
      detail: {
        rowId: this._commActive.rowId,
        col:   this._commActive.col,
        value: v, icon: "edit-3"
      }
    }))
  }

  clearCommSearch() {
    if (!this.hasCommSearchTarget) return
    this.commSearchTarget.value = ""
    this.filterCommOptions({ currentTarget: this.commSearchTarget })
    this.commSearchTarget.focus()
  }

  toggleCommGroup(event) {
    const head = event.currentTarget
    const g = head.parentElement
    if (g) g.classList.toggle("is-open")
  }

  expandAllCommGroups() {
    if (!this.hasCommGroupsTarget) return
    this.commGroupsTarget.querySelectorAll(".q-cc-group").forEach(g => g.classList.add("is-open"))
  }

  collapseAllCommGroups() {
    if (!this.hasCommGroupsTarget) return
    this.commGroupsTarget.querySelectorAll(".q-cc-group").forEach(g => g.classList.remove("is-open"))
  }

  // "Show all options" escape hatch — flip the strict cascade off
  // for the active cell and re-render the picker so the generic set
  // shows alongside the override.
  toggleCommBypass() {
    this._commBypass = !this._commBypass
    this._renderCommStep()
    renderIcons(this.element)
  }

  _renderStep() {
    // If a focused person flow is in progress, take over the whole
    // step pane — hide every other question so the user can complete
    // the family-member detail capture in one motion.
    if (_wizPersonFlow.active) return this._renderPersonFlow()
    // Issue #6: same idea for adding a school.
    if (_wizSchoolFlow.active) return this._renderSchoolFlow()
    const s = SECTIONS[this.step]
    // Preserve the pane's scroll position across an IN-PLACE re-render (chip
    // toggle, add/remove a row, a flag, an edit-pane mirror, etc.) so a click
    // doesn't yank the wizard back to the top. Only reset to the top when the
    // step actually changed — i.e. real navigation to a different section.
    const scroller = findScrollParent(this.screenTarget)
    const prevTop = scroller ? scroller.scrollTop : 0
    const sameStep = s.id === this._renderedStepId
    const renderer = STEP_RENDERERS[s.id]
    // Sections that intentionally have no guided wizard (Brochure /
    // Review / Facilitator are pure data/preview surfaces): render a
    // clean "edit on the left" card so the binding is visible instead
    // of a stub "not yet ported" message.
    // Resilience: a renderer that throws must NOT freeze the pane. Because
    // this.step is advanced by the caller BEFORE _renderStep runs, an
    // uncaught throw here would leave innerHTML on the previous step while
    // the wizard believes it's on the new one — the exact editor/wizard
    // desync a scoping bug in about() once caused. Fall back to the
    // "edit on the left" card so the pane always reflects the active step.
    let stepBody
    try {
      stepBody = renderer
        ? renderer(this.plan)
        : NO_WIZARD_SECTIONS.has(s.id)
          ? `<div class="q-step-noguide">
               <div class="q-step-noguide-icon">↩</div>
               <h3>No guided questions for ${esc(s.label)}</h3>
               <p>This section is content-only — edit it directly on the left.</p>
             </div>`
          : `<p class="q-empty">Step content for "${esc(s.label)}" not yet ported.</p>`
    } catch (err) {
      console.error(`PCPM wizard: "${s.id}" renderer threw — showing fallback`, err)
      stepBody = `<div class="q-step-noguide">
             <div class="q-step-noguide-icon">↩</div>
             <h3>Edit ${escName(s.label)} on the left</h3>
             <p>The guided view for this section hit a snag — your data is safe; edit it directly in the left pane.</p>
           </div>`
    }
    // Each step starts with a small section eyebrow ("CLIENT") and the
    // long-form question as h2 — production wizard.html#960 builds this
    // chrome around every renderer output.
    this.screenTarget.innerHTML = `
      <div class="q-step-eyebrow">${esc(s.short.toUpperCase())}</div>
      <h2 class="q-title">${escName(s.label)}</h2>
      <div class="q-step-body">${stepBody}</div>`
    // Communication step: swap the placeholder with the picker (or the
    // empty/done state). Done here rather than in the static renderer
    // because the picker needs the active-cell state held on the
    // controller (this._commActive).
    if (s.id === "communication") this._renderCommStep()
    // Goals step + the post-add vendor walkthrough step: fetch the
    // domain-filtered, distance-sorted vendor button grid.
    if (s.id === "goals" && _wizGoalUi.pendingSchedGoalId && _wizGoalUi.step === "vendor") {
      this._loadGoalWizVendors()
    }
    // Wire input/change listeners on all plan-field inputs.
    this.screenTarget.querySelectorAll("[data-plan-field]").forEach(el => {
      const evt = (el.tagName === "SELECT" || el.type === "date" || el.type === "checkbox") ? "change" : "input"
      el.addEventListener(evt, (e) => this.fieldChanged(e))
    })
    // If the client step loads with a valid ZIP already present, resolve
    // the "City, ST" readout so a stale/empty value self-corrects (cached —
    // no repeat fetch for the same ZIP, and a valid current city is kept).
    const zipEl = this.screenTarget.querySelector("[data-plan-field='client.zip']")
    if (zipEl && /^\d{5}$/.test((zipEl.value || "").trim())) this._resolveZipCity(zipEl.value.trim())
    this.prevBtnTarget.disabled = this.step === 0
    this.nextBtnTarget.disabled = this.step >= SECTIONS.length - 1
    // Footer label matches production exactly: "Section N of M".
    this.progressTarget.textContent = `Section ${this.step + 1} of ${SECTIONS.length}`
    renderIcons(this.element)
    // Restore scroll for an in-place re-render; land at the top for a genuine
    // section change. Clamped automatically if the new content is shorter.
    this._renderedStepId = s.id
    if (scroller) scroller.scrollTop = sameStep ? prevTop : 0
  }

  // Flag handler. The flag is a NON-destructive "to be determined" marker:
  // it records the field's path in plan.meta.flags WITHOUT touching the
  // field's text. (It used to overwrite the value with "TBD" and disable the
  // input, wiping whatever the user had typed — that was the bug.) A flagged
  // field still surfaces in Open Questions via findOpenQuestions, which folds
  // in meta.flags on top of the legacy value-pattern scan.
  //
  // The field path lives on the button via data-tbd-for (e.g.
  // "client.firstName") so any step can reuse the same control.
  toggleTbd(event) {
    const el = event.currentTarget
    const path = el.dataset.tbdFor
    if (!path) return
    // Old control was a checkbox (state in .checked); the flag button carries
    // its state in .is-flagged / aria-pressed and flips on each click.
    const isCheckbox = el.tagName === "INPUT"
    const flagged = isCheckbox ? el.checked : !el.classList.contains("is-flagged")
    if (!isCheckbox) {
      el.classList.toggle("is-flagged", flagged)
      el.setAttribute("aria-pressed", String(flagged))
    }
    if (!this.plan.meta) this.plan.meta = {}
    if (!this.plan.meta.flags) this.plan.meta.flags = {}
    if (flagged) this.plan.meta.flags[path] = true
    else delete this.plan.meta.flags[path]
    // Persist the whole plan (meta included) — no field value changed, so the
    // input keeps its text and stays editable.
    this._save()
    this._updateNextTbdBtn()
    this._renderNav()
  }

  _sectionHasData(id) {
    const p = this.plan
    switch (id) {
      case "client":          return !!(p.client && (p.client.firstName || p.client.lastName || p.client.dob))
      case "about":           return !!(p.about && (
        (p.about.whoLivesWithMe||[]).length || (p.about.communicationStyle||[]).length ||
        (p.about.schoolDetails||[]).length || (p.about.workDetails||[]).length))
      case "circleOfSupport": return !!(p.about && (p.about.circleOfSupport||[]).length)
      case "communication":   return !!(p.about && (p.about.communicationChart||[]).length)
      case "wonderfulThings": return !!(p.wonderfulThings && Object.values(p.wonderfulThings).some(v => Array.isArray(v) && v.length))
      case "notSoFavorites":  return !!(p.notSoFavorites && Object.values(p.notSoFavorites).some(v => Array.isArray(v) && v.length))
      case "dayInLife":       return !!(p.dayInLife && Object.entries(p.dayInLife).some(([k,v]) => Array.isArray(v) && v.length))
      case "important":       return !!(p.important && ((p.important.to||[]).length || (p.important.for||[]).length))
      case "bigDreams":       return !!(p.bigDreams && ((p.bigDreams.hopes||[]).length || (p.bigDreams.dreams||[]).length))
      case "supportMatch":    return !!(p.supportMatch && Object.values(p.supportMatch).some(v => Array.isArray(v) && v.length))
      case "working":         return !!(p.working && ((p.working.works||[]).length || (p.working.doesntWork||[]).length))
      case "barriers":        return !!(p.barriers && p.barriers.length)
      case "goals":           return !!(p.goals && p.goals.length)
      case "currentServices": return !!(p.currentServices && p.currentServices.length)
      default: return false
    }
  }

  _persistStep() {
    this.plan.meta.wizardStep = SECTIONS[this.step].id
    this._save()
  }

  // Returns the editor section key from the current URL, if the
  // wizard is embedded on `/editor/native/<key>`. Used by connect()
  // to deep-link to the matching wizard step. Empty string when the
  // URL doesn't carry a section (e.g. standalone wizard pages).
  _sectionFromUrl() {
    const m = (window.location.pathname || "").match(/^\/editor\/native\/([^\/?#]+)/)
    return m ? decodeURIComponent(m[1]) : ""
  }

  // Tells the editor pane to switch sections to match this.step.
  // Called from every wizard-side mutation (next/prev/jump/nextTbd/
  // openTbdSection/comm cell-select). Updates the URL and fires
  // `wizard:section-change`; the editor's listener calls
  // showSection on the matching key. Skips when the URL already
  // matches, which both avoids an extra history entry and breaks
  // the editor→wizard→editor event loop (editor's own
  // `editor-native:section-change` dispatch is then a no-op for
  // wizard's _onSectionChange via its `idx === this.step` guard).
  _syncEditorSection() {
    const id = SECTIONS[this.step]?.id
    if (!id) return
    const path = `/editor/native/${id}`
    if (window.location.pathname === path) return
    try { window.history.replaceState({}, "", path) } catch (_) {}
    document.dispatchEvent(new CustomEvent("wizard:section-change", {
      detail: { sectionId: id }
    }))
  }

  // ---- plan-path helpers (support dotted paths AND list ID segments) ----
  // "about.whoLivesWithMe.<id>.text" → walk to about.whoLivesWithMe,
  // find item where id === <id>, then set .text on it.
  _getByPath(path) {
    return path.split(".").reduce((o, k) => {
      if (o == null) return undefined
      if (Array.isArray(o)) {
        const idNum = parseFloat(k)
        return o.find(it => it.id === idNum)
      }
      return o[k]
    }, this.plan)
  }
  _setByPath(path, value) {
    const parts = path.split(".")
    const last = parts.pop()
    let node = this.plan
    for (const k of parts) {
      if (node == null) return
      if (Array.isArray(node)) {
        const idNum = parseFloat(k)
        node = node.find(it => it.id === idNum)
      } else {
        if (!node[k] || typeof node[k] !== "object") node[k] = {}
        node = node[k]
      }
    }
    if (node == null) return
    if (Array.isArray(node)) {
      const idNum = parseFloat(last)
      const it = node.find(i => i.id === idNum)
      if (it) it[Object.keys(it).find(k => k !== "id")] = value
    } else {
      node[last] = value
    }
  }

  // Resolve a 5-digit ZIP to city/state via zippopotam.us, persist them to
  // the plan, and refresh the inline "City, ST" readout under the ZIP field.
  // e.g. 90638 → "La Mirada, CA" (not "LA"). Keeps the current city if it's
  // one this ZIP covers; otherwise takes the ZIP's primary place.
  async _resolveZipCity(zip) {
    zip = String(zip || "").trim()
    if (!/^\d{5}$/.test(zip)) return
    this.constructor._zipCityCache = this.constructor._zipCityCache || {}
    const cache = this.constructor._zipCityCache
    let places = cache[zip]
    if (!places) {
      try {
        const res = await fetch(`https://api.zippopotam.us/us/${encodeURIComponent(zip)}`)
        if (!res.ok) return
        const data = await res.json()
        places = (data.places || [])
          .map(p => ({ city: p["place name"] || "", state: p["state abbreviation"] || "" }))
          .filter(p => p.city)
        cache[zip] = places
      } catch (_) { return }   // network/CORS — leave the readout as-is
    }
    if (!places.length) return
    // Bail if the user has since changed the ZIP (stale async response).
    if (String(this._getByPath("client.zip") || "").trim() !== zip) return
    const cur   = String(this._getByPath("client.city") || "").trim()
    const match = places.find(p => p.city === cur) || places[0]
    this._setByPath("client.city", match.city)
    this._setByPath("client.state", match.state || "")
    this._setByPath("client._zipPlaces", places)
    this._save("client.city")
    this._updateZipStatus(match.city, match.state)
  }

  _updateZipStatus(city, state) {
    const txt = (city && state) ? `${city}, ${state}` : (city || "")
    if (this.hasZipStatusTarget) { this.zipStatusTarget.textContent = txt; return }
    const el = this.screenTarget && this.screenTarget.querySelector(".q-zip-status")
    if (el) el.textContent = txt
  }

  // ---- toast ----
  _toast(msg) {
    let bar = document.getElementById("en-wiz-toast")
    if (!bar) {
      bar = document.createElement("div")
      bar.id = "en-wiz-toast"
      bar.style.cssText = "position:fixed;bottom:18px;right:18px;background:#1e293b;color:#e2e8f0;padding:8px 14px;border:1px solid #334155;border-radius:8px;font:600 12px/1.2 ui-sans-serif;z-index:9999;opacity:0;transition:opacity .15s;"
      document.body.appendChild(bar)
    }
    bar.textContent = msg
    bar.style.opacity = "1"
    clearTimeout(this._tt)
    this._tt = setTimeout(() => { bar.style.opacity = "0" }, 900)
  }

  // -------------------------------------------------------------------
  // Header actions (production parity)
  // -------------------------------------------------------------------

  // Save the current plan to disk as a JSON file. Mirrors the
  // "💾 Save JSON" button at editor_source/wizard.html#191.
  savePlanJson() {
    const blob = new Blob([JSON.stringify(this.plan, null, 2)], { type: "application/json" })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement("a")
    const name = (this.plan.client?.firstName || "plan").toLowerCase().replace(/[^a-z0-9]+/g, "-")
    a.href = url
    a.download = `${name}-${new Date().toISOString().slice(0, 10)}.json`
    document.body.appendChild(a); a.click(); a.remove()
    URL.revokeObjectURL(url)
    this._toast("Saved to file")
  }

  // Open the hidden file input — the real load happens in loadPlanJsonChosen.
  loadPlanJson() {
    if (this.hasLoadFileInputTarget) this.loadFileInputTarget.click()
  }

  async loadPlanJsonChosen(event) {
    const file = event.target.files && event.target.files[0]
    if (!file) return
    try {
      const text = await file.text()
      const next = JSON.parse(text)
      if (typeof next !== "object" || next == null) throw new Error("not an object")
      this.plan = next
      if (!this.plan.meta) this.plan.meta = {}
      this._save()
      this._renderNav()
      this._renderStep()
      this._updateNextTbdBtn()
      this._toast("Plan loaded")
    } catch (e) {
      this._toast("Couldn't load: " + (e.message || "parse error"))
    } finally {
      event.target.value = ""
    }
  }

  // Cycle through every TBD-bearing section. If we're already on a
  // TBD-bearing section, advance to the next one; otherwise jump to
  // the first. Mirrors editor_source/wizard.html#~806 updateNextTBDBtn
  // navigation.
  nextTbd() {
    const sections = this._sectionsWithTbds()
    if (!sections.length) return
    // openQuestions consolidates everything — prefer it when other
    // sections aren't holding TBDs to avoid cycling forever.
    const current = SECTIONS[this.step]?.id
    const idx = sections.indexOf(current)
    const target = sections[(idx + 1) % sections.length] || sections[0]
    const stepIdx = SECTIONS.findIndex(s => s.id === target)
    if (stepIdx >= 0) {
      this.step = stepIdx
      this._persistStep()
      this._renderNav()
      this._renderStep()
      this._syncEditorSection()
    }
  }

  toggleMenu(event) {
    event?.stopPropagation?.()
    if (this.hasMenuTarget) this.menuTarget.hidden = !this.menuTarget.hidden
  }

  // Sign out posts to /session via DELETE. Reuses Rails' CSRF token so
  // the existing devise-style session controller can clear the cookie.
  signOut() {
    const csrf = document.querySelector("meta[name='csrf-token']")?.content || ""
    const form = document.createElement("form")
    form.method = "POST"
    form.action = "/session"
    form.innerHTML = `
      <input type="hidden" name="_method" value="delete">
      <input type="hidden" name="authenticity_token" value="${esc(csrf)}">`
    document.body.appendChild(form)
    form.submit()
  }

  // -------------------------------------------------------------------
  // Open Questions actions (production parity)
  // -------------------------------------------------------------------

  // Inline answer flow — set the value at the dotted path and re-render
  // the Open Questions view so the resolved question drops off.
  applyTbdAnswer(event) {
    const path = event.currentTarget.dataset.tbdPath
    if (!path) return
    const input = this.element.querySelector(`[data-tbd-answer="${CSS.escape(path)}"]`)
    const value = input?.value?.trim() || ""
    if (!value) { this._toast("Type an answer first"); return }
    this._setByPath(path, value)
    this._save(path)
    this._renderStep()
    this._updateNextTbdBtn()
  }

  // "Still TBD" checkbox — sets a meta.stillTbd map keyed by path so the
  // Open Questions filter can hide reviewed-but-still-pending questions.
  toggleStillTbd(event) {
    const path = event.currentTarget.dataset.tbdPath
    if (!path) return
    if (!this.plan.meta.stillTbd) this.plan.meta.stillTbd = {}
    this.plan.meta.stillTbd[path] = !!event.currentTarget.checked
    this._save()
  }

  // Jump from a TBD card into the matching wizard section.
  openTbdSection(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.sectionId
    if (!id) return
    const idx = SECTIONS.findIndex(s => s.id === id)
    if (idx >= 0) {
      this.step = idx
      this._persistStep()
      this._renderNav()
      this._renderStep()
      this._syncEditorSection()
    }
  }

  // The remaining Open Questions toolbar buttons — stub UI for now so the
  // buttons are visible / wired and we can extend them when the export
  // pipelines ship.
  shareAsPdf()    { window.location.assign("/editor/native/preview") }
  shareAsHtml()   { this._toast("HTML share isn't wired yet") }
  exportToExcel() { this._toast("Excel export isn't wired yet") }
  importAnswers() { this._toast("Import Answers isn't wired yet") }

  // -------------------------------------------------------------------
  // TBD bookkeeping
  // -------------------------------------------------------------------

  _sectionsWithTbds() {
    const counts = this._tbdCountsBySection()
    return Object.keys(counts).filter(k => counts[k] > 0)
  }

  _tbdCountsBySection() {
    const all = findOpenQuestions(this.plan)
    const out = {}
    all.forEach(it => { if (it.sectionId) out[it.sectionId] = (out[it.sectionId] || 0) + 1 })
    return out
  }

  _updateNextTbdBtn() {
    if (!this.hasNextTbdBtnTarget) return
    const all = findOpenQuestions(this.plan).length
    if (all > 0) {
      this.nextTbdBtnTarget.hidden = false
      if (this.hasNextTbdCountTarget) this.nextTbdCountTarget.textContent = String(all)
    } else {
      this.nextTbdBtnTarget.hidden = true
    }
  }

  _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch (_) { return {} }
  }
  _save(path = null) {
    // Keep Circle of Support mirrored from Who Lives With Me on every
    // save so Family entries surface in both places (and stay deduped).
    syncWhoLivesIntoCircle(this.plan)
    // savePlan surfaces a visible warning on quota overflow instead of
    // swallowing it — the silent catch here used to hide data loss.
    savePlan(this.plan)
    // Notify same-tab listeners (plan_store_controller, dynamic_list_controller,
    // client_city, etc.) that the plan changed. Cross-tab listeners get the
    // browser's native `storage` event automatically.
    //
    // `path` is the dotted plan-field path of the field that changed (or the
    // list path for structural changes). dynamic_list_controller guards on a
    // path-match before re-rendering, so omitting this means lists won't
    // pick up wizard edits — which is exactly why the editor pane's Goals
    // rows used to need a refresh to reflect wizard changes.
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true, detail: { source: "wizard", plan: this.plan, path }
    }))
  }

  // Briefly swap the "Connected to builder" pill text to confirm a
  // cross-tab sync just landed, then restore.
  _flashStatus(msg) {
    if (!this.hasStatusTarget) return
    const original = this.statusTarget.innerHTML
    this.statusTarget.innerHTML = `<span class="en-wiz-status-dot"></span> ${esc(msg)}`
    clearTimeout(this._statusTimer)
    this._statusTimer = setTimeout(() => {
      this.statusTarget.innerHTML = original
    }, 1500)
  }
}
